/*
 * LiveTracking is an imperative, real-time map renderer. Live device positions are stored in
 * refs (animPositionsRef / animStateRef / lastRenderedPositionsRef — held in LiveTrackingContext
 * so they survive page navigation) and mutated by a 200ms animation loop, then read DURING RENDER
 * to place markers, with a single controlled `animTick` state bump driving re-renders. This is a
 * deliberate performance architecture: hoisting per-device positions into React state would cause
 * re-render storms at fleet scale (thousands of devices).
 *
 * Notes on lint rules that don't apply here (documentation only):
 *   - refs / immutability / preserve-manual-memoization — positions are intentionally read from
 *     refs during render, the animation loop mutates those refs in place every 200ms, and this
 *     file manages its own memoization explicitly via useCallback/useMemo.
 *   - The project does NOT enable the React Compiler (no babel-plugin-react-compiler), so those
 *     concerns would be lint-only anyway.
 *
 * Review any NEW ref/mutation usage manually.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGeneralSettings } from '../hooks/useGeneralSettings';
import { useIsMobileOrWebview } from '../hooks/useIsMobileOrWebview';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useTheme } from '../contexts/ThemeContext';
import { getMapThemeOverride, setMapThemeOverride } from '../utils/mapTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { APIProvider, Map as GoogleMap, AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow as GInfoWindow, useMap as useGoogleMap } from '@vis.gl/react-google-maps';
import { useMapStaleGuard } from '../hooks/useMapStaleGuard';
import { GoogleTrafficLayer, DeckGLOverlay, GooglePolyline, GoogleCircle, GooglePolygon } from '../components/maps/GoogleMapsWrappers';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { NOMINATIM_BASE } from '../utils/nominatim';
import { getPlaceColor, createPlaceIcon, PlaceMarkerContent, getRouteCorridor } from '../components/geofencing/GeofencingUtils';
import { DeviceListPanel } from '../components/live-tracking/DeviceListPanel';
import { TelemetryPanel } from '../components/live-tracking/TelemetryPanel';
import { EditDeviceDialog } from '../components/live-tracking/EditDeviceDialog';
import { SharePositionModal } from '../components/live-tracking/SharePositionModal';
import { CreateTrackingLinkModal } from '../components/live-tracking/CreateTrackingLinkModal';
import { MoreFeaturesModal } from '../components/live-tracking/MoreFeaturesModal';
import { ManageGroupsModal } from '../components/live-tracking/ManageGroupsModal';
import { LiveTrackingVideoModal } from '../components/live-tracking/LiveTrackingVideoModal';
import { DeviceIconDisplay, deviceIcons, isCustomIcon, multicolorIcons } from '../components/icons/DeviceIcons';
import { EventMarkerIcon, ForensicTelemetryModal } from '../components/events/EventMapMarker';
import { renderToStaticMarkup } from 'react-dom/server';
import { useLiveTracking } from '../contexts/LiveTrackingContext';
import { snapToRoad, computePathMetrics, interpolateAlongPath } from '../utils/roadSnap';
import { deviceDisplayName } from '../utils/deviceName';
import { formatSpeed, convertSpeed, convertDistance, getDistanceUnit } from '../utils/timezone';
import { MapContainer, TileLayer, Marker as LeafletMarker, Popup, Polyline as LeafletPolyline, Polygon as LeafletPolygon, Circle as LeafletCircle, CircleMarker as LeafletCircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Supercluster from 'supercluster';
import {
  Search,
  Loader2,
  X,
  EyeOff,
  Hexagon,
  MapIcon,
  MapPin,
  Navigation,
  Globe,
  AlertTriangle,
  CheckCircle,
  Link2,
  Sparkles,
  Copy,
  Activity,
  IdCard,
  Gauge,
  Send,
  Bluetooth,
  Users,
} from 'lucide-react';

const DEFAULT_TRANSITION_EVENT_CARD_FLASH_MS = 5000;
// Condition/health alert event_types that colour a device card via the
// dedicated active-alert colour source (not the event-occurrence pipeline).
const CONDITION_ALERT_EVENT_TYPES = new Set(['beacon_low_battery', 'device_disconnected']);

// Fix for default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/leaflet/marker-icon-2x.png',
  iconUrl: '/leaflet/marker-icon.png',
  shadowUrl: '/leaflet/marker-shadow.png',
});

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;
const GEOFENCE_OVERLAY_COLORS = {
  zone: '#2563eb',
  place: '#16a34a',
  route: '#f59e0b',
};

const parseCoordinatePair = (point) => {
  if (!point) return null;
  if (Array.isArray(point) && point.length >= 2) {
    const first = Number(point[0]);
    const second = Number(point[1]);
    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;
    if (Math.abs(first) <= 90 && Math.abs(second) <= 180) return { lat: first, lng: second };
    return { lat: second, lng: first };
  }
  const lat = Number(point.lat ?? point.latitude);
  const lng = Number(point.lng ?? point.lon ?? point.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const parseCoordinateList = (coordinates = []) => (
  Array.isArray(coordinates)
    ? coordinates.map(parseCoordinatePair).filter(Boolean)
    : []
);

const getZoneOverlayGeometry = (zone) => {
  const geometry = zone?.geometry || {};
  const type = geometry.type || zone?.type;
  if (type === 'circle') {
    const center = parseCoordinatePair(geometry.center || zone?.center || zone?.coordinates?.[0]);
    const radius = Number(geometry.radius ?? zone?.radius ?? 100);
    return center && Number.isFinite(radius) ? { type: 'circle', center, radius } : null;
  }
  const rawCoordinates = geometry.coordinates || zone?.coordinates || [];
  const ring = Array.isArray(rawCoordinates?.[0]?.[0]) ? rawCoordinates[0] : rawCoordinates;
  const path = parseCoordinateList(ring);
  return path.length >= 3 ? { type: 'polygon', path } : null;
};

const getPlaceOverlayGeometry = (place) => {
  const center = parseCoordinatePair(place?.coordinates || place?.location || place);
  const radius = Number(place?.radius ?? 50);
  return center && Number.isFinite(radius) ? { center, radius } : null;
};

const getRouteOverlayPath = (route) => parseCoordinateList(route?.path_coordinates || route?.coordinates || route?.path || []);

const createLeafletTextLabelIcon = (label, color, offsetY = null) => L.divIcon({
  className: '',
  html: `<div style="transform:translate(-50%,${offsetY == null ? '-50%' : `${offsetY}px`});white-space:nowrap;border-radius:999px;background:${color};color:white;padding:3px 8px;font-size:11px;font-weight:700;box-shadow:0 2px 8px rgba(15,23,42,.28);border:1px solid rgba(255,255,255,.7);" data-testid="geofence-overlay-label">${String(label || '').replace(/[<>&"]/g, '')}</div>`,
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const getPathCenter = (path = []) => {
  if (!path.length) return null;
  const midpoint = path[Math.floor(path.length / 2)];
  return midpoint || null;
};

const getActiveEventSignature = (eventsByDevice = {}) => {
  if (!eventsByDevice || typeof eventsByDevice !== 'object') return '';
  return Object.entries(eventsByDevice)
    .map(([deviceId, occurrences]) => {
      const occurrenceSignature = (Array.isArray(occurrences) ? occurrences : [])
        .map((occurrence) => [
          occurrence?.id || '',
          occurrence?.status || '',
          occurrence?.severity || '',
          occurrence?.event_type || '',
          occurrence?.triggered_at || '',
          occurrence?.latitude ?? '',
          occurrence?.longitude ?? '',
        ].join('~'))
        .sort()
        .join('|');
      return `${deviceId}:${occurrenceSignature}`;
    })
    .sort()
    .join('||');
};

const isTouchMapControlViewport = () => (
  typeof window !== 'undefined'
    && (window.matchMedia('(max-width: 1023px)').matches || window.matchMedia('(pointer: coarse)').matches)
);

// Suppress known non-actionable Google Maps / Canvas2D console noise.
// These fire on every render frame and flood the console, blocking the UI thread.
const _origConsoleWarn = console.warn;
const _origConsoleError = console.error;
const _suppressPattern = /preregistered map type|willReadFrequently|Multiple readback operations/;
console.warn = (...args) => {
  if (typeof args[0] === 'string' && _suppressPattern.test(args[0])) return;
  _origConsoleWarn.apply(console, args);
};
console.error = (...args) => {
  if (typeof args[0] === 'string' && _suppressPattern.test(args[0])) return;
  _origConsoleError.apply(console, args);
};

// Patch Canvas2D globally: set willReadFrequently=true on all 2D contexts to eliminate
// Chrome's "Multiple readback operations" warning from Google Maps & deck.gl internals.
const _origGetContext = HTMLCanvasElement.prototype.getContext;
HTMLCanvasElement.prototype.getContext = function(type, attrs) {
  if (type === '2d') {
    return _origGetContext.call(this, type, { willReadFrequently: true, ...attrs });
  }
  return _origGetContext.call(this, type, attrs);
};

// Stable style reference — avoids creating a new object on every render
const FULL_SIZE_STYLE = { width: '100%', height: '100%' };

const defaultCenter = {
  lat: -33.8688,
  lng: 151.2093, // Sydney
};

// Status colors shared by both Leaflet and Google markers — matches Device Management
const STATUS_COLORS = {
  moving: '#2563eb',
  online: '#16a34a',
  idle: '#ca8a04',
  stopped: '#dc2626',
  offline: '#6b7280',
};

const isAssetBeaconDevice = (device) => device?.device_type === 'beacon' && (device?.beacon_purpose || 'asset') === 'asset';

const getBeaconObservedBadgeText = (device) => {
  if (!isAssetBeaconDevice(device)) return null;
  const confidence = device.beacon_confidence || device.beacon_live_state?.confidence || 'low';
  return `Observed · ${confidence}`;
};

const MARKER_SIZE = 44;
const OSM_MARKER_ANCHOR = [22, 26];
const GOOGLE_MARKER_TRANSLATE = 'translate(-50%, -58%)';
const SNAP_ENFORCEMENT_WAIT_MS = 1500;
const MIN_ANIMATION_DURATION_MS = 4000;
const ANIMATION_STALE_SYNC_MS = 10000;
const MANUAL_SELECTION_HOLD_MS = 5000;
const POSITION_DRIFT_RESET_SCORE = 0.00025;
const DEFAULT_SNAPPED_POINT_SIZE_PX = 5;
const DEFAULT_SNAPPED_POINT_COLOR = '#06b6d4';

const normalizeHeading = (heading = 0) => ((heading % 360) + 360) % 360;
const roundPositionCoord = (value) => Math.round(value * 1e6) / 1e6;
const coordDistanceScore = (point, target) => Math.abs(point.lat - target.lat) + Math.abs(point.lng - target.lng);
const hasCoordinatePair = (lat, lng) => lat != null && lng != null;
const isLiveDeviceStreamPayload = (data) => {
  if (!data || typeof data !== 'object' || typeof data.type === 'string') return false;
  return (
    'latitude' in data ||
    'longitude' in data ||
    'status' in data ||
    'speed' in data ||
    'heading' in data ||
    'server_timestamp' in data ||
    'imei' in data
  );
};

/**
 * Detect if a coordinate path loops back on itself (self-intersection).
 * Used to detect artificial roundabout loops injected by OSRM map matching.
 * Returns true if any point comes within `thresholdDeg` of a non-adjacent earlier point.
 * ~0.0003 degrees ≈ 30m at mid-latitudes.
 * @param {Array<{lat, lng}>} path
 * @param {number} thresholdDeg - proximity threshold in degrees
 * @param {number} minGap - minimum index gap between compared points (skips adjacent points on the same road segment)
 */
const pathHasSelfIntersection = (path, thresholdDeg = 0.0003, minGap = 15) => {
  if (!path || path.length < minGap + 5) return false;
  // Sample every 2nd point for performance on dense OSRM paths (hundreds of points)
  for (let i = minGap; i < path.length; i += 2) {
    const p = path[i];
    for (let j = 0; j <= i - minGap; j += 2) {
      if (Math.abs(p.lat - path[j].lat) < thresholdDeg &&
          Math.abs(p.lng - path[j].lng) < thresholdDeg) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Calculate bearing (heading) in degrees from point p1 to point p2.
 * Returns 0-360 where 0=North, 90=East, 180=South, 270=West.
 */
const calcBearing = (p1, p2) => {
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const lat1 = p1.lat * Math.PI / 180;
  const lat2 = p2.lat * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
};

/** Create a Leaflet divIcon chevron marker pointing in a given heading direction */
const createChevronIcon = (heading, color = '#06b6d4', size = 10) => {
  const half = size / 2;
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center"><svg width="${size}" height="${size}" viewBox="0 0 12 12" style="transform:rotate(${heading}deg);transform-origin:center;flex-shrink:0" xmlns="http://www.w3.org/2000/svg"><path d="M6 1L10.5 9L6 7L1.5 9Z" fill="${color}" stroke="#fff" stroke-width="0.8" stroke-linejoin="round"/></svg></div>`,
    iconSize: [size, size],
    iconAnchor: [half, half],
  });
};

// ── Speed Limit Sign (Google Maps overlay component) ──
const SpeedLimitBadge = ({ limit }) => {
  if (limit == null) return null;
  const display = Math.round(convertSpeed(limit) ?? limit);
  return (
    <div style={{ position: 'relative', width: 0, height: 0 }}>
      <div style={{
        position: 'absolute',
        left: '18px',
        top: '10px',
        width: '32px',
        height: '32px',
        pointerEvents: 'none',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
      }} data-testid="speed-limit-badge">
        <svg viewBox="0 0 455.111 455.111" width="32" height="32">
          <path fill="#E24C4B" d="M227.556,0C102.4,0,0,102.4,0,227.556s102.4,227.556,227.556,227.556s227.556-102.4,227.556-227.556S352.711,0,227.556,0z" />
          <path fill="#FFFFFF" d="M227.556,412.444c-102.4,0-184.889-82.489-184.889-184.889S125.156,42.667,227.556,42.667s184.889,82.489,184.889,184.889S329.956,412.444,227.556,412.444z" />
          <text x="227.556" y="245" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize={display >= 100 ? '160' : '180'} fill="#1a1a1a">{display}</text>
        </svg>
      </div>
    </div>
  );
};

// Speed limit Leaflet HTML for L.divIcon overlay
const createSpeedLimitLeafletHtml = (limit) => {
  if (limit == null) return '';
  const display = Math.round(convertSpeed(limit) ?? limit);
  return `<div style="position:absolute;left:18px;top:10px;width:32px;height:32px;pointer-events:none;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));" data-testid="speed-limit-badge-leaflet">
    <svg viewBox="0 0 455.111 455.111" width="32" height="32">
      <path fill="#E24C4B" d="M227.556,0C102.4,0,0,102.4,0,227.556s102.4,227.556,227.556,227.556s227.556-102.4,227.556-227.556S352.711,0,227.556,0z"/>
      <path fill="#FFFFFF" d="M227.556,412.444c-102.4,0-184.889-82.489-184.889-184.889S125.156,42.667,227.556,42.667s184.889,82.489,184.889,184.889S329.956,412.444,227.556,412.444z"/>
      <text x="227.556" y="245" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-weight="bold" font-size="${display >= 100 ? '160' : '180'}" fill="#1a1a1a">${display}</text>
    </svg>
  </div>`;
};

// Format a proximity distance (in km) for the Nearest Devices panel in the user's unit.
// Mirrors the metric m/km split with an imperial ft/mi split when the unit is miles.
const formatNearestDistance = (km) => {
  if (getDistanceUnit() === 'mi') {
    const mi = convertDistance(km) ?? km;
    return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(1)} mi`;
  }
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
};

// Custom marker icon creator for Leaflet (OSM) — always arrow, optional label
const createLeafletIcon = (status, heading = 0, label = null, stopTimer = null) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.offline;
  const stopTimerHtml = stopTimer ? `
    <div style="
      position: absolute;
      left: 50%;
      top: -26px;
      transform: translateX(-50%);
      padding: 2px 6px;
      background: ${stopTimer.color};
      border-radius: 4px;
      font-size: 10px;
      font-weight: 700;
      color: white;
      white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      pointer-events: none;
    ">${stopTimer.label || 'Stopped'} ${stopTimer.timeStr}</div>
  ` : '';

  if (!label) {
    return L.divIcon({
      className: 'custom-marker-arrow',
      html: `
        <div style="position:relative;width:44px;height:44px;">
          <div style="
            width: 44px;
            height: 44px;
            transform: translateY(-2px) rotate(${heading}deg);
            display: flex;
            align-items: center;
            justify-content: center;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          ">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5">
              <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
            </svg>
          </div>
          ${stopTimerHtml}
        </div>
      `,
      iconSize: [MARKER_SIZE, MARKER_SIZE],
      iconAnchor: OSM_MARKER_ANCHOR,
      popupAnchor: [0, -26],
    });
  }

  // With label: the arrow stays anchored at [22,22] = its center.
  // The label is absolutely positioned to the right so it doesn't affect layout.
  const escapedLabel = label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return L.divIcon({
    className: 'custom-marker-arrow-labeled',
    html: `
      <div style="position:relative;width:44px;height:44px;">
        <div style="
          width: 44px;
          height: 44px;
          transform: translateY(-2px) rotate(${heading}deg);
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        ">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5">
            <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
          </svg>
        </div>
        <div style="
          position: absolute;
          left: 46px;
          top: 50%;
          transform: translateY(-50%);
          padding: 3px 8px;
          background: rgba(255,255,255,0.92);
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          color: #1e293b;
          box-shadow: 0 1px 4px rgba(0,0,0,0.18);
          line-height: 1.3;
          pointer-events: none;
          white-space: nowrap;
        "><div style="
            position: absolute;
            left: -6px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-right: 6px solid rgba(255,255,255,0.92);
          "></div>${escapedLabel}</div>
        ${stopTimerHtml}
      </div>
    `,
    iconSize: [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: OSM_MARKER_ANCHOR,
    popupAnchor: [0, -26],
  });
};

// Leaflet cluster icon
const createClusterIcon = (count) => {
  const size = count < 10 ? 36 : count < 100 ? 42 : 48;
  const bg = count < 10 ? '#2563eb' : count < 100 ? '#7c3aed' : '#dc2626';
  return L.divIcon({
    className: 'cluster-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${bg};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: ${count < 100 ? '13' : '11'}px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">${count}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Leaflet icon-based marker for devices with map_marker_mode === 'icon'
const createLeafletDeviceIconMarker = (device, label = null, stopTimer = null, customIcons = []) => {
  const iconName = device.device_icon || 'car';
  const iconColor = device.device_icon_color || null;

  const stopTimerHtml = stopTimer ? `
    <div style="
      position: absolute; left: 50%; top: -26px; transform: translateX(-50%);
      padding: 2px 6px; background: ${stopTimer.color}; border-radius: 4px;
      font-size: 10px; font-weight: 700; color: white; white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3); pointer-events: none;
    ">${stopTimer.label || 'Stopped'} ${stopTimer.timeStr}</div>
  ` : '';

  let iconHtml;
  if (isCustomIcon(iconName)) {
    const custom = customIcons.find(c => c.id === iconName);
    const src = custom ? (custom.thumb_url || custom.full_url) : '';
    iconHtml = src ? `<img src="${src}" style="width:32px;height:32px;object-fit:contain;" />` : 
      `<svg width="32" height="32" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1.5"><path d="M12 2l-5 18.5L12 17l5 3.5z"/></svg>`;
  } else {
    // Built-in SVG icon — render React component to static HTML string
    const svgElement = deviceIcons[iconName] || deviceIcons.device;
    const applyColor = iconColor && !multicolorIcons.has(iconName);
    const colorStyle = applyColor ? `color:${iconColor}` : 'color:#1f2937';
    try {
      const svgMarkup = renderToStaticMarkup(svgElement);
      iconHtml = `<div style="width:32px;height:32px;${colorStyle};display:flex;align-items:center;justify-content:center;">${svgMarkup}</div>`;
    } catch {
      iconHtml = `<svg width="32" height="32" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="1.5"><path d="M12 2l-5 18.5L12 17l5 3.5z"/></svg>`;
    }
  }

  const labelHtml = label ? `
    <div style="
      position: absolute; left: 32px; top: 50%; transform: translateY(-50%);
      padding: 3px 8px; background: rgba(255,255,255,0.92); border-radius: 4px;
      font-size: 12px; font-weight: 500; color: #1e293b;
      box-shadow: 0 1px 4px rgba(0,0,0,0.18); line-height: 1.3;
      pointer-events: none; white-space: nowrap;
    "><div style="
        position: absolute; left: -6px; top: 50%; transform: translateY(-50%);
        width: 0; height: 0; border-top: 6px solid transparent;
        border-bottom: 6px solid transparent; border-right: 6px solid rgba(255,255,255,0.92);
      "></div>${label.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
  ` : '';

  return L.divIcon({
    className: 'custom-marker-icon-mode',
    html: `
      <div data-testid="leaflet-device-icon-marker-${device.id}" style="position:relative;width:44px;height:44px;">
        <div style="
          width: 44px; height: 44px;
          display: flex; align-items: center; justify-content: center;
          background: transparent; border: 0;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));
          overflow: visible;
        ">
          ${iconHtml}
        </div>
        ${labelHtml}
        ${stopTimerHtml}
      </div>
    `,
    iconSize: [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: OSM_MARKER_ANCHOR,
    popupAnchor: [0, -26],
  });
};

// Google Maps AdvancedMarker content — directional arrow for all devices
// Uses a 0-height wrapper so the layout anchor stays exactly at the GPS point,
// then positions the visual content centered on that point via translate.
const DeviceMarkerContent = ({ status, heading = 0, label = null, stopTimer = null }) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.offline;

  if (!label && !stopTimer) {
    return (
      <div style={{ width: 0, height: 0, overflow: 'visible' }}>
        <div style={{
          width: '44px',
          height: '44px',
          transform: GOOGLE_MARKER_TRANSLATE + ' rotate(' + heading + 'deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill={color} stroke="white" strokeWidth="1.5">
            <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
          </svg>
        </div>
        {stopTimer && (
          <div style={{
            position: 'absolute',
            left: '50%',
            top: '-30px',
            transform: 'translateX(-50%)',
            padding: '2px 6px',
            background: stopTimer.color,
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 700,
            color: 'white',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}>
            {stopTimer.label || 'Stopped'} {stopTimer.timeStr}
          </div>
        )}
      </div>
    );
  }

  // With label — keep the 0x0 anchor wrapper so AdvancedMarker doesn't shift.
  // Arrow is centered via translate(-50%,-50%), label floats to the right absolutely.
  return (
    <div style={{ width: 0, height: 0, overflow: 'visible' }}>
      <div style={{
        width: '44px',
        height: '44px',
        transform: `${GOOGLE_MARKER_TRANSLATE} rotate(${heading}deg)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
      }}>
        <svg width="44" height="44" viewBox="0 0 24 24" fill={color} stroke="white" strokeWidth="1.5">
          <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
        </svg>
      </div>
      <div style={{
        position: 'absolute',
        left: '24px',
        top: '-10px',
        padding: '3px 8px',
        background: 'rgba(255,255,255,0.92)',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        color: '#1e293b',
        boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
        lineHeight: '1.3',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}>
        {/* Left-pointing arrow tail */}
        <div style={{
          position: 'absolute',
          left: '-6px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent',
          borderRight: '6px solid rgba(255,255,255,0.92)',
        }} />
        {label}
      </div>
      {stopTimer && (
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '-30px',
          transform: 'translateX(-50%)',
          padding: '2px 6px',
          background: stopTimer.color,
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 700,
          color: 'white',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
        }}>
          {stopTimer.label || 'Stopped'} {stopTimer.timeStr}
        </div>
      )}
    </div>
  );
};

const BeaconMarkerContent = ({ device, label = null }) => {
  const confidence = device.beacon_confidence || device.beacon_live_state?.confidence || 'low';
  // Stale (not observed within the online window) → greyed + faded marker.
  const stale = device.status === 'offline';
  const color = stale ? '#6b7280' : confidence === 'high' ? '#0f766e' : confidence === 'medium' ? '#2563eb' : '#64748b';
  return (
    <div style={{ width: 0, height: 0, overflow: 'visible' }} data-testid={`google-asset-beacon-marker-${device.id}`} data-stale={stale ? 'true' : 'false'}>
      <div style={{
        width: 42,
        height: 42,
        transform: 'translate(-50%, -58%)',
        borderRadius: 12,
        background: color,
        border: '2px solid #ffffff',
        boxShadow: stale ? '0 3px 10px rgba(15,23,42,.2)' : '0 6px 18px rgba(15,23,42,.35)',
        opacity: stale ? 0.55 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        cursor: 'pointer',
      }}>
        <Bluetooth className="w-5 h-5" />
      </div>
      {label && (
        <div style={{
          position: 'absolute',
          left: 24,
          top: -12,
          padding: '3px 8px',
          background: 'rgba(255,255,255,0.94)',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          color: '#0f172a',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          opacity: stale ? 0.6 : 1,
        }}>{label}</div>
      )}
    </div>
  );
};

const createLeafletBeaconIcon = (device, label = null) => {
  const confidence = device.beacon_confidence || device.beacon_live_state?.confidence || 'low';
  const stale = device.status === 'offline';
  const color = stale ? '#6b7280' : confidence === 'high' ? '#0f766e' : confidence === 'medium' ? '#2563eb' : '#64748b';
  const safeLabel = label ? String(label).replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
  return L.divIcon({
    className: '',
    html: `<div data-testid="leaflet-asset-beacon-marker-${device.id}" data-stale="${stale}" style="position:relative;width:44px;height:44px;">
      <div style="width:42px;height:42px;transform:translateY(-2px);border-radius:12px;background:${color};border:2px solid #fff;box-shadow:0 ${stale ? '3px 10px rgba(15,23,42,.2)' : '6px 18px rgba(15,23,42,.35)'};opacity:${stale ? 0.55 : 1};display:flex;align-items:center;justify-content:center;color:white;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 5V2l5 5L7 17"/></svg>
      </div>
      ${safeLabel ? `<div style="position:absolute;left:46px;top:50%;transform:translateY(-50%);padding:3px 8px;background:rgba(255,255,255,.94);border-radius:4px;font-size:12px;font-weight:600;color:#0f172a;box-shadow:0 1px 4px rgba(0,0,0,.18);white-space:nowrap;pointer-events:none;opacity:${stale ? 0.6 : 1};">${safeLabel}</div>` : ''}
    </div>`,
    iconSize: [MARKER_SIZE, MARKER_SIZE],
    iconAnchor: OSM_MARKER_ANCHOR,
    popupAnchor: [0, -26],
  });
};

// Google Maps cluster marker
const ClusterMarkerContent = ({ count }) => {
  const size = count < 10 ? 36 : count < 100 ? 42 : 48;
  const bg = count < 10 ? '#2563eb' : count < 100 ? '#7c3aed' : '#dc2626';
  return (
    <div style={{
      width: `${size}px`,
      height: `${size}px`,
      background: bg,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 700,
      fontSize: count < 100 ? '13px' : '11px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      border: '3px solid white',
      cursor: 'pointer',
    }}>
      {count}
    </div>
  );
};

// Google Maps icon-mode marker for devices with map_marker_mode === 'icon'
const DeviceIconMarkerContent = ({ device, label = null, stopTimer = null, customIcons = [] }) => {
  const iconName = device.device_icon || 'car';
  const iconColor = device.device_icon_color || null;

  return (
    <div style={{ width: 0, height: 0, overflow: 'visible' }}>
      <div data-testid={`google-device-icon-marker-${device.id}`} style={{
        width: '44px',
        height: '44px',
        transform: 'translate(-50%, -58%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 0,
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))',
        cursor: 'pointer',
        overflow: 'visible',
      }}>
        <DeviceIconDisplay
          iconName={iconName}
          className="w-8 h-8"
          customIcons={customIcons}
          useThumb={true}
          iconColor={iconColor || '#1f2937'}
        />
      </div>
      {label && (
        <div style={{
          position: 'absolute',
          left: '24px',
          top: '-10px',
          padding: '3px 8px',
          background: 'rgba(255,255,255,0.92)',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 500,
          color: '#1e293b',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          lineHeight: '1.3',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          <div style={{
            position: 'absolute', left: '-6px', top: '50%', transform: 'translateY(-50%)',
            width: 0, height: 0, borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent', borderRight: '6px solid rgba(255,255,255,0.92)',
          }} />
          {label}
        </div>
      )}
      {stopTimer && (
        <div style={{
          position: 'absolute', left: '50%', top: '-30px', transform: 'translateX(-50%)',
          padding: '2px 6px', background: stopTimer.color, borderRadius: '4px',
          fontSize: '10px', fontWeight: 700, color: 'white', whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)', pointerEvents: 'none',
        }}>
          {stopTimer.label || 'Stopped'} {stopTimer.timeStr}
        </div>
      )}
    </div>
  );
};

// Google Maps fly-to controller — uses shared GoogleMapController for map ref
const LiveTrackingMapController = ({ selectedDevice, devices, onMapReady, onBoundsChange, onMapClick, getDisplayPosition, skipPan }) => {
  const map = useGoogleMap();
  const prevSelectedRef = useRef(selectedDevice);

  useEffect(() => {
    if (map && onMapReady) onMapReady(map);
  }, [map, onMapReady]);

  // Map click handler for Nearest Device Finder
  useEffect(() => {
    if (!map || !onMapClick) return;
    const listener = map.addListener('click', (e) => {
      onMapClick(e.latLng.lat(), e.latLng.lng());
    });
    return () => { if (listener) window.google?.maps?.event?.removeListener(listener); };
  }, [map, onMapClick]);

  // Track bounds/zoom changes for clustering
  useEffect(() => {
    if (!map || !onBoundsChange) return;
    const listener = map.addListener('idle', () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        onBoundsChange([sw.lng(), sw.lat(), ne.lng(), ne.lat()], zoom);
      }
    });
    return () => { if (listener) window.google?.maps?.event?.removeListener(listener); };
  }, [map, onBoundsChange]);

  // Only pan when selectedDevice VALUE changes — not when map is recreated (POI toggle)
  // Skip when an event coordinate pan is active (skipPan=true) to avoid overriding it
  useEffect(() => {
    if (skipPan) { prevSelectedRef.current = selectedDevice; return; }
    if (selectedDevice && selectedDevice !== prevSelectedRef.current && map) {
      const device = devices.find(d => d.id === selectedDevice);
      const pos = device && getDisplayPosition ? getDisplayPosition(device) : null;
      if (pos?.lat != null && pos?.lng != null) {
        map.panTo({ lat: pos.lat, lng: pos.lng });
        map.setZoom(18);
      }
    }
    prevSelectedRef.current = selectedDevice;
  }, [selectedDevice, map, devices, getDisplayPosition, skipPan]);

  return null;
};

// Leaflet map controller — handles fly-to and bounds tracking
const LeafletMapController = ({ selectedDevice, devices, onBoundsChange, onMapClick, getDisplayPosition, skipPan }) => {
  const map = useMap();
  const prevSelectedRef = useRef(selectedDevice);

  useMapEvents({
    moveend: () => {
      if (!onBoundsChange) return;
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onBoundsChange(
        [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        zoom
      );
    },
    click: (e) => {
      if (onMapClick) onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });

  // Only fly-to when selectedDevice VALUE changes — not on mount (avoids fly from default center)
  // Skip when an event coordinate pan is active (skipPan=true) to avoid overriding it
  useEffect(() => {
    if (skipPan) { prevSelectedRef.current = selectedDevice; return; }
    if (selectedDevice && selectedDevice !== prevSelectedRef.current) {
      const device = devices.find(d => d.id === selectedDevice);
      const pos = device && getDisplayPosition ? getDisplayPosition(device) : null;
      if (pos?.lat != null && pos?.lng != null) {
        map.flyTo([pos.lat, pos.lng], 18);
      }
    }
    prevSelectedRef.current = selectedDevice;
  }, [selectedDevice, map, devices, getDisplayPosition, skipPan]);

  // Fire initial bounds on mount and fix container sizing
  useEffect(() => {
    // Leaflet calculates container size on mount — if the flex layout hasn't
    // fully settled yet, tiles won't fill the space. invalidateSize forces a
    // re-measure after the browser has had time to finalize layout.
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (onBoundsChange) {
        const bounds = map.getBounds();
        const zoom = map.getZoom();
        if (bounds) {
          onBoundsChange(
            [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
            zoom
          );
        }
      }
    }, 200);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Keep map sized correctly when the container resizes (sidebar toggle, window resize)
  useEffect(() => {
    const container = map.getContainer();
    if (!container || !window.ResizeObserver) return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(container);
    return () => ro.disconnect();
  }, [map]);

  return null;
};

export const LiveTracking = () => {
  const { api, user: currentUser, wsConnected, subscribeToDevices, onDeviceUpdate, soundEnabled } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    hasOSMMaps,
    hasGoogleMaps, 
    hasSatelliteView, 
    hasTerrainView, 
    hasTrafficLayer,
    hasHeatmap,
    hasDarkMode,
    hasWeather,
    hasNearestDevice,
    isFeatureEnabled,
    isSubpageEnabled,
  } = useFeatureFlags();

  // ── Persisted state from LiveTrackingContext ──
  const lt = useLiveTracking();
  const { selectedDevice, setSelectedDevice } = lt;
  const { followedDevices, setFollowedDevices, toggleFollow: handleToggleFollow } = lt;
  const { hiddenDevices, setHiddenDevices, toggleHide: handleToggleHide } = lt;
  const { trailEnabledDevices, toggleDeviceTrail: handleToggleDeviceTrail } = lt;
  const { lastMapCenter, setLastMapCenter, lastMapZoom, setLastMapZoom } = lt;
  const { showTraffic, setShowTraffic, showPOI, setShowPOI } = lt;
  const { showHeatmap, setShowHeatmap, darkModeMap, setDarkModeMap } = lt;
  const { showWeather, setShowWeather, weatherLayer, setWeatherLayer } = lt;
  const { showGeofenceOverlay, setShowGeofenceOverlay, geofenceOverlayTypes, setGeofenceOverlayTypes } = lt;
  const { mapFeatureSettings, setMapFeatureSettings } = lt;
  const { positionHistoryRef, animStateRef, animPositionsRef } = lt;

  // ── Local-only state (reset on navigation is OK) ──
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const devicesRef = useRef([]);
  const [customIcons, setCustomIcons] = useState([]);
  const [telemetryRefreshKey, setTelemetryRefreshKey] = useState(0);
  // Flag to prevent map controllers from auto-panning to device position
  // when we're panning to specific event coordinates from URL params
  const [eventPanActive, setEventPanActive] = useState(false);
  // Event marker displayed on map when navigating via "View Event on Map"
  const [eventMarker, setEventMarker] = useState(null); // { eventId, lat, lng, severity }
  const [forensicModalOpen, setForensicModalOpen] = useState(false);

  // Google Map staleness watchdog — remounts the map after long idle so
  // expired vector-style sessions don't leak default POI bubbles.
  const gmapEpoch = useMapStaleGuard();

  // Load map feature settings from backend on mount (ensures settings survive page refresh)
  useEffect(() => {
    if (mapFeatureSettings) return; // Already loaded from session
    api.get('/map-features/settings')
      .then((res) => {
        if (res.data?.settings && Object.keys(res.data.settings).length > 0) {
          setMapFeatureSettings(res.data.settings);
        }
      })
      .catch(() => {}); // Silent fail
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Road-snapped trail paths (keyed by deviceId) — use state to trigger re-renders
  const snappedTrailsRef = useRef({});
  const trailSnapPendingRef = useRef({});
  const trailSnapRequestRef = useRef({});
  const lastRenderedPositionsRef = useRef({});
  const recentPositionBufferRef = useRef({});
  // On mount, seed the buffer from context's persisted trail data (fills gap when returning to page)
  useEffect(() => {
    const existing = positionHistoryRef.current;
    if (existing && Object.keys(existing).length > 0) {
      for (const [deviceId, points] of Object.entries(existing)) {
        if (points?.length > 0 && !recentPositionBufferRef.current[deviceId]?.length) {
          recentPositionBufferRef.current[deviceId] = [...points];
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const recentHistoryLoadingRef = useRef(new Set());
  const trailPreloadedRef = useRef(new Set());
  const trailLoadQueueRef = useRef([]);
  const trailLoadActiveRef = useRef(0);
  const animQueueRef = useRef({});
  const animSequenceRef = useRef(0);
  const manualSelectionOverrideRef = useRef({ deviceId: null, until: 0 });
  const [, setSnapVersion] = useState(0);

  // User's timezone from profile settings (fallback to browser timezone)
  const userTimezone = currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Feature flags for More menu options
  const sharePositionEnabled = isFeatureEnabled('share_position');
  const generateLinkEnabled = isFeatureEnabled('generate_link');
  const liveVideoAccessEnabled = isFeatureEnabled('media_page') && isFeatureEnabled('media_live_videos');

  // Map Enhancement Features privileges
  const mapEnhancementPrivileges = {
    trip_direction_trail: isFeatureEnabled('trip_direction_trail'),
    animated_movement: isFeatureEnabled('animated_movement'),
    live_stop_timer: isFeatureEnabled('live_stop_timer'),
    device_card_customization: isFeatureEnabled('device_card_customization'),
    speed_alert_config: isFeatureEnabled('speed_alert_config'),
    snap_to_road: isFeatureEnabled('snap_to_road'),
  };
  const hasGeofenceMapOverlay = isFeatureEnabled('map_geofence_overlay') && isFeatureEnabled('geofencing_page');
  const hasAnyMapEnhancement = Object.values(mapEnhancementPrivileges).some(Boolean);

  const [geofenceOverlayData, setGeofenceOverlayData] = useState({ zones: [], places: [], routes: [] });
  const [geofenceOverlayLoading, setGeofenceOverlayLoading] = useState(false);
  const [showGeofenceOverlayPicker, setShowGeofenceOverlayPicker] = useState(false);
  const geofenceOverlayPickerRef = useRef(null);

  // More Features Modal state
  const [moreFeaturesOpen, setMoreFeaturesOpen] = useState(false);
  const [liveVideoDevice, setLiveVideoDevice] = useState(null);

  // ── Road Speed Limits (from context — persisted across navigation) ──
  const { roadSpeedLimits, setRoadSpeedLimits } = lt;
  const roadSpeedLimitTimerRef = useRef(null);
  const lastSpeedLimitCoordsRef = useRef({});
  const roadSpeedLimitsRef = useRef(roadSpeedLimits);
  const [speedLimitRefreshMode, setSpeedLimitRefreshMode] = useState({ mode: 'websocket', timer_interval_seconds: 10 });

  // ── Event Engine: Active events by device (replaces client-side speed evaluation) ──
  const [activeEventsByDevice, setActiveEventsByDevice] = useState({});
  const [alertCardColors, setAlertCardColors] = useState({});
  const activeEventsSignatureRef = useRef('');
  const [transitionFlashCardColors, setTransitionFlashCardColors] = useState({});
  const activeEventsTimerRef = useRef(null);
  const transitionCardFlashTimeoutsRef = useRef({});
  const fetchActiveEventsRef = useRef(null);
  const fetchAlertCardColorsRef = useRef(null);
  const followedDevicesRef = useRef(followedDevices);
  const mapTypeRef = useRef(null);
  const getDisplayPositionForDeviceIdRef = useRef(null);
  const startDeviceAnimationRef = useRef(null);
  const updatePositionHistoryRef = useRef(null);
  const fetchSingleDeviceSpeedLimitRef = useRef(null);
  const isIgnitionOnRef = useRef(null);
  const speedLimitRefreshModeRef = useRef(speedLimitRefreshMode.mode);

  // Keep ref in sync with state
  useEffect(() => {
    roadSpeedLimitsRef.current = roadSpeedLimits;
  }, [roadSpeedLimits]);

  // Load speed limit refresh mode config from backend
  useEffect(() => {
    api.get('/speed-limit-refresh-mode')
      .then(res => { if (res.data?.mode) setSpeedLimitRefreshMode(res.data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: check if device has ignition ON
  const isIgnitionOn = useCallback((device) => {
    if (!device || device.status === 'offline') return false;
    const ignMap = device.io_mappings?.find(m => m.io_type?.toLowerCase() === 'ignition');
    if (ignMap && device.last_io_elements) {
      const v = device.last_io_elements[ignMap.io_id];
      if (v !== undefined) return Number(v) > 0;
    }
    const io = device.last_io_elements;
    if (io) {
      if (io['239'] !== undefined) return Number(io['239']) > 0;
      if (io['ignition'] !== undefined) return Number(io['ignition']) > 0;
      if (io['din1'] !== undefined) return Number(io['din1']) > 0;
    }
    return device.status === 'idle' || device.status === 'moving';
  }, []);

  // Fetch road speed limits for online devices with ignition ON
  const fetchRoadSpeedLimits = useCallback(async () => {
    if (!mapEnhancementPrivileges.speed_alert_config) return;
    const speedAlertSettings = mapFeatureSettings?.speedAlert;
    if (!speedAlertSettings?.enabled) return;

    const eligibleDevices = devicesRef.current.filter(d => 
      d.status !== 'offline' && d.last_latitude && d.last_longitude && isIgnitionOn(d)
    );

    if (eligibleDevices.length === 0) return;

    // Only query devices whose GPS position has changed OR that don't have a result yet
    const devicesToQuery = eligibleDevices.filter(d => {
      const coordKey = `${d.last_latitude.toFixed(4)},${d.last_longitude.toFixed(4)}`;
      const cached = lastSpeedLimitCoordsRef.current[d.id];
      if (cached === coordKey && roadSpeedLimitsRef.current[d.id]) return false;
      return true;
    });

    if (devicesToQuery.length === 0) return;

    try {
      const resp = await api.post('/road-speed-limits-batch', {
        devices: devicesToQuery.map(d => ({
          id: d.id,
          lat: d.last_latitude,
          lng: d.last_longitude,
          heading: d.last_heading ?? d.last_position?.angle ?? null,
        })),
      });
      if (resp.data?.results) {
        setRoadSpeedLimits(prev => {
          const next = { ...prev };
          // Clear limits for devices that went offline or ignition OFF
          const eligibleIds = new Set(eligibleDevices.map(d => d.id));
          for (const id of Object.keys(next)) {
            if (!eligibleIds.has(id)) delete next[id];
          }
          // Update with new results — only cache coords for successful lookups
          for (const [id, data] of Object.entries(resp.data.results)) {
            if (data.speed_limit != null) {
              next[id] = { limit: data.speed_limit, source: data.source || 'osm', road_name: data.road_name || '' };
              const dev = devicesToQuery.find(d => d.id === id);
              if (dev) {
                lastSpeedLimitCoordsRef.current[id] = `${dev.last_latitude.toFixed(4)},${dev.last_longitude.toFixed(4)}`;
              }
            }
          }
          return next;
        });
      }
    } catch {
      // Speed limit service not available — silent fail
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, mapEnhancementPrivileges.speed_alert_config, mapFeatureSettings?.speedAlert, isIgnitionOn]);

  // Fetch speed limit for a SINGLE device (used by WebSocket mode on position update)
  const fetchSingleDeviceSpeedLimit = useCallback(async (deviceId, lat, lng, heading) => {
    if (!mapEnhancementPrivileges.speed_alert_config) return;
    if (!mapFeatureSettings?.speedAlert?.enabled) return;
    try {
      const resp = await api.post('/road-speed-limits-batch', {
        devices: [{ id: deviceId, lat, lng, heading }],
      });
      if (resp.data?.results?.[deviceId]) {
        const data = resp.data.results[deviceId];
        if (data.speed_limit != null) {
          setRoadSpeedLimits(prev => ({
            ...prev,
            [deviceId]: { limit: data.speed_limit, source: data.source || 'osm', road_name: data.road_name || '' },
          }));
          lastSpeedLimitCoordsRef.current[deviceId] = `${lat.toFixed(4)},${lng.toFixed(4)}`;
        }
      }
    } catch {
      // Silent fail
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, mapEnhancementPrivileges.speed_alert_config, mapFeatureSettings?.speedAlert]);

  // Speed limit refresh — mode-dependent (WebSocket mode uses onDeviceUpdate, Timer mode uses interval)
  useEffect(() => {
    if (!mapEnhancementPrivileges.speed_alert_config || !mapFeatureSettings?.speedAlert?.enabled) {
      setRoadSpeedLimits({});
      return;
    }
    // Initial batch fetch for both modes (populate on page load)
    const t1 = setTimeout(fetchRoadSpeedLimits, 3000);
    const t2 = setTimeout(fetchRoadSpeedLimits, 8000);

    if (speedLimitRefreshMode.mode === 'timer') {
      // Timer mode: batch refresh at configured interval
      const interval = (speedLimitRefreshMode.timer_interval_seconds || 10) * 1000;
      roadSpeedLimitTimerRef.current = setInterval(fetchRoadSpeedLimits, interval);
    }
    // WebSocket mode: no timer needed (fetches are triggered per-device in onDeviceUpdate)

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (roadSpeedLimitTimerRef.current) clearInterval(roadSpeedLimitTimerRef.current);
    };
  }, [fetchRoadSpeedLimits, mapEnhancementPrivileges.speed_alert_config, mapFeatureSettings?.speedAlert?.enabled, speedLimitRefreshMode, setRoadSpeedLimits]);

  // ── Event Engine: Fetch active events by device (server-side evaluated) ──
  const fetchActiveEvents = useCallback(async () => {
    try {
      const resp = await api.get('/event-occurrences/active-by-device');
      if (resp.data) {
        const nextSignature = getActiveEventSignature(resp.data);
        if (nextSignature !== activeEventsSignatureRef.current) {
          activeEventsSignatureRef.current = nextSignature;
          setActiveEventsByDevice(resp.data);
        }
      }
    } catch {
      // Silent fail — event engine may not have events
    }
  }, [api]);

  useEffect(() => {
    fetchActiveEventsRef.current = fetchActiveEvents;
  }, [fetchActiveEvents]);

  // Active condition-alert card colours (Low Battery, Disconnected) — separate
  // source so the event-occurrence reconcile can't wipe them.
  const fetchAlertCardColors = useCallback(async () => {
    try {
      const resp = await api.get('/alerts/active-card-colors');
      setAlertCardColors(resp.data || {});
    } catch {
      // Silent fail
    }
  }, [api]);

  useEffect(() => {
    fetchAlertCardColorsRef.current = fetchAlertCardColors;
  }, [fetchAlertCardColors]);

  const clearTransitionCardColor = useCallback((deviceId, eventId = null) => {
    if (!deviceId) return;
    const existingTimeout = transitionCardFlashTimeoutsRef.current[deviceId];
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      delete transitionCardFlashTimeoutsRef.current[deviceId];
    }
    setTransitionFlashCardColors(prev => {
      const current = prev[deviceId];
      if (!current) return prev;
      if (eventId && current.eventId !== eventId) return prev;
      const next = { ...prev };
      delete next[deviceId];
      return next;
    });
  }, []);

  const scheduleTransitionCardFlash = useCallback((deviceId, notificationConfig, eventId) => {
    if (!deviceId || !notificationConfig?.change_device_card_color || !notificationConfig?.device_card_color) return;

    clearTransitionCardColor(deviceId);

    const mode = notificationConfig.transition_card_color_mode || '5s';
    const customSeconds = Math.max(1, parseInt(notificationConfig.transition_card_color_seconds, 10) || 5);
    const isPersistentUntilClick = mode === 'until_click';
    const durationMs = mode === '30s'
      ? 30000
      : mode === '10s'
        ? 10000
        : mode === 'custom'
          ? customSeconds * 1000
          : DEFAULT_TRANSITION_EVENT_CARD_FLASH_MS;

    setTransitionFlashCardColors(prev => ({
      ...prev,
      [deviceId]: {
        light: notificationConfig.device_card_color,
        dark: notificationConfig.device_card_color_dark || '',
        flash: !isPersistentUntilClick,
        persistentUntilClick: isPersistentUntilClick,
        eventId,
      },
    }));

    if (!isPersistentUntilClick) {
      transitionCardFlashTimeoutsRef.current[deviceId] = setTimeout(() => {
        clearTransitionCardColor(deviceId, eventId);
      }, durationMs);
    }
  }, [clearTransitionCardColor]);

  useEffect(() => () => {
    Object.values(transitionCardFlashTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
    transitionCardFlashTimeoutsRef.current = {};
  }, []);

  // Poll active events every 5 seconds (backup reconciliation — WebSocket is primary)
  useEffect(() => {

    fetchActiveEvents(); // Initial fetch immediately
    fetchAlertCardColors(); // Initial alert-colour fetch
    const t2 = setTimeout(fetchActiveEvents, 2000); // Second fetch after 2s (catches events during WS connection setup)
    // Backup reconcile poll: 5s only when WS is down; 30s when WS is healthy
    // (WS already pushes event create/clear instantly — frequent polling just
    // adds queue pressure during page load).
    let tick = 0;
    activeEventsTimerRef.current = setInterval(() => {
      tick += 1;
      if (wsConnectedRef.current && tick % 6 !== 0) return;
      fetchActiveEvents();
      fetchAlertCardColors();
    }, 5000);
    return () => {
      clearTimeout(t2);
      if (activeEventsTimerRef.current) clearInterval(activeEventsTimerRef.current);
    };
  }, [fetchActiveEvents, fetchAlertCardColors]);

  // ── WebSocket: Instantly update activeEventsByDevice on event create/clear ──
  useEffect(() => {
    // Coalesce optimistic event create/clear updates into ONE setActiveEventsByDevice per
    // animation frame, and DEBOUNCE the reconcile fetch. Previously every WS event message
    // fired a synchronous setActiveEventsByDevice AND an immediate fetchActiveEvents() — each
    // re-rendering the full ~1000-marker Live Tracking tree, which is the dominant cause of the
    // "[Violation] 'message' handler took ~190ms" on busy fleets (the device-position path is
    // already batched separately). A 5s backup poll still reconciles authoritative state.
    const pendingOps = []; // { kind:'add'|'remove', devId, id, occData? }
    let flushRafId = null;
    let reconcileTimer = null;

    const scheduleReconcile = () => {
      if (reconcileTimer != null) return;
      reconcileTimer = setTimeout(() => {
        reconcileTimer = null;
        fetchActiveEventsRef.current?.();
      }, 3000);
    };

    const flushEventOps = () => {
      flushRafId = null;
      if (pendingOps.length === 0) return;
      const ops = pendingOps.splice(0, pendingOps.length);
      setActiveEventsByDevice(prev => {
        const next = { ...prev };
        for (const op of ops) {
          if (op.kind === 'add') {
            const list = next[op.devId] ? [...next[op.devId]] : [];
            const idx = list.findIndex(o => o.id === op.id);
            if (idx >= 0) list[idx] = op.occData; else list.unshift(op.occData);
            next[op.devId] = list;
          } else if (op.kind === 'remove') {
            if (next[op.devId]) {
              const filtered = next[op.devId].filter(o => o.id !== op.id);
              if (filtered.length === 0) delete next[op.devId]; else next[op.devId] = filtered;
            }
          }
        }
        activeEventsSignatureRef.current = getActiveEventSignature(next);
        return next;
      });
    };

    const enqueue = (op) => {
      pendingOps.push(op);
      if (flushRafId == null) flushRafId = requestAnimationFrame(flushEventOps);
      scheduleReconcile();
    };

    const unsubscribe = onDeviceUpdate?.((deviceId, data) => {
      // Condition/health alerts (Low Battery, Device Disconnected) carry their card
      // colour via a dedicated source (they aren't event_occurrences). Refetch the
      // active-alert colours on any alert signal (raise = event_occurrence w/ these
      // event_types, resolve = bare 'alert' status poke) and skip the occurrence path.
      if (data?.type === 'alert' && !data.event_type) {
        fetchAlertCardColorsRef.current?.();
        return;
      }
      if (CONDITION_ALERT_EVENT_TYPES.has(data?.event_type)) {
        fetchAlertCardColorsRef.current?.();
        return;
      }
      // Event occurrence CREATED — instantly add to activeEventsByDevice
      if (data.type === 'event_occurrence' || (data.type === 'alert' && data.event_type && data.id && !data.event_type?.includes('cleared'))) {
        const devId = data.device_id;
        if (!devId) return;
        const nc = data.notifications || {};
        const isInstantTransitionEvent = Boolean(data.metadata?.instant_event) || data.status === 'cleared';
        if (isInstantTransitionEvent) {
          scheduleTransitionCardFlash(devId, nc, data.id);
          return;
        }
        const occData = {
          id: data.id,
          event_rule_id: data.event_rule_id,
          event_rule_name: data.event_rule_name,
          event_type: data.event_type,
          device_id: devId,
          device_name: data.device_name,
          severity: data.severity,
          status: 'active',
          speed_kmh: data.speed_kmh,
          threshold_kmh: data.threshold_kmh,
          road_speed_limit: data.road_speed_limit,
          triggered_at: data.triggered_at,
          metadata: data.metadata || {},
          device_timezone: data.device_timezone,
          latitude: data.latitude,
          longitude: data.longitude,
          notification_config: {
            change_device_card_color: nc.change_device_card_color || false,
            device_card_color: nc.device_card_color || '#ef4444',
            device_card_color_dark: nc.device_card_color_dark || '',
          },
        };
        enqueue({ kind: 'add', devId, id: data.id, occData });
      }

      // Event occurrence CLEARED — instantly remove from activeEventsByDevice
      if (data.type === 'event_occurrence_cleared' || (data.type === 'alert' && data.event_type === 'event_occurrence_cleared')) {
        const devId = data.device_id;
        if (!devId) return;
        enqueue({ kind: 'remove', devId, id: data.id });
      }
    });
    return () => {
      if (flushRafId != null) cancelAnimationFrame(flushRafId);
      if (reconcileTimer != null) clearTimeout(reconcileTimer);
      unsubscribe?.();
    };
  }, [onDeviceUpdate, scheduleTransitionCardFlash]);

  // ── Speed Alert Evaluation (Event Engine driven) ──
  // Derives speedingDevices from server-side Event Engine active occurrences
  const speedingDevices = useMemo(() => {
    const result = {};
    for (const [deviceId, occurrences] of Object.entries(activeEventsByDevice)) {
      // Find the first speed-related occurrence for this device
      const speedOcc = occurrences.find(o =>
        o.event_type === 'overspeed_fixed' || o.event_type === 'overspeed_road_sign'
      );
      if (speedOcc) {
        result[deviceId] = {
          isSpeeding: true,
          roadLimit: speedOcc.road_speed_limit,
          threshold: speedOcc.threshold_kmh,
          deviceSpeed: speedOcc.speed_kmh,
          severity: speedOcc.severity,
          eventRuleName: speedOcc.event_rule_name,
        };
      }
    }
    return result;
  }, [activeEventsByDevice]);

  // ── Event Card Colors: Extract per-device card color overrides from active events ──
  // Returns { deviceId: { light: '#ef4444', dark: '#5e2230' } }
  const eventCardColors = useMemo(() => {
    const colors = {};
    for (const [deviceId, occurrences] of Object.entries(activeEventsByDevice)) {
      for (const occ of occurrences) {
        const nc = occ.notification_config;
        if (nc?.change_device_card_color && nc?.device_card_color) {
          colors[deviceId] = {
            light: nc.device_card_color,
            dark: nc.device_card_color_dark || '',
            flash: false,
          };
          break;
        }
      }
    }
    for (const [deviceId, flashColor] of Object.entries(transitionFlashCardColors)) {
      colors[deviceId] = flashColor;
    }
    // Persistent condition-alert colours (Low Battery, Disconnected). Applied
    // last so an active alert keeps the device card tinted until it resolves.
    for (const [deviceId, alertColor] of Object.entries(alertCardColors)) {
      if (!colors[deviceId]) {
        colors[deviceId] = { light: alertColor.light, dark: alertColor.dark || '', flash: false };
      }
    }
    return colors;
  }, [activeEventsByDevice, transitionFlashCardColors, alertCardColors]);

  // Position history for trip direction trail — refs from context

  const getRawDevicePosition = useCallback((device) => {
    if (!device) return null;
    const pos = device.last_position || {};
    const lat = device.last_latitude ?? pos.latitude;
    const lng = device.last_longitude ?? pos.longitude;
    if (lat == null || lng == null) return null;
    // Preserve last known heading when GPS signal is lost (avoid resetting to North)
    const rawHeading = device.last_heading ?? pos.angle;
    const lastRendered = lastRenderedPositionsRef.current[device.id];
    return {
      lat,
      lng,
      heading: rawHeading ?? lastRendered?.heading ?? 0,
    };
  }, []);

  const computeAnimatedPositionFromState = useCallback((state, now = Date.now()) => {
    if (!state) return null;

    if (!state.motionStartTime) {
      return {
        lat: state.fromLat,
        lng: state.fromLng,
        heading: state.fromHeading,
        progress: 0,
      };
    }

    const duration = Math.max(state.motionDuration || state.duration || 0, 1);
    const elapsed = now - state.motionStartTime;
    const progress = Math.min(Math.max(elapsed / duration, 0), 1);
    const eased = 1 - Math.pow(1 - progress, 2);

    if (state.waypoints && state.cumulativeDistances && state.totalDistance > 0) {
      const interp = interpolateAlongPath(state.waypoints, state.cumulativeDistances, state.totalDistance, eased);
      if (interp) {
        return {
          lat: interp.lat,
          lng: interp.lng,
          heading: state.smoothRotation === false ? state.toHeading : progress > 0.9 ? state.toHeading : interp.heading,
          progress,
        };
      }
    }

    let headingDiff = state.toHeading - state.fromHeading;
    if (headingDiff > 180) headingDiff -= 360;
    if (headingDiff < -180) headingDiff += 360;

    return {
      lat: state.fromLat + (state.toLat - state.fromLat) * eased,
      lng: state.fromLng + (state.toLng - state.fromLng) * eased,
      heading: state.smoothRotation === false ? state.toHeading : state.fromHeading + headingDiff * eased,
      progress,
    };
  }, []);

  const getDeviceDisplayPosition = useCallback((device) => {
    const raw = getRawDevicePosition(device);
    const animSettings = mapFeatureSettings?.animation;
    const animationEnabled = animSettings?.enabled && mapEnhancementPrivileges.animated_movement;
    if (!animationEnabled || !device?.id) {
      return raw;
    }

    const liveAnim = animPositionsRef.current[device.id];
    if (liveAnim) {
      return { lat: liveAnim.lat, lng: liveAnim.lng, heading: liveAnim.heading };
    }

    const animState = animStateRef.current[device.id];
    const computed = computeAnimatedPositionFromState(animState);
    if (computed) {
      return { lat: computed.lat, lng: computed.lng, heading: normalizeHeading(computed.heading) };
    }

    const lastRendered = lastRenderedPositionsRef.current[device.id];
    if (lastRendered) {
      return { lat: lastRendered.lat, lng: lastRendered.lng, heading: lastRendered.heading };
    }

    return raw;
  }, [animPositionsRef, animStateRef, computeAnimatedPositionFromState, getRawDevicePosition, mapEnhancementPrivileges.animated_movement, mapFeatureSettings]);

  const getDisplayPositionForDeviceId = useCallback((deviceId) => {
    const device = devicesRef.current.find((item) => item.id === deviceId);
    if (!device) return lastRenderedPositionsRef.current[deviceId] || animPositionsRef.current[deviceId] || null;
    return getDeviceDisplayPosition(device);
  }, [getDeviceDisplayPosition, animPositionsRef]);

  const getTrailPointLimit = useCallback(() => {
    return Math.min(Math.max(mapFeatureSettings?.trail?.trailLength || 20, 10), 30);
  }, [mapFeatureSettings]);

  const normalizePointList = useCallback((points) => {
    if (!Array.isArray(points)) return [];
    const normalized = [];
    for (const point of points) {
      if (!point || point.lat == null || point.lng == null) continue;
      const prev = normalized[normalized.length - 1];
      if (!prev || prev.lat !== point.lat || prev.lng !== point.lng) {
        const p = { lat: point.lat, lng: point.lng };
        if (point.ts != null) p.ts = point.ts;
        normalized.push(p);
      }
    }
    return normalized;
  }, []);

  const mergeRecentPoints = useCallback((deviceId, points, prependExisting = false) => {
    const maxLen = getTrailPointLimit();
    const existing = recentPositionBufferRef.current[deviceId] || [];
    const merged = prependExisting
      ? normalizePointList([...points, ...existing])
      : normalizePointList([...existing, ...points]);
    recentPositionBufferRef.current[deviceId] = merged.slice(-maxLen);
    return recentPositionBufferRef.current[deviceId];
  }, [getTrailPointLimit, normalizePointList]);

  const applyTrailPoints = useCallback((deviceId, points) => {
    const maxLen = getTrailPointLimit();
    const normalized = normalizePointList(points).slice(-maxLen);
    positionHistoryRef.current[deviceId] = normalized;
    const device = devicesRef.current.find((item) => item.id === deviceId);
    const shouldUseRoadSnap = !isAssetBeaconDevice(device) && (mapFeatureSettings?.trail?.useRoadSnap ?? true) !== false;

    if (normalized.length >= 2 && shouldUseRoadSnap) {
      // Throttle: minimum 2s between OSRM calls per device (self-hosted, no rate limits)
      const lastSnapTime = trailSnapPendingRef.current[`${deviceId}_lastSnapTime`] || 0;
      const now = Date.now();
      if (now - lastSnapTime < 2000 && snappedTrailsRef.current[deviceId]) {
        // Already have a snapped trail and snapped recently — skip snap but still re-render
        setSnapVersion((value) => value + 1);
        return;
      }
      const requestId = (trailSnapRequestRef.current[deviceId] || 0) + 1;
      trailSnapRequestRef.current[deviceId] = requestId;
      trailSnapPendingRef.current[deviceId] = true;
      trailSnapPendingRef.current[`${deviceId}_lastSnapTime`] = now;
      setSnapVersion((value) => value + 1);
      snapToRoad(normalized, api).then((snapped) => {
        if (trailSnapRequestRef.current[deviceId] !== requestId) {
          return;
        }
        trailSnapPendingRef.current[deviceId] = false;
        if (snapped && snapped.length >= 2) {
          // Always use latest snap — it covers the most recent GPS points
          snappedTrailsRef.current[deviceId] = snapped;
          // Upgrade any active straight-line animation to follow the snapped road path
          const activeAnim = animStateRef.current[deviceId];
          if (activeAnim && !activeAnim.waypoints) {
            const updatedSnap = snappedTrailsRef.current[deviceId];
            if (updatedSnap && updatedSnap.length >= 2) {
              // Inline path extraction: find segment from→to in snapped trail
              const fromPt = { lat: activeAnim.fromLat, lng: activeAnim.fromLng };
              const toPt = { lat: activeAnim.toLat, lng: activeAnim.toLng };
              let startIdx = 0, bestStart = Infinity;
              for (let i = 0; i < updatedSnap.length; i++) {
                const s = Math.abs(updatedSnap[i].lat - fromPt.lat) + Math.abs(updatedSnap[i].lng - fromPt.lng);
                if (s < bestStart) { bestStart = s; startIdx = i; }
              }
              let endIdx = -1, bestEnd = Infinity;
              for (let i = startIdx + 1; i < updatedSnap.length; i++) {
                const s = Math.abs(updatedSnap[i].lat - toPt.lat) + Math.abs(updatedSnap[i].lng - toPt.lng);
                if (s < bestEnd) { bestEnd = s; endIdx = i; }
              }
              if (endIdx > startIdx) {
                const path = updatedSnap.slice(startIdx, endIdx + 1);
                if (path.length >= 2) {
                  // Skip upgrade if the animation path self-intersects (loop)
                  if (!pathHasSelfIntersection(path, 0.0003, 8)) {
                    const waypoints = normalizePointList(path);
                    if (waypoints.length >= 2) {
                      const { totalDistance, cumulativeDistances } = computePathMetrics(waypoints);
                      if (totalDistance > 0) {
                        activeAnim.waypoints = waypoints;
                        activeAnim.totalDistance = totalDistance;
                        activeAnim.cumulativeDistances = cumulativeDistances;
                        activeAnim.snapStatus = 'snapped';
                      }
                    }
                  }
                }
              }
            }
          }
        }
        setSnapVersion((value) => value + 1);
      });
    } else {
      delete trailSnapPendingRef.current[deviceId];
      delete snappedTrailsRef.current[deviceId];
      setSnapVersion((value) => value + 1);
    }
  }, [api, getTrailPointLimit, mapFeatureSettings, normalizePointList, positionHistoryRef, animStateRef]);

  const loadRecentHistoryForDevice = useCallback(async (device, { applyToTrail = false, forceRefresh = false } = {}) => {
    if (!device?.id) return [];

    const buffered = recentPositionBufferRef.current[device.id] || [];
    if (applyToTrail && buffered.length >= 2 && !forceRefresh) {
      applyTrailPoints(device.id, buffered);
      return buffered;
    }

    if (recentHistoryLoadingRef.current.has(device.id)) {
      return buffered;
    }

    recentHistoryLoadingRef.current.add(device.id);
    try {
      const maxAgeSecs = Math.max(device.offline_threshold_secs || 600, 300);
      const limit = Math.max(getTrailPointLimit(), 10);
      const res = await api.get(`/gps/recent/${device.id}?limit=${limit}&max_age_secs=${maxAgeSecs}`);
      const points = normalizePointList(res.data?.points || []);
      const merged = mergeRecentPoints(device.id, points, true);
      if (applyToTrail && merged.length >= 2) {
        applyTrailPoints(device.id, merged);
      }
      return merged;
    } catch {
      return buffered;
    } finally {
      recentHistoryLoadingRef.current.delete(device.id);
    }
  }, [api, applyTrailPoints, getTrailPointLimit, mergeRecentPoints, normalizePointList]);

  // Concurrency-limited trail preload pump — max 3 parallel /gps/recent calls so the
  // browser's per-host connection pool isn't saturated on page load (large fleets were
  // stalling small polls like active-by-device for 20-40s behind the request burst).
  const pumpTrailQueue = useCallback(() => {
    while (trailLoadActiveRef.current < 3 && trailLoadQueueRef.current.length > 0) {
      const device = trailLoadQueueRef.current.shift();
      trailLoadActiveRef.current += 1;
      loadRecentHistoryForDevice(device, { applyToTrail: true, forceRefresh: true })
        .finally(() => {
          trailLoadActiveRef.current -= 1;
          pumpTrailQueue();
        });
    }
  }, [loadRecentHistoryForDevice]);

  const resetDeviceTrailState = useCallback((deviceId) => {
    delete positionHistoryRef.current[deviceId];
    delete snappedTrailsRef.current[deviceId];
    delete trailSnapPendingRef.current[deviceId];
    trailPreloadedRef.current.delete(deviceId);
    setSnapVersion((value) => value + 1);
  }, [positionHistoryRef]);

  const handleToggleTrailWithReset = useCallback((deviceId) => {
    const isTurningOn = !trailEnabledDevices.has(deviceId);
    resetDeviceTrailState(deviceId);
    handleToggleDeviceTrail(deviceId);
    if (isTurningOn) {
      const device = devicesRef.current.find((item) => item.id === deviceId);
      if (device) {
        const buffered = recentPositionBufferRef.current[deviceId] || [];
        setTimeout(() => {
          if (buffered.length >= 2) {
            applyTrailPoints(deviceId, buffered);
          } else {
            loadRecentHistoryForDevice(device, { applyToTrail: true });
          }
        }, 0);
      }
    }
  }, [applyTrailPoints, handleToggleDeviceTrail, loadRecentHistoryForDevice, resetDeviceTrailState, trailEnabledDevices]);

  // Load map feature settings on mount
  useEffect(() => {
    api.get('/map-features/settings')
      .then((res) => {
        if (res.data?.settings && Object.keys(res.data.settings).length > 0) {
          setMapFeatureSettings(res.data.settings);
        }
      })
      .catch(() => {});
  }, [api, setMapFeatureSettings]);

  // Track position history for trail — ONLY for explicitly trail-toggled devices (More menu) or "show all"
  const updatePositionHistory = useCallback((deviceId, lat, lng, timestamp) => {
    const point = { lat, lng };
    if (timestamp != null) point.ts = timestamp;
    const buffered = mergeRecentPoints(deviceId, [point]);
    const trailSettings = mapFeatureSettings?.trail;
    if (!trailSettings?.enabled || !mapEnhancementPrivileges.trip_direction_trail) return;
    // Only accumulate for devices with trail explicitly turned ON, or "show all"
    if (!trailEnabledDevices.has(deviceId) && !trailSettings.showAllDevices) return;
    if (buffered.length >= 2) {
      applyTrailPoints(deviceId, buffered);
    }
  }, [applyTrailPoints, mapEnhancementPrivileges.trip_direction_trail, mapFeatureSettings, mergeRecentPoints, trailEnabledDevices]);

  const extractAnimationPath = useCallback((snapped, from, to) => {
    const normalized = normalizePointList(snapped);
    if (normalized.length < 2) return null;

    let startIdx = 0;
    let bestStartScore = Infinity;
    for (let i = 0; i < normalized.length; i++) {
      const score = coordDistanceScore(normalized[i], from);
      if (score < bestStartScore) {
        bestStartScore = score;
        startIdx = i;
      }
    }

    let endIdx = -1;
    let bestEndScore = Infinity;
    for (let i = startIdx + 1; i < normalized.length; i++) {
      const score = coordDistanceScore(normalized[i], to);
      if (score < bestEndScore) {
        bestEndScore = score;
        endIdx = i;
      }
    }

    if (endIdx === -1) return null;
    const segmentPath = normalized.slice(startIdx, endIdx + 1);
    if (segmentPath.length < 2) return null;

    // Safety: if the extracted animation path self-intersects (loops), discard it.
    // The animation will fall back to straight-line interpolation instead of looping the icon.
    if (pathHasSelfIntersection(segmentPath, 0.0003, 8)) {
      return null;
    }

    return segmentPath;
  }, [normalizePointList]);

  const clearQueuedAnimations = useCallback((deviceId) => {
    const queuedSegments = animQueueRef.current[deviceId] || [];
    queuedSegments.forEach((segment) => {
      if (segment.fallbackTimer) clearTimeout(segment.fallbackTimer);
    });
    delete animQueueRef.current[deviceId];
  }, []);

  // Activate a queued animation segment.
  // Uses the trail's snapped path for road-following animation when available.
  const activateAnimationSegment = useCallback((deviceId, segment, anchorOverride = null) => {
    const now = Date.now();
    const anchor = anchorOverride || lastRenderedPositionsRef.current[deviceId] || animPositionsRef.current[deviceId] || {
      lat: segment.from.lat,
      lng: segment.from.lng,
      heading: segment.fromHeading,
    };

    // Dynamic speed: if queue is deep, speed up to catch up (visit every point faster)
    const queueDepth = (animQueueRef.current[deviceId] || []).length;
    const speedMultiplier = queueDepth > 4 ? 4 : queueDepth > 2 ? 2 : 1;
    const baseDuration = Math.max(segment.duration - (now - segment.createdAt), MIN_ANIMATION_DURATION_MS);
    const motionDuration = Math.max(baseDuration / speedMultiplier, 1000); // At least 1 second per segment

    const fromHeading = normalizeHeading(anchor.heading ?? segment.fromHeading ?? 0);
    const state = {
      segmentId: segment.id,
      fromLat: anchor.lat,
      fromLng: anchor.lng,
      fromHeading,
      toLat: segment.to.lat,
      toLng: segment.to.lng,
      toHeading: normalizeHeading(segment.toHeading ?? fromHeading),
      startTime: segment.createdAt,
      duration: segment.duration,
      lastUpdateTime: segment.lastUpdateTime,
      waypoints: null,
      totalDistance: 0,
      cumulativeDistances: null,
      motionStartTime: now,
      motionDuration,
      snapStatus: 'fallback',
      smoothRotation: segment.smoothRotation !== false,
    };

    // Try to use the trail's snapped path so icon follows the same road as the trail
    const trailSnapped = snappedTrailsRef.current[deviceId];
    if (trailSnapped && trailSnapped.length >= 2) {
      const path = extractAnimationPath(trailSnapped, segment.from, segment.to);
      if (path && path.length >= 2) {
        // Replace first point with actual anchor position for smooth start
        const waypointSeed = path.map((point, index) => (
          index === 0 ? { lat: anchor.lat, lng: anchor.lng } : { lat: point.lat, lng: point.lng }
        ));
        const waypoints = normalizePointList(waypointSeed);
        if (waypoints.length >= 2) {
          const { totalDistance, cumulativeDistances } = computePathMetrics(waypoints);
          if (totalDistance > 0) {
            const lastPoint = waypoints[waypoints.length - 1];
            state.waypoints = waypoints;
            state.totalDistance = totalDistance;
            state.cumulativeDistances = cumulativeDistances;
            state.fromLat = waypoints[0].lat;
            state.fromLng = waypoints[0].lng;
            state.toLat = lastPoint.lat;
            state.toLng = lastPoint.lng;
            state.snapStatus = 'snapped';
          }
        }
      }
    }

    animStateRef.current[deviceId] = state;
    const startPosition = {
      lat: roundPositionCoord(state.fromLat),
      lng: roundPositionCoord(state.fromLng),
      heading: fromHeading,
    };
    animPositionsRef.current[deviceId] = startPosition;
    lastRenderedPositionsRef.current[deviceId] = startPosition;
    setAnimTick((value) => value + 1);
  }, [animPositionsRef, animStateRef, extractAnimationPath, normalizePointList]);

  // Start next queued animation — activates immediately, no snap delay
  const startNextQueuedAnimation = useCallback((deviceId) => {
    if (animStateRef.current[deviceId]) return;

    const queue = animQueueRef.current[deviceId];
    if (!queue || queue.length === 0) return;

    const segment = queue[0];
    if (segment.fallbackTimer) {
      clearTimeout(segment.fallbackTimer);
      segment.fallbackTimer = null;
    }

    const anchor = lastRenderedPositionsRef.current[deviceId] || animPositionsRef.current[deviceId] || {
      lat: segment.from.lat,
      lng: segment.from.lng,
      heading: segment.fromHeading,
    };
    activateAnimationSegment(deviceId, segment, anchor);
  }, [activateAnimationSegment, animPositionsRef, animStateRef]);

  // Trail path builder — uses snapped path when it covers the trail well, raw GPS otherwise.
  // Always extends to the latest raw GPS points so the trail never stops short.
  // NOTE: Self-intersection detection is intentionally NOT applied here — OSRM road-snapped
  // curves are always visually better than raw GPS straight lines, even if OSRM occasionally
  // adds a minor loop at U-turns. Loop prevention is applied only to animation paths.
  const buildRenderableTrailPath = useCallback((deviceId, rawPoints, snappedPath) => {
    const raw = rawPoints || [];
    if (snappedPath && snappedPath.length > raw.length && snappedPath.length >= 2) {
      // Snapped trail is better — but extend with any raw points beyond the snapped end
      const lastSnapped = snappedPath[snappedPath.length - 1];
      // Find raw points that come AFTER the last snapped position
      let startAppendIdx = -1;
      for (let i = raw.length - 1; i >= 0; i--) {
        if (Math.abs(raw[i].lat - lastSnapped.lat) < 0.001 && Math.abs(raw[i].lng - lastSnapped.lng) < 0.001) {
          startAppendIdx = i + 1;
          break;
        }
      }
      if (startAppendIdx > 0 && startAppendIdx < raw.length) {
        return [...snappedPath, ...raw.slice(startAppendIdx)];
      }
      return snappedPath;
    }
    return raw;
  }, []);

  const reconcileAnimationStateWithLiveDevices = useCallback((liveDevices) => {
    if (!Array.isArray(liveDevices)) return;

    const now = Date.now();
    let changed = false;

    liveDevices.forEach((device) => {
      const rawPosition = getRawDevicePosition(device);
      if (!rawPosition || !device?.id) return;

      const state = animStateRef.current[device.id];
      const stateAge = state?.lastUpdateTime ? now - state.lastUpdateTime : Infinity;
      const hasQueuedSegments = !!(animQueueRef.current[device.id]?.length);

      // Only reset if animation state is truly stale (no GPS updates for >60s)
      // and there are no queued segments waiting to play.
      // NEVER reset just because of drift — drift is expected during sequential animation.
      const shouldReset = state && !hasQueuedSegments && stateAge > 60000;

      if (!shouldReset) return;

      clearQueuedAnimations(device.id);
      delete animStateRef.current[device.id];
      const synced = {
        lat: roundPositionCoord(rawPosition.lat),
        lng: roundPositionCoord(rawPosition.lng),
        heading: normalizeHeading(rawPosition.heading),
      };
      animPositionsRef.current[device.id] = synced;
      lastRenderedPositionsRef.current[device.id] = synced;
      changed = true;
    });

    if (changed) {
      setAnimTick((value) => value + 1);
    }
  }, [animPositionsRef, animStateRef, clearQueuedAnimations, getRawDevicePosition]);


  // ── Animated Movement System ──
  // Animates all moving devices. Uses setInterval at 200ms (5fps) for performance.
  // Supports road-snapped waypoint interpolation when Roads API is available.
  const animIntervalRef = useRef(null);
  const [animTick, setAnimTick] = useState(0);

  // Called when a new GPS update arrives for a device
  const startDeviceAnimation = useCallback((deviceId, newLat, newLng, newHeading, speed, gpsTimestamp) => {
    const animSettings = mapFeatureSettings?.animation;
    if (!animSettings?.enabled || !mapEnhancementPrivileges.animated_movement) return;
    if (newLat == null || newLng == null) return;

    const queue = animQueueRef.current[deviceId] || [];
    const useRoadSnap = (animSettings?.useRoadSnap ?? true) !== false;

    if ((speed || 0) < 1) {
      clearQueuedAnimations(deviceId);
      delete animStateRef.current[deviceId];
      const settledPosition = {
        lat: roundPositionCoord(newLat),
        lng: roundPositionCoord(newLng),
        heading: normalizeHeading(newHeading ?? lastRenderedPositionsRef.current[deviceId]?.heading ?? 0),
      };
      animPositionsRef.current[deviceId] = settledPosition;
      lastRenderedPositionsRef.current[deviceId] = settledPosition;
      return;
    }

    const now = Date.now();
    const activeSegment = animStateRef.current[deviceId];

    // Use GPS timestamps for accurate interval if available, fall back to wall-clock
    let uploadInterval = 30000;
    const prevGpsTs = queue[queue.length - 1]?.gpsTimestamp || activeSegment?.gpsTimestamp;
    const currGpsTs = gpsTimestamp ? new Date(gpsTimestamp).getTime() : null;
    if (prevGpsTs && currGpsTs) {
      const prevMs = new Date(prevGpsTs).getTime();
      const gpsInterval = currGpsTs - prevMs;
      if (gpsInterval > 2000 && gpsInterval < 120000) uploadInterval = gpsInterval;
    } else {
      const timingSource = queue[queue.length - 1] || activeSegment;
      if (timingSource?.lastUpdateTime) {
        const measured = now - timingSource.lastUpdateTime;
        if (measured > 2000 && measured < 120000) uploadInterval = measured;
      }
    }
    const duration = uploadInterval * 1.0; // Use full interval — icon arrives exactly when next point comes

    const device = devicesRef.current.find((item) => item.id === deviceId);
    const queuedTail = queue[queue.length - 1];
    const currentDisplay = device ? getDeviceDisplayPosition(device) : null;
    const anchor = queuedTail
      ? {
          lat: queuedTail.to.lat,
          lng: queuedTail.to.lng,
          heading: queuedTail.toHeading,
        }
      : activeSegment
      ? {
          lat: activeSegment.toLat,
          lng: activeSegment.toLng,
          heading: activeSegment.toHeading,
        }
      : currentDisplay || animPositionsRef.current[deviceId] || lastRenderedPositionsRef.current[deviceId] || {
          lat: newLat,
          lng: newLng,
          heading: newHeading ?? 0,
        };

    const fromLat = anchor.lat;
    const fromLng = anchor.lng;
    const fromHeading = normalizeHeading(anchor.heading ?? newHeading ?? 0);
    if (Math.abs(fromLat - newLat) <= 1e-6 && Math.abs(fromLng - newLng) <= 1e-6) {
      const syncedPosition = {
        lat: roundPositionCoord(newLat),
        lng: roundPositionCoord(newLng),
        heading: normalizeHeading(newHeading ?? fromHeading),
      };
      animPositionsRef.current[deviceId] = syncedPosition;
      lastRenderedPositionsRef.current[deviceId] = syncedPosition;
      return;
    }

    const segment = {
      id: `${deviceId}-${now}-${animSequenceRef.current++}`,
      createdAt: now,
      duration,
      lastUpdateTime: now,
      gpsTimestamp: gpsTimestamp || null,
      from: { lat: fromLat, lng: fromLng },
      to: { lat: newLat, lng: newLng },
      fromHeading,
      toHeading: normalizeHeading(newHeading ?? fromHeading),
      path: null,
      snapStatus: useRoadSnap ? 'pending' : 'disabled',
      smoothRotation: animSettings?.smoothRotation !== false,
    };

    if (!animQueueRef.current[deviceId]) {
      animQueueRef.current[deviceId] = [];
    }
    animQueueRef.current[deviceId].push(segment);

    // NEVER drop queued segments — every GPS point must be visited in order

    if (!animStateRef.current[deviceId]) {
      startNextQueuedAnimation(deviceId);
    }
  }, [clearQueuedAnimations, getDeviceDisplayPosition, mapEnhancementPrivileges.animated_movement, mapFeatureSettings, startNextQueuedAnimation, animPositionsRef, animStateRef]);

  // Throttled animation loop — 5fps via setInterval (not 60fps requestAnimationFrame)
  useEffect(() => {
    const animSettings = mapFeatureSettings?.animation;
    if (!animSettings?.enabled || !mapEnhancementPrivileges.animated_movement) {
      if (animIntervalRef.current) { clearInterval(animIntervalRef.current); animIntervalRef.current = null; }
      Object.values(animQueueRef.current).forEach((queue) => {
        queue.forEach((segment) => {
          if (segment.fallbackTimer) clearTimeout(segment.fallbackTimer);
        });
      });
      animQueueRef.current = {};
      animPositionsRef.current = {};
      animStateRef.current = {};
      lastRenderedPositionsRef.current = {};
      return;
    }

    animIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const states = animStateRef.current;
      const deviceIds = Object.keys(states);
      if (deviceIds.length === 0) return;

      let anyChanged = false;
      for (const id of deviceIds) {
        const s = states[id];
        const computed = computeAnimatedPositionFromState(s, now);
        if (!computed) continue;
        const { lat, lng, heading, progress } = computed;

        const prev = animPositionsRef.current[id];
        const newLat = Math.round(lat * 1e6) / 1e6;
        const newLng = Math.round(lng * 1e6) / 1e6;
        const normalizedHeading = ((heading % 360) + 360) % 360;
        if (!prev || prev.lat !== newLat || prev.lng !== newLng) {
          animPositionsRef.current[id] = { lat: newLat, lng: newLng, heading: normalizedHeading };
          lastRenderedPositionsRef.current[id] = animPositionsRef.current[id];
          anyChanged = true;
        }
        // Clean up completed animations
        if (progress >= 1) {
          lastRenderedPositionsRef.current[id] = { lat: newLat, lng: newLng, heading: normalizedHeading };
          const queue = animQueueRef.current[id];
          if (queue?.length && queue[0].id === s.segmentId) {
            const finished = queue.shift();
            if (finished?.fallbackTimer) clearTimeout(finished.fallbackTimer);
            if (queue.length === 0) delete animQueueRef.current[id];
          }
          delete states[id];
          startNextQueuedAnimation(id);
        }
      }

      if (anyChanged) setAnimTick(t => t + 1); // trigger re-render at 5fps max
    }, 200); // 200ms = 5fps — smooth enough for map movement, won't block UI

    return () => { if (animIntervalRef.current) { clearInterval(animIntervalRef.current); animIntervalRef.current = null; } };
  }, [computeAnimatedPositionFromState, mapEnhancementPrivileges.animated_movement, mapFeatureSettings, startNextQueuedAnimation, animPositionsRef, animStateRef]);

  // Determine default map type based on available providers
  const getDefaultMapType = () => {
    const saved = localStorage.getItem('preferred_map_type');
    if (saved) {
      if (saved === 'osm' && hasOSMMaps) return 'osm';
      if (saved !== 'osm' && hasGoogleMaps) return saved;
    }
    if (hasGoogleMaps) return 'roadmap';
    return 'osm';
  };

  // Use context mapType, but initialize from localStorage if not set yet
  const mapType = lt.mapType || getDefaultMapType();
  const setMapTypeState = lt.setMapType;

  // Update map type when privileges change
  useEffect(() => {
    if (mapType === 'osm' && !hasOSMMaps && hasGoogleMaps) {
      setMapTypeState('roadmap');
    } else if (mapType !== 'osm' && !hasGoogleMaps && hasOSMMaps) {
      setMapTypeState('osm');
    } else if (mapType !== 'osm' && !hasGoogleMaps && !hasOSMMaps) {
      setMapTypeState('osm');
    }
  }, [hasOSMMaps, hasGoogleMaps, mapType, setMapTypeState]);

  const setMapType = (type) => {
    localStorage.setItem('preferred_map_type', type);
    setMapTypeState(type);
  };

  useEffect(() => {
    followedDevicesRef.current = followedDevices;
  }, [followedDevices]);

  useEffect(() => {
    mapTypeRef.current = mapType;
  }, [mapType]);

  useEffect(() => {
    getDisplayPositionForDeviceIdRef.current = getDisplayPositionForDeviceId;
  }, [getDisplayPositionForDeviceId]);

  useEffect(() => {
    startDeviceAnimationRef.current = startDeviceAnimation;
  }, [startDeviceAnimation]);

  useEffect(() => {
    updatePositionHistoryRef.current = updatePositionHistory;
  }, [updatePositionHistory]);

  useEffect(() => {
    fetchSingleDeviceSpeedLimitRef.current = fetchSingleDeviceSpeedLimit;
  }, [fetchSingleDeviceSpeedLimit]);

  useEffect(() => {
    isIgnitionOnRef.current = isIgnitionOn;
  }, [isIgnitionOn]);

  useEffect(() => {
    speedLimitRefreshModeRef.current = speedLimitRefreshMode.mode;
  }, [speedLimitRefreshMode.mode]);

  // Local-only UI state (OK to reset on navigation)
  const [showLabels, setShowLabels] = useState(() => localStorage.getItem('showDeviceLabels') === 'true');
  const [clusteringEnabled, setClusteringEnabled] = useState(() => {
    const stored = localStorage.getItem('clusteringEnabled');
    return stored !== null ? stored === 'true' : true;
  });
  const [weatherApiKey, setWeatherApiKey] = useState('');
  const [weatherLayers, setWeatherLayers] = useState([]);
  const [showWeatherPicker, setShowWeatherPicker] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Nearest Device Finder
  const [finderActive, setFinderActive] = useState(false);
  const [finderPin, setFinderPin] = useState(null); // {lat, lng}
  const [finderAddress, setFinderAddress] = useState('');
  const [finderResults, setFinderResults] = useState([]);
  const [infoWindowOpen, setInfoWindowOpen] = useState(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [mobileDeviceListOpen, setMobileDeviceListOpen] = useState(false);
  const [mobileTelemetrySection, setMobileTelemetrySection] = useState(null);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  // Per-group visibility (localStorage-persisted). Devices in hidden groups are removed
  // from the map (markers AND clusters) — same semantics as per-device "Hide from Map".
  const [hiddenGroupNames, setHiddenGroupNames] = useState(() => {
    try {
      const raw = localStorage.getItem('live-tracking-hidden-groups');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const toggleGroupVisibility = useCallback((groupName) => {
    setHiddenGroupNames((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      try { localStorage.setItem('live-tracking-hidden-groups', JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  }, []);
  const clearHiddenGroups = useCallback(() => {
    setHiddenGroupNames(new Set());
    try { localStorage.removeItem('live-tracking-hidden-groups'); } catch { /* noop */ }
  }, []);
  const [searchMarker, setSearchMarker] = useState(null); // {lat, lng} for search result marker
  // Edit Device Dialog state
  const [editDevice, setEditDevice] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Share Position Modal state
  const [shareDevice, setShareDevice] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Create Tracking Link Modal state
  const [trackingLinkDevice, setTrackingLinkDevice] = useState(null);
  const [trackingLinkModalOpen, setTrackingLinkModalOpen] = useState(false);
  // Revoke confirmation modal state
  const [revokeDevice, setRevokeDevice] = useState(null);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeResult, setRevokeResult] = useState(null);
  const fetchDevicesRef = useRef(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  // Refs for WS state — lets fetchDevices subscribe without dependency loops
  const wsConnectedRef = useRef(wsConnected);
  const subscribeRef = useRef(subscribeToDevices);
  useEffect(() => { wsConnectedRef.current = wsConnected; }, [wsConnected]);
  useEffect(() => { subscribeRef.current = subscribeToDevices; }, [subscribeToDevices]);

  useEffect(() => {
    if (mapTypeRef.current === 'osm' && leafletMapRef.current) {
      const center = leafletMapRef.current.getCenter();
      const zoom = leafletMapRef.current.getZoom();
      if (center) {
        setLastMapCenter({ lat: center.lat, lng: center.lng });
        setLastMapZoom(zoom);
      }
    } else if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        setLastMapCenter({ lat: center.lat(), lng: center.lng() });
        setLastMapZoom(zoom);
      }
    }

    // Restore the user's explicit map-mode choice (shared across the three map pages);
    // fall back to the master app theme only when they've never toggled a map.
    setDarkModeMap(hasDarkMode ? (getMapThemeOverride(isDark) ?? isDark) : false);
  }, [hasDarkMode, isDark, setDarkModeMap, setLastMapCenter, setLastMapZoom]);

  // Devices visible on map (excluding individually-hidden devices AND devices in hidden groups).
  // Group-hide filter intentionally runs BEFORE clustering so cluster counts stay accurate.
  const visibleDevices = devices.filter((d) => {
    if (hiddenDevices.has(d.id)) return false;
    const groupKey = d.device_group || 'Ungrouped';
    if (hiddenGroupNames.has(groupKey)) return false;
    return true;
  });

  // Toggle device labels and persist preference
  const handleToggleLabels = useCallback(() => {
    setShowLabels(prev => {
      const next = !prev;
      localStorage.setItem('showDeviceLabels', String(next));
      return next;
    });
  }, []);

  // Toggle marker clustering and persist preference
  const handleToggleClustering = useCallback(() => {
    setClusteringEnabled(prev => {
      const next = !prev;
      localStorage.setItem('clusteringEnabled', String(next));
      return next;
    });
  }, []);

  // Build label text for a device: "Name (speed)" in the user's speed unit
  const getDeviceLabel = useCallback((device) => {
    const name = deviceDisplayName(device);
    if (isAssetBeaconDevice(device)) {
      return `${name} (${getBeaconObservedBadgeText(device)})`;
    }
    const speed = device.last_speed != null ? formatSpeed(device.last_speed) : '';
    return speed ? `${name} (${speed})` : name;
  }, []);

  // Get stop timer info for a device — uses device.status_since from backend
  // (same source as the Telemetry Panel status field)
  const getStopTimerInfo = useCallback((device) => {
    const timerSettings = mapFeatureSettings?.stopTimer;
    if (!timerSettings?.enabled || !mapEnhancementPrivileges.live_stop_timer) return null;

    const requireIgn = timerSettings.requireIgnitionOn !== false; // default true

    if (requireIgn) {
      // Engine Idle mode: device must be in 'idle' status (stopped + ignition ON)
      // Also check IO elements as fallback for devices that report 'stopped' but have ignition on
      let isIdle = device.status === 'idle';
      if (!isIdle && (device.status === 'stopped' || (device.status !== 'offline' && (device.last_speed || 0) < 1))) {
        const io = device.last_io_elements;
        if (io) {
          if (io['239'] !== undefined) isIdle = Number(io['239']) > 0;
          else if (io['ignition'] !== undefined) isIdle = Number(io['ignition']) > 0;
          else if (io['din1'] !== undefined) isIdle = Number(io['din1']) > 0;
        }
      }
      if (!isIdle) return null;
    } else {
      // General stop timer: device must be stopped (any ignition state)
      const isStopped = device.status === 'stopped' || device.status === 'idle' ||
        (device.status !== 'offline' && (device.last_speed || 0) < 1);
      if (!isStopped) return null;
    }

    // Use status_since from the backend (same source as Telemetry Panel)
    const statusSince = device.status_since || device.last_seen || device.last_position?.server_timestamp;
    if (!statusSince) return null;

    const elapsedMs = Date.now() - new Date(statusSince).getTime();
    if (elapsedMs < 0) return null;
    const elapsedMin = elapsedMs / 60000;
    const hours = Math.floor(elapsedMs / 3600000);
    const minutes = Math.floor((elapsedMs % 3600000) / 60000);
    const seconds = Math.floor((elapsedMs % 60000) / 1000);
    const timeStr = hours > 0
      ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    let color = '#eab308';
    for (const t of timerSettings.thresholds) {
      if (elapsedMin >= t.minMin && (t.maxMin === null || elapsedMin < t.maxMin)) {
        color = t.color;
        break;
      }
    }
    const label = requireIgn ? 'Engine Idle' : 'Stopped';
    return { timeStr, color, elapsedMin, label };
  }, [mapFeatureSettings, mapEnhancementPrivileges.live_stop_timer]);

  // Force re-render every 10 seconds for stop timer updates
  const [, setTimerTick] = useState(0);
  useEffect(() => {
    const timerSettings = mapFeatureSettings?.stopTimer;
    if (!timerSettings?.enabled || !mapEnhancementPrivileges.live_stop_timer) return;
    const interval = setInterval(() => setTimerTick(t => t + 1), 10000);
    return () => clearInterval(interval);
  }, [mapFeatureSettings, mapEnhancementPrivileges.live_stop_timer]);

  // Current wall-clock time, refreshed every 10s. Keeps time-derived values (the idle
  // durations below) pure at render time instead of calling Date.now() inside a memo.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowTs(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  // Compute device idle durations (in minutes) for card customization Idle Time Trigger
  // Uses device.status_since from backend — same source as Telemetry Panel and Stop Timer
  const deviceIdleDurations = useMemo(() => {
    const map = new Map();
    for (const d of devices) {
      if (d.status === 'idle' && d.status_since) {
        map.set(d.id, (nowTs - new Date(d.status_since).getTime()) / 60000);
      }
    }
    return map;
  }, [devices, nowTs]);

  // Edit Device from More menu
  const handleEditDevice = useCallback((device) => {
    setEditDevice(device);
    setEditDialogOpen(true);
  }, []);

  // Share Position from More menu
  const handleSharePosition = useCallback((device) => {
    setShareDevice(device);
    setShareModalOpen(true);
  }, []);

  // Generate Tracking Link from More menu
  const handleGenerateLink = useCallback((device) => {
    setTrackingLinkDevice(device);
    setTrackingLinkModalOpen(true);
  }, []);

  // Revoke All Links from More menu — opens confirmation modal
  const handleRevokeLinks = useCallback((device) => {
    setRevokeDevice(device);
    setRevokeModalOpen(true);
    setRevokeResult(null);
  }, []);

  const executeRevoke = useCallback(async () => {
    if (!revokeDevice) return;
    setRevoking(true);
    try {
      const res = await api.post('/tracking-links/revoke', { device_id: revokeDevice.id });
      setRevokeResult({ success: true, message: res.data.message });
      fetchDevicesRef.current?.();
    } catch (err) {
      setRevokeResult({ success: false, message: err.response?.data?.detail || 'Failed to revoke links' });
    } finally {
      setRevoking(false);
    }
  }, [api, revokeDevice]);

  // Follow in New Window — transfer auth token for sessionStorage users + map style
  const handleFollowNewWindow = useCallback((deviceId) => {
    const token = sessionStorage.getItem('impersonate_token') || sessionStorage.getItem('quick_login_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token && !localStorage.getItem('token')) {
      localStorage.setItem('follow_window_token', token);
    }
    const params = new URLSearchParams({ id: deviceId, map: mapType });
    if (darkModeMap) params.set('dark', '1');
    if (showPOI) params.set('poi', '1');
    const url = `${window.location.origin}/follow-device?${params.toString()}`;
    window.open(url, '_blank', 'noopener');
  }, [mapType, darkModeMap, showPOI]);

  // Handle ?follow=, ?select=, ?lat=&lng=&zoom=, ?event= query params on mount and URL change
  const pendingPanRef = useRef(null);
  useEffect(() => {
    const searchStr = location.search;
    if (!searchStr) return;

    const params = new URLSearchParams(searchStr);
    const followId = params.get('follow');
    const selectId = params.get('select') || params.get('deviceId') || params.get('device');
    const paramLat = params.get('lat');
    const paramLng = params.get('lng');
    const paramZoom = params.get('zoom');
    const eventId = params.get('event') || params.get('eventId');

    if (followId && devices.length > 0) {
      const device = devices.find(d => d.id === followId);
      if (device) {
        setSelectedDevice(followId);
        setFollowedDevices(new Set([followId]));
        navigate(window.location.pathname, { replace: true });
      }
    } else if (selectId && devices.length > 0) {
      const device = devices.find(d => d.id === selectId);
      if (device) {

        setTelemetryRefreshKey(k => k + 1);

        // Determine target position
        let targetLat, targetLng, targetZoom;
        const hasEventCoords = paramLat != null && paramLng != null;
        if (hasEventCoords) {
          targetLat = parseFloat(paramLat);
          targetLng = parseFloat(paramLng);
          targetZoom = paramZoom ? parseInt(paramZoom) : 17;
        } else {
          const position = getDeviceDisplayPosition(device) || getDisplayPositionForDeviceId(selectId);
          if (position?.lat != null) { targetLat = position.lat; targetLng = position.lng; targetZoom = 18; }
        }

        if (!isNaN(targetLat) && !isNaN(targetLng)) {
          // When we have explicit event coordinates, prevent the map controller
          // from auto-panning to the device's current position (which would override)
          if (hasEventCoords) {
            setEventPanActive(true);
          }

          // Store pending event marker info and render marker on map
          if (eventId) {
            pendingPanRef.current = { eventId, lat: targetLat, lng: targetLng };
            // Fetch occurrence to get severity, then place the marker
            api.get(`/event-occurrences/${eventId}`).then(res => {
              if (res.data) {
                setEventMarker({
                  eventId,
                  lat: targetLat,
                  lng: targetLng,
                  severity: res.data.severity || 'warning',
                  occurrence: res.data,
                });
              }
            }).catch(() => {
              // Still show marker even if fetch fails
              setEventMarker({ eventId, lat: targetLat, lng: targetLng, severity: 'warning' });
            });
          }

          // Set selected device (shows device card + telemetry panel)
          setSelectedDevice(selectId);

          // Retry panning with delays to ensure map is ready
          const tryPan = (attempt) => {
            if (mapRef.current || (mapType === 'osm' && leafletMapRef.current)) {
              panMapToPosition({ lat: targetLat, lng: targetLng }, targetZoom);
              // Clear the event pan override after panning completes
              if (hasEventCoords) {
                setTimeout(() => setEventPanActive(false), 1500);
              }
            } else if (attempt < 15) {
              setTimeout(() => tryPan(attempt + 1), 300);
            } else {
              // All retries exhausted — clear override
              setEventPanActive(false);
            }
          };
          // Start panning attempts after a short delay
          setTimeout(() => tryPan(0), 300);
        } else {
          setSelectedDevice(selectId);
        }

        // Clean URL params via React Router (keeps internal state in sync)
        navigate(window.location.pathname, { replace: true });
      }
    } else if (eventId && paramLat != null && paramLng != null) {
      const targetLat = parseFloat(paramLat);
      const targetLng = parseFloat(paramLng);
      const targetZoom = paramZoom ? parseInt(paramZoom) : 17;

      if (!isNaN(targetLat) && !isNaN(targetLng)) {
        setEventPanActive(true);
        api.get(`/event-occurrences/${eventId}`).then(res => {
          const occurrence = res.data || null;
          setEventMarker({
            eventId,
            lat: targetLat,
            lng: targetLng,
            severity: occurrence?.severity || 'warning',
            occurrence,
          });
          if (occurrence?.device_id) {
            setSelectedDevice(occurrence.device_id);
          }
        }).catch(() => {
          setEventMarker({ eventId, lat: targetLat, lng: targetLng, severity: 'warning' });
        });

        const tryPan = (attempt) => {
          if (mapRef.current || (mapType === 'osm' && leafletMapRef.current)) {
            panMapToPosition({ lat: targetLat, lng: targetLng }, targetZoom);
            setTimeout(() => setEventPanActive(false), 1500);
          } else if (attempt < 15) {
            setTimeout(() => tryPan(attempt + 1), 300);
          } else {
            setEventPanActive(false);
          }
        };

        setTimeout(() => tryPan(0), 300);
        navigate(window.location.pathname, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, devices.length]);

  // Clustering state
  const [mapBounds, setMapBounds] = useState(null); // [west, south, east, north]
  const [mapZoom, setMapZoom] = useState(12);
  const superclusterRef = useRef(null);

  const handleBoundsChange = useCallback((bounds, zoom) => {
    setMapBounds(bounds);
    setMapZoom(zoom);
    // Track center for map-type switching (avoids fly-from-Sydney on Google→OSM)
    const lat = (bounds[1] + bounds[3]) / 2;
    const lng = (bounds[0] + bounds[2]) / 2;
    setLastMapCenter({ lat, lng });
    setLastMapZoom(zoom);
  }, [setLastMapCenter, setLastMapZoom]);

  // Compute clusters from visible devices using Supercluster
  const clusteredData = useMemo(() => {
    const geoDevices = visibleDevices
      .map(device => ({ device, position: getDeviceDisplayPosition(device) }))
      .filter(item => item.position?.lat != null && item.position?.lng != null);

    // When clustering is disabled, return all devices as individual markers
    if (!clusteringEnabled || !mapBounds || geoDevices.length === 0) {
      return geoDevices.map(item => ({ type: 'device', device: item.device }));
    }

    const points = geoDevices.map(({ device, position }) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [position.lng, position.lat] },
      properties: { deviceId: device.id },
    }));

    const sc = new Supercluster({ radius: 60, maxZoom: 16 });
    sc.load(points);
    superclusterRef.current = sc;

    const clusters = sc.getClusters(mapBounds, mapZoom);
    return clusters.map(c => {
      if (c.properties.cluster) {
        // Cluster point
        const [lng, lat] = c.geometry.coordinates;
        return {
          type: 'cluster',
          id: c.id,
          lat,
          lng,
          count: c.properties.point_count,
          clusterId: c.id,
        };
      }
      // Individual device
      const match = geoDevices.find(item => item.device.id === c.properties.deviceId);
      return match ? { type: 'device', device: match.device } : null;
    }).filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- animTick is an intentional recompute trigger: getDeviceDisplayPosition reads animPositionsRef.current (a ref mutated by the 5fps animation loop without changing identity), so animTick (bumped each tick) is what forces cluster positions to refresh during animation.
  }, [visibleDevices, mapBounds, mapZoom, animTick, getDeviceDisplayPosition, clusteringEnabled]);

  // Zoom into cluster on click
  const handleClusterClick = useCallback((clusterId, lat, lng) => {
    const sc = superclusterRef.current;
    if (!sc) return;
    const zoom = Math.min(sc.getClusterExpansionZoom(clusterId), 18);
    if (mapType === 'osm' && leafletMapRef.current) {
      leafletMapRef.current.flyTo([lat, lng], zoom);
    } else if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(zoom);
    }
  }, [mapType]);

  // "Fit All Devices" — zoom to show all visible devices
  const handleFitAllDevices = useCallback(() => {
    const positions = visibleDevices
      .map(d => getDeviceDisplayPosition(d))
      .filter(Boolean);
    if (positions.length === 0) { toast.info('No devices with GPS positions'); return; }
    if (positions.length === 1) {
      const p = positions[0];
      if (mapType === 'osm' && leafletMapRef.current) {
        leafletMapRef.current.flyTo([p.lat, p.lng], 15);
      } else if (mapRef.current) {
        mapRef.current.panTo(p);
        mapRef.current.setZoom(15);
      }
      return;
    }
    if (mapType === 'osm' && leafletMapRef.current) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      leafletMapRef.current.fitBounds(bounds, { padding: [55, 55] });
    } else if (mapRef.current && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      positions.forEach(p => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 62);
    }
  }, [visibleDevices, getDeviceDisplayPosition, mapType]);

  const panMapToPosition = useCallback((position, zoom = null) => {
    if (!position?.lat && !position?.lng && position?.lat !== 0 && position?.lng !== 0) return;

    if (mapType === 'osm' && leafletMapRef.current) {
      if (typeof zoom === 'number') {
        leafletMapRef.current.flyTo([position.lat, position.lng], zoom);
      } else {
        leafletMapRef.current.panTo([position.lat, position.lng]);
      }
      return;
    }

    if (mapRef.current) {
      mapRef.current.panTo({ lat: position.lat, lng: position.lng });
      if (typeof zoom === 'number') {
        mapRef.current.setZoom(zoom);
      }
    }
  }, [mapType]);

  // Wrap context toggleFollow with map panning logic
  const handleToggleFollowWithPan = useCallback((deviceId) => {
    handleToggleFollow(deviceId);
    manualSelectionOverrideRef.current = { deviceId: null, until: 0 };
    // After toggling, fit map to followed devices
    const next = new Set(followedDevices);
    if (next.has(deviceId)) next.delete(deviceId);
    else next.add(deviceId);
    if (next.size > 0) {
      const positions = [];
      for (const fid of next) {
        const fd = devicesRef.current.find(d => d.id === fid);
        const pos = fd ? getDeviceDisplayPosition(fd) : null;
        if (pos) {
          positions.push({ lat: pos.lat, lng: pos.lng });
        }
      }
      if (positions.length === 1) {
        const p = positions[0];
        if (mapType === 'osm' && leafletMapRef.current) {
          leafletMapRef.current.flyTo([p.lat, p.lng], 18);
        } else if (mapRef.current) {
          mapRef.current.panTo(p);
          mapRef.current.setZoom(18);
        }
      } else if (positions.length > 1) {
        if (mapType === 'osm' && leafletMapRef.current) {
          const L = window.L;
          if (L) leafletMapRef.current.fitBounds(L.latLngBounds(positions.map(p => [p.lat, p.lng])), { padding: [40, 40] });
        } else if (mapRef.current && window.google?.maps) {
          const bounds = new window.google.maps.LatLngBounds();
          positions.forEach(p => bounds.extend(p));
          mapRef.current.fitBounds(bounds, 40);
        }
      }
    }
  }, [followedDevices, getDeviceDisplayPosition, handleToggleFollow, mapType]);

  const handleGoogleMapReady = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleTogglePOI = useCallback(() => {
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      const z = mapRef.current.getZoom();
      if (c) {
        setLastMapCenter({ lat: c.lat(), lng: c.lng() });
        setLastMapZoom(z);
      }
    }
    if (showWeather) {
      setShowWeather(false);
      setShowPOI(prev => !prev);
      setTimeout(() => setShowWeather(true), 500);
    } else {
      setShowPOI(prev => !prev);
    }
  }, [showWeather, setLastMapCenter, setLastMapZoom, setShowPOI, setShowWeather]);

  const handleToggleDarkMode = useCallback(() => {
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      const z = mapRef.current.getZoom();
      if (c) {
        setLastMapCenter({ lat: c.lat(), lng: c.lng() });
        setLastMapZoom(z);
      }
    }
    const next = !darkModeMap;
    setMapThemeOverride(next, isDark);
    if (showWeather) {
      setShowWeather(false);
      setDarkModeMap(next);
      setTimeout(() => setShowWeather(true), 500);
    } else {
      setDarkModeMap(next);
    }
  }, [showWeather, darkModeMap, isDark, setDarkModeMap, setLastMapCenter, setLastMapZoom, setShowWeather]);

  useEffect(() => {
    devicesRef.current = devices;
    // Auto-follow on polling refresh: fit map to show all followed devices
    const manualSelectionHold = manualSelectionOverrideRef.current;
    const shouldSuppressFollow = manualSelectionHold.until > Date.now()
      && manualSelectionHold.deviceId
      && !followedDevices.has(manualSelectionHold.deviceId);
    if (followedDevices.size > 0 && !shouldSuppressFollow) {
      const followedPositions = [];
      for (const fid of followedDevices) {
        const fd = devices.find(d => d.id === fid);
        const pos = fd ? getDeviceDisplayPosition(fd) : null;
        if (pos) {
          followedPositions.push({ lat: pos.lat, lng: pos.lng });
        }
      }
      if (followedPositions.length === 1) {
        // Single device: pan to it
        const p = followedPositions[0];
        if (mapType === 'osm' && leafletMapRef.current) {
          leafletMapRef.current.panTo([p.lat, p.lng]);
        } else if (mapRef.current) {
          mapRef.current.panTo(p);
        }
      } else if (followedPositions.length > 1) {
        // Multiple devices: fit bounds to show all
        if (mapType === 'osm' && leafletMapRef.current) {
          const L = window.L;
          if (L) {
            const bounds = L.latLngBounds(followedPositions.map(p => [p.lat, p.lng]));
            leafletMapRef.current.fitBounds(bounds, { padding: [40, 40] });
          }
        } else if (mapRef.current && window.google?.maps) {
          const bounds = new window.google.maps.LatLngBounds();
          followedPositions.forEach(p => bounds.extend(p));
          mapRef.current.fitBounds(bounds, 40);
        }
      }
    }
  }, [devices, followedDevices, getDeviceDisplayPosition, mapType]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await api.get('/gps/live');
      response.data.forEach((device) => {
        if (device.last_latitude != null && device.last_longitude != null) {
          mergeRecentPoints(device.id, [{ lat: device.last_latitude, lng: device.last_longitude }]);
        }
      });
      reconcileAnimationStateWithLiveDevices(response.data);

      // Only update state if data actually changed — prevents unnecessary re-renders.
      setDevices(prev => {
        const newData = response.data;
        if (prev.length === newData.length) {
          let changed = false;
          for (let i = 0; i < newData.length; i++) {
            const o = prev[i];
            const n = newData[i];
            if (o.id !== n.id || o.status !== n.status || o.last_latitude !== n.last_latitude || 
                o.last_longitude !== n.last_longitude || o.last_speed !== n.last_speed || 
                o.is_active !== n.is_active || o.last_seen !== n.last_seen ||
                o.device_icon !== n.device_icon || o.asset_name !== n.asset_name ||
                o.computed_odometer !== n.computed_odometer || o.computed_engine_hours !== n.computed_engine_hours ||
                o.odometer_reading !== n.odometer_reading || o.engine_hours !== n.engine_hours ||
                o.timezone_override !== n.timezone_override || o.timezone_override_enabled !== n.timezone_override_enabled ||
                o.effective_timezone !== n.effective_timezone || o.active_tracking_links !== n.active_tracking_links ||
                o.device_group !== n.device_group || o.device_group_color !== n.device_group_color || o.device_group_order !== n.device_group_order ||
                o.beacon_confidence !== n.beacon_confidence || o.beacon_last_seen_by !== n.beacon_last_seen_by ||
                o.last_gps_fix?.gps_timestamp !== n.last_gps_fix?.gps_timestamp ||
                o.last_gps_fix?.status_flags !== n.last_gps_fix?.status_flags ||
                o.beacon_last_rssi_dbm !== n.beacon_last_rssi_dbm || o.beacon_battery_voltage_mv !== n.beacon_battery_voltage_mv ||
                o.active_driver_session?.id !== n.active_driver_session?.id || o.active_driver_session?.status !== n.active_driver_session?.status ||
                o.active_driver_session?.person_name !== n.active_driver_session?.person_name) {
              changed = true;
              break;
            }
          }
          if (!changed) return prev; // Return same reference — no re-render
        }
        return newData;
      });

      // Subscribe after fetch using refs (avoids dependency on wsConnected/subscribeToDevices)
      if (wsConnectedRef.current) {
        subscribeRef.current(response.data.map(d => d.id));
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [api, mergeRecentPoints, reconcileAnimationStateWithLiveDevices]);

  useEffect(() => {
    // (a) Coalesce device-card state updates: a burst of WS device_update messages
    // previously ran setDevices(prev => prev.map(...)) — an O(N) array rebuild + a React
    // render — ONCE PER MESSAGE, which is what produced the "[Violation] 'message' handler
    // took ~160ms" perf warnings on large fleets. We now buffer the per-device payloads
    // (merged, latest-wins per field) and apply them in a SINGLE setDevices flush per
    // animation frame. Position-history + animation still run per-message (every GPS point
    // matters for the trail/animation queue); only the React state update is batched.
    const updateBuffer = new Map(); // deviceId -> merged data
    let flushRafId = null;

    const flushDeviceUpdates = () => {
      flushRafId = null;
      if (updateBuffer.size === 0) return;
      const pending = new Map(updateBuffer);
      updateBuffer.clear();
      // Reconnection detection: when any device flips offline → online, re-fetch the
      // condition-alert card colours immediately so the red "Disconnected" tint clears
      // without waiting for the 30s backup poll.
      let cameBackOnline = false;
      for (const [devId, data] of pending) {
        if (data.status && data.status !== 'offline'
            && devicesRef.current.find((d) => d.id === devId)?.status === 'offline') {
          cameBackOnline = true;
          break;
        }
      }
      if (cameBackOnline) {
        setTimeout(() => fetchAlertCardColorsRef.current?.(), 1500);
      }
      setDevices(prevDevices => prevDevices.map(device => {
        const data = pending.get(device.id);
        if (!data) return device;
        const updated = { ...device, ...data };
        // Only override specific fields when they exist in the incoming data
        // (prevents offline/status-only broadcasts from wiping last known position)
        if (data.latitude != null) updated.last_latitude = data.latitude;
        if (data.longitude != null) updated.last_longitude = data.longitude;
        if (data.speed != null) updated.last_speed = data.speed;
        if (data.heading != null) updated.last_heading = data.heading;
        if (data.altitude != null) updated.last_altitude = data.altitude;
        if (data.satellites != null) updated.last_satellites = data.satellites;
        if ('gps_fix' in data) updated.last_gps_fix = data.gps_fix;
        if (data.io_elements != null) updated.last_io_elements = data.io_elements;
        if ('active_driver_session' in data) updated.active_driver_session = data.active_driver_session;
        if ('current_driver_session' in data) updated.current_driver_session = data.current_driver_session;
        if (data.server_timestamp != null) updated.last_seen = data.server_timestamp;
        if (data.server_timestamp != null) updated.last_server_timestamp = data.server_timestamp;
        if (data.timestamp != null) updated.last_gps_time = data.timestamp;
        return updated;
      }));
    };

    const unsubscribe = onDeviceUpdate((deviceId, data) => {
      if (!deviceId || !isLiveDeviceStreamPayload(data)) return;
      // Track position history for trail feature + start animation (per-message — every point matters)
      if (hasCoordinatePair(data.latitude, data.longitude)) {
        updatePositionHistoryRef.current?.(deviceId, data.latitude, data.longitude, data.server_timestamp || data.timestamp);
        startDeviceAnimationRef.current?.(deviceId, data.latitude, data.longitude, data.heading, data.speed, data.server_timestamp || data.timestamp);
      }

      // Buffer the device-card state update (merge so a later status-only broadcast can't
      // drop an earlier position field), then flush once per frame.
      const existing = updateBuffer.get(deviceId);
      updateBuffer.set(deviceId, existing ? { ...existing, ...data } : data);
      if (flushRafId == null) flushRafId = requestAnimationFrame(flushDeviceUpdates);

      // WebSocket mode: trigger speed limit re-fetch for this device on position change
      if (speedLimitRefreshModeRef.current === 'websocket' && hasCoordinatePair(data.latitude, data.longitude)) {
        // Check if device has ignition ON before fetching
        const dev = devicesRef.current.find(d => d.id === deviceId);
        if (dev && dev.status !== 'offline' && isIgnitionOnRef.current?.(dev)) {
          const coordKey = `${data.latitude.toFixed(4)},${data.longitude.toFixed(4)}`;
          const cached = lastSpeedLimitCoordsRef.current[deviceId];
          if (cached !== coordKey) {
            // Use WebSocket heading if available, fall back to device's last known heading
            const heading = data.heading ?? dev.last_heading ?? null;
            fetchSingleDeviceSpeedLimitRef.current?.(deviceId, data.latitude, data.longitude, heading);
          }
        }
      }
      // Auto-follow: if this device is being followed, pan the map to it
      const manualSelectionHold = manualSelectionOverrideRef.current;
      const shouldSuppressFollow = manualSelectionHold.until > Date.now()
        && manualSelectionHold.deviceId
        && !followedDevicesRef.current.has(manualSelectionHold.deviceId);
      if (!shouldSuppressFollow && followedDevicesRef.current.has(deviceId) && hasCoordinatePair(data.latitude, data.longitude)) {
        const pos = getDisplayPositionForDeviceIdRef.current?.(deviceId) || { lat: data.latitude, lng: data.longitude };
        if (mapTypeRef.current === 'osm' && leafletMapRef.current) {
          leafletMapRef.current.panTo([pos.lat, pos.lng]);
        } else if (mapRef.current) {
          mapRef.current.panTo({ lat: pos.lat, lng: pos.lng });
        }
      }
    });

    return () => {
      if (flushRafId != null) cancelAnimationFrame(flushRafId);
      unsubscribe?.();
    };
  }, [onDeviceUpdate]);

  // Polling configuration — fetched from admin settings
  const [pollingConfig, setPollingConfig] = useState({ enabled: false, interval_seconds: 60 });
  const { dateFormat: userDateFormat, timeFormat: userTimeFormat } = useGeneralSettings();

  useEffect(() => {
    api.get('/settings/polling')
      .then(res => setPollingConfig(res.data))
      .catch(() => {}); // Fallback: polling stays disabled
  }, [api]);

  useEffect(() => {
    fetchDevicesRef.current = fetchDevices;
  }, [fetchDevices]);

  useEffect(() => {

    fetchDevices(); // Fetch once on mount — stable reference, won't re-trigger
    // Safety net: re-fetch after 3s to catch any hard-refresh timing issues
    // where WS connects but subscription was missed
    const safetyFetch = setTimeout(() => fetchDevices(), 3000);
    return () => clearTimeout(safetyFetch);
  }, [fetchDevices]);

  // Fetch custom icons for icon-mode markers
  useEffect(() => {
    if (api) {
      api.get('/custom-icons').then(res => setCustomIcons(res.data || [])).catch(() => {});
    }
  }, [api]);

  // Polling as a true fallback — only runs when enabled AND WebSocket is disconnected
  useEffect(() => {
    if (!pollingConfig.enabled || wsConnected) return;
    const interval = setInterval(fetchDevices, pollingConfig.interval_seconds * 1000);
    return () => clearInterval(interval);
  }, [fetchDevices, pollingConfig, wsConnected]);

  // Pre-populate trail history from database on page load using the device's own
  // offline threshold window so slower stop uploads still restore reliably.
  // Refresh once per page mount even if context already has old trail points,
  // so returning to Live Tracking fills in any points that arrived while away.
  useEffect(() => {
    const trailSettings = mapFeatureSettings?.trail;
    if (!trailSettings?.enabled || !mapEnhancementPrivileges.trip_direction_trail) return;
    if (devices.length === 0) return;
    const devicesToLoad = devices.filter(d => {
      if (trailPreloadedRef.current.has(d.id)) return false;
      if (!trailSettings.showAllDevices && !trailEnabledDevices.has(d.id)) return false;
      return true;
    });
    if (devicesToLoad.length === 0) return;
    devicesToLoad.forEach(d => {
      trailPreloadedRef.current.add(d.id);
      trailLoadQueueRef.current.push(d);
    });
    pumpTrailQueue();
  }, [devices, pumpTrailQueue, mapEnhancementPrivileges.trip_direction_trail, mapFeatureSettings, trailEnabledDevices]);

  // Subscribe to device updates on WS connect/reconnect AND when devices first load
  const deviceCount = devices.length;
  useEffect(() => {
    if (wsConnected && deviceCount > 0) {
      subscribeToDevices(devices.map(d => d.id));
    }
  }, [wsConnected, deviceCount, subscribeToDevices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for fullscreen changes (including ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Check if we're actually in fullscreen mode
      const isCurrentlyFullscreen = document.fullscreenElement !== null;
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange); // Safari
    document.addEventListener('mozfullscreenchange', handleFullscreenChange); // Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange); // IE11

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);


  const selectedDeviceData = devices.find(d => d.id === selectedDevice);
  const selectedDeviceTelemetryCommands = useMemo(() => {
    return (selectedDeviceData?.custom_commands || []).filter((c) => c.show_telemetry !== false);
  }, [selectedDeviceData?.custom_commands]);
  const showMobileDriverLauncher = !!selectedDeviceData
    && ['live_tracking_driver', 'live_tracking_staff', 'live_tracking_passengers', 'live_tracking_assets']
      .some((k) => isSubpageEnabled(k));
  const showMobileCommandsLauncher = !!selectedDeviceData
    && isSubpageEnabled('live_tracking_commands')
    && selectedDeviceTelemetryCommands.length > 0;
  // Hide the mobile Occupants tab when the vehicle has nobody to show — no current/last
  // driver, staff, passengers or bound assets (respecting subpage privileges) and no
  // manual default driver. Mirrors the TelemetryPanel Occupants card's empty state.
  const isMobileOrWebview = useIsMobileOrWebview();
  const [mobileOccupantsEmpty, setMobileOccupantsEmpty] = useState(false);
  useEffect(() => {
    setMobileOccupantsEmpty(false);
    if (!isMobileOrWebview || !selectedDeviceData?.id) return undefined;
    let alive = true;
    api.get(`/fleet/vehicles/${selectedDeviceData.id}/bindings`)
      .then((res) => {
        if (!alive) return;
        const b = res.data?.bindings || {};
        const ROW_FLAGS = {
          driver: 'live_tracking_driver',
          staff: 'live_tracking_staff',
          passenger: 'live_tracking_passengers',
          trailer: 'live_tracking_assets',
          equipment: 'live_tracking_assets',
        };
        const hasAny = Object.entries(ROW_FLAGS).some(([type, flag]) => {
          if (!isSubpageEnabled(flag)) return false;
          const row = b[type] || {};
          const cur = Array.isArray(row.current) ? row.current : (row.current ? [row.current] : []);
          const last = Array.isArray(row.last) ? row.last : (row.last ? [row.last] : []);
          return cur.length > 0 || last.length > 0;
        }) || (isSubpageEnabled('live_tracking_driver') && !!b.manual_assigned_driver);
        setMobileOccupantsEmpty(!hasAny);
        if (!hasAny) setMobileTelemetrySection((cur) => (cur === 'driver' ? null : cur));
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isMobileOrWebview, selectedDeviceData?.id, telemetryRefreshKey]);
  // Ordered section cards for the mobile island + swipe navigation between them.
  const mobileTelemetrySectionItems = [
    { key: 'overview', label: 'Overview', icon: Gauge },
    ...(showMobileDriverLauncher && !mobileOccupantsEmpty ? [{ key: 'driver', label: 'Occupants', icon: Users }] : []),
    { key: 'data', label: 'Data', icon: Activity },
    ...(showMobileCommandsLauncher ? [{ key: 'commands', label: 'Commands', icon: Send }] : []),
    { key: 'events', label: 'Events', icon: AlertTriangle },
  ];
  const telemetrySwipeRef = useRef(null);
  const [telemetrySlideDir, setTelemetrySlideDir] = useState(null);
  // Keep the last opened section mounted so the sheet collapse animates smoothly.
  const lastMobileSectionRef = useRef(null);
  if (mobileTelemetrySection) lastMobileSectionRef.current = mobileTelemetrySection;
  const mobileSheetSection = mobileTelemetrySection || lastMobileSectionRef.current;
  const sheetDragRef = useRef(null);
  const handleSheetDragStart = (e) => {
    const t = e.touches?.[0];
    if (t) sheetDragRef.current = t.clientY;
  };
  const handleSheetDragEnd = (e) => {
    const start = sheetDragRef.current;
    sheetDragRef.current = null;
    const t = e.changedTouches?.[0];
    if (start == null || !t) return;
    const dy = t.clientY - start;
    if (dy > 60 && mobileTelemetrySection) setMobileTelemetrySection(null);
    // Peek drag — drag up from the collapsed island opens the sheet.
    else if (dy < -60 && !mobileTelemetrySection) setMobileTelemetrySection(lastMobileSectionRef.current || 'overview');
  };
  const goToTelemetrySection = (key) => {
    // Blur the previously tapped tab — iOS keeps sticky focus/hover styling
    // on the touched button, which made the old tab look still-selected.
    if (typeof document !== 'undefined' && document.activeElement?.blur) document.activeElement.blur();
    const keys = mobileTelemetrySectionItems.map((i) => i.key);
    const from = keys.indexOf(mobileTelemetrySection);
    const to = keys.indexOf(key);
    // Incoming card slides in from the side you swiped toward; plain fade when opening fresh.
    setTelemetrySlideDir(from === -1 || to === from ? null : (to > from ? 'left' : 'right'));
    setMobileTelemetrySection(key);
  };
  const handleTelemetrySwipeStart = (e) => {
    const t = e.touches?.[0];
    if (t) telemetrySwipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTelemetrySwipeEnd = (e) => {
    const start = telemetrySwipeRef.current;
    telemetrySwipeRef.current = null;
    const t = e.changedTouches?.[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return; // ignore vertical scrolls / small drags
    const keys = mobileTelemetrySectionItems.map((i) => i.key);
    const idx = keys.indexOf(mobileTelemetrySection);
    if (idx === -1) return;
    const next = dx < 0 ? idx + 1 : idx - 1; // swipe left = next card
    if (next >= 0 && next < keys.length) goToTelemetrySection(keys[next]);
  };

  // Dynamically load deck.gl HeatmapLayer (lazy-loaded to keep bundle small)
  const [HeatmapLayerClass, setHeatmapLayerClass] = useState(null);
  useEffect(() => {
    if (showHeatmap && !HeatmapLayerClass) {
      import('@deck.gl/aggregation-layers').then(mod => {
        setHeatmapLayerClass(() => mod.HeatmapLayer);
      });
    }
  }, [showHeatmap, HeatmapLayerClass]);

  // Compute deck.gl heatmap layers from visible device positions
  const heatmapLayers = useMemo(() => {
    if (!showHeatmap || !HeatmapLayerClass) return [];
    const positions = visibleDevices
      .filter(d => d.last_latitude && d.last_longitude)
      .map(d => ({ COORDINATES: [d.last_longitude, d.last_latitude], WEIGHT: 1 }));
    if (positions.length === 0) return [];

    return [
      new HeatmapLayerClass({
        id: 'device-heatmap',
        data: positions,
        getPosition: d => d.COORDINATES,
        getWeight: d => d.WEIGHT,
        aggregation: 'SUM',
        radiusPixels: 60,
        intensity: 2,
        threshold: 0.01,
        opacity: 0.7,
        weightsTextureSize: 512,
        debounceTimeout: 200,
      })
    ];
  }, [visibleDevices, showHeatmap, HeatmapLayerClass]);

  // Fetch weather overlay config when feature is enabled
  useEffect(() => {
    if (!hasWeather) return;
    api.get('/weather/config').then(res => {
      setWeatherApiKey(res.data.api_key || '');
      setWeatherLayers(res.data.layers || []);
    }).catch(() => {});
  }, [hasWeather, api]);

  useEffect(() => {
    if (!hasGeofenceMapOverlay && showGeofenceOverlay) {
      setShowGeofenceOverlay(false);
    }
  }, [hasGeofenceMapOverlay, setShowGeofenceOverlay, showGeofenceOverlay]);

  useEffect(() => {
    if (!hasGeofenceMapOverlay || !showGeofenceOverlay) return;
    let cancelled = false;

    setGeofenceOverlayLoading(true);
    api.get('/geofences/map-overlay')
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setGeofenceOverlayData({
          zones: data.zones || [],
          places: data.places || [],
          routes: data.routes || [],
        });
      })
      .catch((error) => {
        console.error('Failed to load geofence overlays:', error);
        if (!cancelled) toast.error('Failed to load map overlays');
      })
      .finally(() => {
        if (!cancelled) setGeofenceOverlayLoading(false);
      });
    return () => { cancelled = true; };
  }, [api, hasGeofenceMapOverlay, showGeofenceOverlay]);

  const geofenceOverlayCounts = useMemo(() => ({
    zones: geofenceOverlayData.zones.length,
    places: geofenceOverlayData.places.length,
    routes: geofenceOverlayData.routes.length,
    total: (geofenceOverlayTypes?.zones ? geofenceOverlayData.zones.length : 0)
      + (geofenceOverlayTypes?.places ? geofenceOverlayData.places.length : 0)
      + (geofenceOverlayTypes?.routes ? geofenceOverlayData.routes.length : 0),
    allTotal: geofenceOverlayData.zones.length + geofenceOverlayData.places.length + geofenceOverlayData.routes.length,
  }), [geofenceOverlayData, geofenceOverlayTypes]);

  const geofenceOverlayTypeOptions = useMemo(() => ([
    { key: 'zones', label: 'Zones', count: geofenceOverlayCounts.zones, color: GEOFENCE_OVERLAY_COLORS.zone },
    { key: 'places', label: 'Places', count: geofenceOverlayCounts.places, color: GEOFENCE_OVERLAY_COLORS.place },
    { key: 'routes', label: 'Routes', count: geofenceOverlayCounts.routes, color: GEOFENCE_OVERLAY_COLORS.route },
  ]), [geofenceOverlayCounts]);

  const handleGeofenceOverlayButtonClick = useCallback(() => {
    if (isTouchMapControlViewport()) {
      setShowGeofenceOverlay(true);
      setShowGeofenceOverlayPicker(prev => !prev);
      return;
    }
    setShowGeofenceOverlay(prev => !prev);
  }, [setShowGeofenceOverlay]);

  const toggleGeofenceOverlayType = useCallback((typeKey) => {
    setShowGeofenceOverlay(true);
    setGeofenceOverlayTypes(prev => ({
      zones: true,
      places: true,
      routes: true,
      ...(prev || {}),
      [typeKey]: !(prev?.[typeKey] ?? true),
    }));
  }, [setGeofenceOverlayTypes, setShowGeofenceOverlay]);

  const overlayRouteCorridors = useMemo(() => {
    const m = {};
    (geofenceOverlayData.routes || []).forEach((route) => {
      const path = getRouteOverlayPath(route);
      const polys = path.length >= 2 ? getRouteCorridor(path.map((p) => [p.lat, p.lng]), route.deviation_distance) : null;
      m[route.id] = polys ? {
        leaflet: polys,
        google: polys.map((rings) => rings.map((ring) => ring.map(([lat, lng]) => ({ lat, lng })))),
      } : null;
    });
    return m;
  }, [geofenceOverlayData.routes]);

  const renderLeafletGeofenceOverlays = useCallback(() => {
    if (!hasGeofenceMapOverlay || !showGeofenceOverlay) return null;
    return (
      <React.Fragment>
        {geofenceOverlayTypes?.zones && geofenceOverlayData.zones.filter(zone => zone.visible_on_map !== false).map((zone) => {
          const geometry = getZoneOverlayGeometry(zone);
          if (!geometry) return null;
          const color = zone.zone_color || zone.color || GEOFENCE_OVERLAY_COLORS.zone;
          const label = zone.name || zone.zone_name || 'Zone';
          if (geometry.type === 'circle') {
            return (
              <React.Fragment key={`geo-zone-${zone.id}`}>
                <LeafletCircle center={[geometry.center.lat, geometry.center.lng]} radius={geometry.radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.12, opacity: 0.85, weight: 2 }} />
                <LeafletMarker position={[geometry.center.lat, geometry.center.lng]} icon={createLeafletTextLabelIcon(label, color)} interactive={false} />
              </React.Fragment>
            );
          }
          const center = getPathCenter(geometry.path);
          return (
            <React.Fragment key={`geo-zone-${zone.id}`}>
              <LeafletPolygon positions={geometry.path.map(point => [point.lat, point.lng])} pathOptions={{ color, fillColor: color, fillOpacity: 0.12, opacity: 0.85, weight: 2 }} />
              {center && <LeafletMarker position={[center.lat, center.lng]} icon={createLeafletTextLabelIcon(label, color)} interactive={false} />}
            </React.Fragment>
          );
        })}
        {geofenceOverlayTypes?.places && geofenceOverlayData.places.filter(place => place.visible_on_map !== false).map((place) => {
          const geometry = getPlaceOverlayGeometry(place);
          if (!geometry) return null;
          const color = place.place_color || place.color || getPlaceColor(place.icon);
          const label = place.name || place.place_name || 'Place';
          return (
            <React.Fragment key={`geo-place-${place.id}`}>
              <LeafletCircle center={[geometry.center.lat, geometry.center.lng]} radius={geometry.radius} pathOptions={{ color, fillColor: color, fillOpacity: 0.14, opacity: 0.85, weight: 2, dashArray: '4 4' }} />
              {place.icon ? (
                <LeafletMarker position={[geometry.center.lat, geometry.center.lng]} icon={createPlaceIcon(place.icon)} interactive={false} />
              ) : null}
              <LeafletMarker position={[geometry.center.lat, geometry.center.lng]} icon={createLeafletTextLabelIcon(label, color, place.icon ? 20 : null)} interactive={false} />
            </React.Fragment>
          );
        })}
        {geofenceOverlayTypes?.routes && geofenceOverlayData.routes.filter(route => route.visible_on_map !== false).map((route) => {
          const path = getRouteOverlayPath(route);
          if (path.length < 2) return null;
          const color = route.route_color || route.color || GEOFENCE_OVERLAY_COLORS.route;
          const center = getPathCenter(path);
          const label = route.name || route.route_name || 'Route';
          return (
            <React.Fragment key={`geo-route-${route.id}`}>
              {(overlayRouteCorridors[route.id]?.leaflet || []).map((rings, i) => (
                <LeafletPolygon key={`corridor-${i}`} positions={rings} pathOptions={{ color, fillColor: color, fillOpacity: 0.08, weight: 1.5, dashArray: '6,4' }} interactive={false} />
              ))}
              <LeafletPolyline positions={path.map(point => [point.lat, point.lng])} pathOptions={{ color, opacity: 0.9, weight: 4 }} />
              {center && <LeafletMarker position={[center.lat, center.lng]} icon={createLeafletTextLabelIcon(label, color)} interactive={false} />}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  }, [geofenceOverlayData, geofenceOverlayTypes, hasGeofenceMapOverlay, showGeofenceOverlay, overlayRouteCorridors]);

  const renderGoogleGeofenceOverlays = useCallback(() => {
    if (!hasGeofenceMapOverlay || !showGeofenceOverlay) return null;
    return (
      <React.Fragment>
        {geofenceOverlayTypes?.zones && geofenceOverlayData.zones.filter(zone => zone.visible_on_map !== false).map((zone) => {
          const geometry = getZoneOverlayGeometry(zone);
          if (!geometry) return null;
          const color = zone.zone_color || zone.color || GEOFENCE_OVERLAY_COLORS.zone;
          const label = zone.name || zone.zone_name || 'Zone';
          const center = geometry.type === 'circle' ? geometry.center : getPathCenter(geometry.path);
          return (
            <React.Fragment key={`g-geo-zone-${zone.id}`}>
              {geometry.type === 'circle' ? (
                <GoogleCircle center={geometry.center} radius={geometry.radius} options={{ strokeColor: color, strokeOpacity: 0.85, strokeWeight: 2, fillColor: color, fillOpacity: 0.12 }} />
              ) : (
                <GooglePolygon paths={geometry.path} options={{ strokeColor: color, strokeOpacity: 0.85, strokeWeight: 2, fillColor: color, fillOpacity: 0.12 }} />
              )}
              {center && <AdvancedMarker position={center}><div className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow-md border border-white/70" style={{ backgroundColor: color }} data-testid="geofence-overlay-label">{label}</div></AdvancedMarker>}
            </React.Fragment>
          );
        })}
        {geofenceOverlayTypes?.places && geofenceOverlayData.places.filter(place => place.visible_on_map !== false).map((place) => {
          const geometry = getPlaceOverlayGeometry(place);
          if (!geometry) return null;
          const color = place.place_color || place.color || getPlaceColor(place.icon);
          const label = place.name || place.place_name || 'Place';
          return (
            <React.Fragment key={`g-geo-place-${place.id}`}>
              <GoogleCircle center={geometry.center} radius={geometry.radius} options={{ strokeColor: color, strokeOpacity: 0.85, strokeWeight: 2, fillColor: color, fillOpacity: 0.14 }} />
              {place.icon ? (
                <AdvancedMarker position={geometry.center} anchorPoint={AdvancedMarkerAnchorPoint.CENTER}>
                  <PlaceMarkerContent icon={place.icon} />
                </AdvancedMarker>
              ) : null}
              <AdvancedMarker position={geometry.center}><div className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow-md border border-white/70" style={{ backgroundColor: color, transform: place.icon ? 'translateY(-24px)' : undefined }} data-testid="geofence-overlay-label">{label}</div></AdvancedMarker>
            </React.Fragment>
          );
        })}
        {geofenceOverlayTypes?.routes && geofenceOverlayData.routes.filter(route => route.visible_on_map !== false).map((route) => {
          const path = getRouteOverlayPath(route);
          if (path.length < 2) return null;
          const color = route.route_color || route.color || GEOFENCE_OVERLAY_COLORS.route;
          const center = getPathCenter(path);
          const label = route.name || route.route_name || 'Route';
          return (
            <React.Fragment key={`g-geo-route-${route.id}`}>
              {(overlayRouteCorridors[route.id]?.google || []).map((paths, i) => (
                <GooglePolygon key={`corridor-${i}`} paths={paths} options={{ fillColor: color, fillOpacity: 0.08, strokeColor: color, strokeOpacity: 0.7, strokeWeight: 1.5, clickable: false, zIndex: 4 }} />
              ))}
              <GooglePolyline path={path} options={{ strokeColor: color, strokeOpacity: 0.9, strokeWeight: 4 }} />
              {center && <AdvancedMarker position={center}><div className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow-md border border-white/70" style={{ backgroundColor: color }} data-testid="geofence-overlay-label">{label}</div></AdvancedMarker>}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  }, [geofenceOverlayData, geofenceOverlayTypes, hasGeofenceMapOverlay, showGeofenceOverlay, overlayRouteCorridors]);

  const handleWeatherButtonClick = useCallback(() => {
    if (isTouchMapControlViewport()) {
      setShowWeather(true);
      setShowWeatherPicker(prev => !prev);
      return;
    }
    setShowWeather(prev => !prev);
  }, [setShowWeather]);

  // Close weather layer picker on click outside
  useEffect(() => {
    if (!showWeatherPicker) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="weather-layer-picker"]') && !e.target.closest('[data-testid="toggle-weather-btn"]')) {
        setShowWeatherPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showWeatherPicker]);

  useEffect(() => {
    if (!showGeofenceOverlayPicker) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="geofence-overlay-picker"]') && !e.target.closest('[data-testid="toggle-geofence-overlay-btn"]')) {
        setShowGeofenceOverlayPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showGeofenceOverlayPicker]);

  // deck.gl weather tile layer — renders on separate canvas, immune to Google Maps repaints
  const [WeatherTileLayerClass, setWeatherTileLayerClass] = useState(null);
  const [BitmapLayerClass, setBitmapLayerClass] = useState(null);
  useEffect(() => {
    if (showWeather && hasWeather && weatherApiKey && !WeatherTileLayerClass) {
      Promise.all([
        import('@deck.gl/geo-layers'),
        import('@deck.gl/layers'),
      ]).then(([geoMod, layersMod]) => {
        setWeatherTileLayerClass(() => geoMod.TileLayer);
        setBitmapLayerClass(() => layersMod.BitmapLayer);
      });
    }
  }, [showWeather, hasWeather, weatherApiKey, WeatherTileLayerClass]);

  const weatherDeckLayers = useMemo(() => {
    if (!showWeather || !hasWeather || !weatherApiKey || !WeatherTileLayerClass || !BitmapLayerClass || mapType === 'osm') return [];
    const cacheBucket = Math.floor(nowTs / 600000);
    return [
      new WeatherTileLayerClass({
        id: 'weather-tiles',
        data: `https://tile.openweathermap.org/map/${weatherLayer}/{z}/{x}/{y}.png?appid=${weatherApiKey}&_t=${cacheBucket}`,
        minZoom: 0,
        maxZoom: 18,
        tileSize: 256,
        opacity: 0.85,
        refinementStrategy: 'no-overlap',
        maxRequests: 6,
        loadOptions: { fetch: { mode: 'cors' } },
        onTileError: () => {},
        renderSubLayers: (props) => {
          if (!props.data) return null;
          const { tile: { boundingBox } } = props;
          return new BitmapLayerClass(props, {
            data: null,
            image: props.data,
            bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
          });
        },
      })
    ];
  }, [showWeather, hasWeather, weatherApiKey, weatherLayer, WeatherTileLayerClass, BitmapLayerClass, mapType, nowTs]);

  // Combined deck.gl layers — memoized to prevent new array reference on every render
  const allDeckLayers = useMemo(() => [...heatmapLayers, ...weatherDeckLayers], [heatmapLayers, weatherDeckLayers]);

  // ============ NEAREST DEVICE FINDER ============
  // Haversine distance in km
  const calcDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, []);

  const computeFinderResults = useCallback((lat, lng) => {
    const results = devices
      .filter(d => d.last_latitude && d.last_longitude)
      .map(d => ({
        id: d.id,
        name: deviceDisplayName(d),
        registration: d.registration,
        status: d.status,
        distance: calcDistance(lat, lng, d.last_latitude, d.last_longitude),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10);
    setFinderResults(results);
  }, [devices, calcDistance]);

  const handleFinderMapClick = useCallback((lat, lng) => {
    if (!finderActive) return;
    setFinderPin({ lat, lng });
    computeFinderResults(lat, lng);
  }, [finderActive, computeFinderResults]);

  const handleFinderAddressSearch = async (e) => {
    e.preventDefault();
    if (!finderAddress.trim()) return;
    try {
      if (mapType === 'osm') {
        if (!NOMINATIM_BASE) {
          toast.error('Nominatim URL is not configured');
          return;
        }
        const response = await fetch(`${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(finderAddress)}&limit=1`);
        const data = await response.json();
        if (data?.length > 0) {
          const lat = parseFloat(data[0].lat), lng = parseFloat(data[0].lon);
          setFinderPin({ lat, lng });
          computeFinderResults(lat, lng);
          if (leafletMapRef.current) leafletMapRef.current.flyTo([lat, lng], 12);
        } else { toast.error('Location not found'); }
      } else {
        if (!window.google?.maps?.Geocoder) return;
        new window.google.maps.Geocoder().geocode({ address: finderAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const lat = results[0].geometry.location.lat(), lng = results[0].geometry.location.lng();
            setFinderPin({ lat, lng });
            computeFinderResults(lat, lng);
            if (mapRef.current) { mapRef.current.panTo({ lat, lng }); mapRef.current.setZoom(12); }
          } else { toast.error('Location not found'); }
        });
      }
    } catch { toast.error('Search failed'); }
  };

  // Update finder results when devices change (positions update)
  useEffect(() => {

    if (finderPin) computeFinderResults(finderPin.lat, finderPin.lng);
  }, [devices, finderPin, computeFinderResults]);

  const handleToggleFullscreen = () => {
    const mapContainer = document.querySelector('[data-testid="map-container"]');
    if (!mapContainer) return;
    const isCurrentlyFullscreen = document.fullscreenElement !== null;
    if (!isCurrentlyFullscreen) {
      if (mapContainer.requestFullscreen) mapContainer.requestFullscreen();
    } else {
      if (document.exitFullscreen && document.fullscreenElement) document.exitFullscreen();
    }
  };

  const handleDeviceSelect = useCallback((deviceId) => {
    if (transitionFlashCardColors[deviceId]?.persistentUntilClick) {
      clearTransitionCardColor(deviceId);
    }
    setSelectedDevice(deviceId);
    setMobileTelemetrySection(null);
    setInfoWindowOpen(null); // Close any open InfoWindow when selecting from list
    setTelemetryRefreshKey(k => k + 1); // Trigger address refresh even for same device
    manualSelectionOverrideRef.current = {
      deviceId,
      until: Date.now() + MANUAL_SELECTION_HOLD_MS,
    };

    const device = devicesRef.current.find((item) => item.id === deviceId);
    const position = device ? getDeviceDisplayPosition(device) : getDisplayPositionForDeviceId(deviceId);
    if (position?.lat != null && position?.lng != null) {
      panMapToPosition(position, 18);
    }
  }, [transitionFlashCardColors, clearTransitionCardColor, getDeviceDisplayPosition, getDisplayPositionForDeviceId, panMapToPosition, setSelectedDevice]);

  const handleMobileDeviceSelect = useCallback((deviceId) => {
    document.activeElement?.blur?.();
    handleDeviceSelect(deviceId);
    setMobileDeviceListOpen(false);
    window.setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }), 80);
  }, [handleDeviceSelect]);

  const closeSelectedMobileDevice = useCallback(() => {
    setSelectedDevice(null);
    setMobileTelemetrySection(null);
  }, [setSelectedDevice]);

  const handleZoomIn = () => {
    if (mapType === 'osm' && leafletMapRef.current) {
      leafletMapRef.current.zoomIn();
    } else if (mapRef.current) {
      mapRef.current.setZoom(mapRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapType === 'osm' && leafletMapRef.current) {
      leafletMapRef.current.zoomOut();
    } else if (mapRef.current) {
      mapRef.current.setZoom(mapRef.current.getZoom() - 1);
    }
  };

  const handleAddressSearch = async (e) => {
    e.preventDefault();
    if (!addressSearch.trim()) return;

    try {
      if (mapType === 'osm') {
        if (!NOMINATIM_BASE) {
          toast.error('Nominatim URL is not configured');
          return;
        }
        // Use Nominatim for OSM
        const response = await fetch(`${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(addressSearch)}&limit=1`);
        const data = await response.json();

        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          const latNum = parseFloat(lat);
          const lngNum = parseFloat(lon);

          if (leafletMapRef.current) {
            leafletMapRef.current.flyTo([latNum, lngNum], 16);
          }
          setSearchMarker({ lat: latNum, lng: lngNum });
          toast.success('Location found');
        } else {
          toast.error('Location not found');
        }
      } else {
        // Use Google Places Geocoder for Google Maps
        if (!window.google?.maps?.Geocoder) {
          toast.error('Google Maps not ready');
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: addressSearch }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;
            const lat = location.lat();
            const lng = location.lng();

            setSearchMarker({ lat, lng });

            if (mapRef.current) {
              mapRef.current.panTo({ lat, lng });
              mapRef.current.setZoom(16);
              toast.success('Location found');
            } else {
              toast.error('Map not ready');
            }
          } else {
            toast.error('Location not found');
          }
        });
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Search failed');
    }
  };

  const renderDeviceListPanel = (isMobile = false) => (
    <DeviceListPanel
      devices={devices}
      selectedDevice={selectedDevice}
      onDeviceSelect={isMobile ? handleMobileDeviceSelect : handleDeviceSelect}
      followedDevices={followedDevices}
      hiddenDevices={hiddenDevices}
      onToggleFollow={handleToggleFollowWithPan}
      onToggleHide={handleToggleHide}
      onFollowAll={() => {
        const allIds = new Set(devices.map(d => d.id));
        setFollowedDevices(allIds);
      }}
      onUnfollowAll={() => setFollowedDevices(new Set())}
      onHideAll={() => {
        const allIds = new Set(devices.map(d => d.id));
        setHiddenDevices(allIds);
      }}
      onShowAll={() => setHiddenDevices(new Set())}
      onEditDevice={handleEditDevice}
      onFollowNewWindow={handleFollowNewWindow}
      onSharePosition={handleSharePosition}
      onGenerateLink={handleGenerateLink}
      onRevokeLinks={handleRevokeLinks}
      userTimezone={userTimezone}
      sharePositionEnabled={sharePositionEnabled}
      generateLinkEnabled={generateLinkEnabled}
      cardCustomSettings={mapFeatureSettings?.cardCustom}
      cardCustomAllowed={mapEnhancementPrivileges.device_card_customization}
      trailEnabled={mapFeatureSettings?.trail?.enabled && mapEnhancementPrivileges.trip_direction_trail}
      trailEnabledDevices={trailEnabledDevices}
      onToggleDeviceTrail={handleToggleTrailWithReset}
      onTrailAll={() => {
        devices.forEach(d => { if (!trailEnabledDevices.has(d.id)) handleToggleDeviceTrail(d.id); });
      }}
      onUntrailAll={() => {
        devices.forEach(d => { if (trailEnabledDevices.has(d.id)) handleToggleDeviceTrail(d.id); });
      }}
      deviceIdleDurations={deviceIdleDurations}
      speedingDevices={speedingDevices}
      eventCardColors={eventCardColors}
      onManageGroups={() => setManageGroupsOpen(true)}
      hiddenGroupNames={hiddenGroupNames}
      onToggleGroupVisibility={toggleGroupVisibility}
      className={isMobile ? 'w-full h-full rounded-none border-0' : ''}
      testId={isMobile ? 'mobile-device-list-panel' : 'device-list-panel'}
      testIdPrefix={isMobile ? 'mobile-' : ''}
      isMobile={isMobile}
      liveVideoEnabled={liveVideoAccessEnabled}
      onOpenLiveVideo={(device) => setLiveVideoDevice(device)}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-0 lg:space-y-4" data-testid="live-tracking-page">
      {/* Page Header */}
      <div className="hidden lg:flex flex-wrap items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <Navigation className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">Live Tracking</h1>
          <p className="text-sm text-muted-foreground">Real-time device monitoring and location tracking</p>
        </div>
        {hiddenGroupNames.size > 0 && (
          <button
            type="button"
            onClick={clearHiddenGroups}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-400/60 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-semibold transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/40"
            data-testid="live-tracking-hidden-groups-chip"
            title="Show all hidden groups on map"
          >
            <EyeOff className="w-3 h-3" />
            <span>{hiddenGroupNames.size} group{hiddenGroupNames.size === 1 ? '' : 's'} hidden · Show all</span>
          </button>
        )}
      </div>

      {/* Main Content - Desktop keeps three columns; mobile uses map-first sheets */}
      <div className="relative flex gap-3 h-[calc(100dvh-4rem)] min-h-[calc(100dvh-4rem)] lg:h-[calc(100vh-14rem)] lg:min-h-0 overflow-hidden">
        {/* Left Panel - Device List */}
        <div className="hidden lg:flex" data-testid="desktop-device-list-wrapper">
          {renderDeviceListPanel(false)}
        </div>

        {/* Center Panel - Map */}
        <div className="flex-1 min-w-0 relative rounded-lg overflow-hidden border border-border" data-testid="map-container">
          {/* Mobile primary actions — desktop layout remains unchanged. Hide when a device is selected so the selected-device tools own the bottom space. */}
          {!selectedDeviceData && (
            <div className="lg:hidden absolute bottom-[calc(env(safe-area-inset-bottom)+2rem)] left-0 right-0 z-[1002] flex items-center justify-center pointer-events-none" data-testid="mobile-live-map-actions">
              <button
                type="button"
                onClick={() => setMobileDeviceListOpen(true)}
                className="pointer-events-auto flex min-h-[56px] items-center gap-3.5 rounded-full bg-white/95 dark:bg-slate-900/95 px-6 py-3.5 text-lg font-bold text-slate-800 dark:text-slate-100 shadow-2xl border border-slate-200 dark:border-slate-700 backdrop-blur-sm active:scale-[0.98] transition-transform"
                data-testid="mobile-open-device-list-button"
                aria-label="Open device list"
              >
                <Navigation className="w-7 h-7 text-[#2563eb]" />
                <span>Devices</span>
                <span className="rounded-full bg-[#2563eb] px-3 py-1.5 text-base leading-none text-white" data-testid="mobile-device-count">{devices.length}</span>
              </button>
            </div>
          )}

          {selectedDeviceData && (
            <div
              className="lg:hidden absolute bottom-0 left-0 right-0 z-[1001] pointer-events-none px-3"
              data-testid="mobile-telemetry-section-launcher"
            >
              <div
                className="pointer-events-auto mx-auto mb-[calc(env(safe-area-inset-bottom,0px)+1.15rem)] w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200 flex flex-col"
                data-testid="mobile-selected-device-floating-card"
              >
                {/* Drag handle + header — drag down collapses the expanded sheet */}
                <div onTouchStart={handleSheetDragStart} onTouchEnd={handleSheetDragEnd} data-testid="mobile-telemetry-sheet-drag-handle">
                {/* Drag handle */}
                <div className="flex justify-center pt-1.5 pb-0.5" aria-hidden="true">
                  <span className="h-1 w-10 rounded-full bg-muted-foreground/40" aria-hidden="true" />
                </div>
                {/* Header */}
                <div className="relative flex items-center justify-center px-11 pb-2 border-b border-border text-center">
                  <button
                    type="button"
                    className="min-w-0 max-w-full rounded-md px-1 hover:bg-muted/60 active:scale-[0.99] transition-all"
                    onClick={() => setMobileDeviceListOpen(true)}
                    data-testid="mobile-selected-device-header-button"
                    aria-label="Open device list"
                  >
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Selected device</p>
                    <h3 className="text-lg font-bold leading-tight truncate" data-testid="mobile-selected-device-name">{deviceDisplayName(selectedDeviceData)}</h3>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-0 h-9 w-9 rounded-full shrink-0"
                    onClick={closeSelectedMobileDevice}
                    data-testid="mobile-telemetry-launcher-close"
                    aria-label="Clear selected device"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                </div>
                {/* Expanded telemetry — slides up from the island (animated grid rows) */}
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${mobileTelemetrySection ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                  data-testid="mobile-telemetry-sheet-expander"
                >
                  <div className="min-h-0 overflow-hidden">
                    {mobileSheetSection && (
                      <div
                        className="h-[calc(82dvh-9.5rem)] flex flex-col"
                        data-testid="mobile-telemetry-section-window"
                        onTouchStart={handleTelemetrySwipeStart}
                        onTouchEnd={handleTelemetrySwipeEnd}
                      >
                        <div
                          key={mobileSheetSection}
                          className={`min-h-0 flex-1 flex flex-col ${telemetrySlideDir === 'left' ? 'animate-in slide-in-from-right-8 fade-in duration-200' : telemetrySlideDir === 'right' ? 'animate-in slide-in-from-left-8 fade-in duration-200' : 'animate-in fade-in duration-200'}`}
                          data-testid="mobile-telemetry-slide-wrapper"
                        >
                          <TelemetryPanel
                            device={selectedDeviceData}
                            onClose={() => setMobileTelemetrySection(null)}
                            api={api}
                            userTimezone={userTimezone}
                            roadSpeedLimit={roadSpeedLimits[selectedDeviceData?.id]?.limit}
                            roadSpeedSource={roadSpeedLimits[selectedDeviceData?.id]?.source}
                            roadName={roadSpeedLimits[selectedDeviceData?.id]?.road_name}
                            refreshKey={telemetryRefreshKey}
                            activeEvents={activeEventsByDevice[selectedDeviceData?.id] || []}
                            className="w-full min-h-0 flex-1 rounded-none border-0"
                            testId="mobile-telemetry-panel"
                            mobileSection={mobileSheetSection}
                            testIdPrefix="mobile-"
                            onViewEventOnMap={(evt) => {
                              if (evt.latitude != null && evt.longitude != null) {
                                setEventPanActive(true);
                                setEventMarker({
                                  eventId: evt.id,
                                  lat: evt.latitude,
                                  lng: evt.longitude,
                                  severity: evt.severity || 'warning',
                                  occurrence: evt,
                                });
                                setMobileTelemetrySection(null);
                                panMapToPosition({ lat: evt.latitude, lng: evt.longitude }, 17);
                                setTimeout(() => setEventPanActive(false), 1500);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Tabs — open/switch sections; tapping the active tab collapses back to the island */}
                <div className="flex justify-center gap-2 px-3 py-2">
                  {mobileTelemetrySectionItems.map(({ key, label, icon: Icon }) => {
                    const active = mobileTelemetrySection === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => (active ? setMobileTelemetrySection(null) : goToTelemetrySection(key))}
                        className={`flex min-w-0 flex-1 basis-0 max-w-24 flex-col items-center justify-center gap-1 rounded-lg border active:scale-[0.98] transition-all py-2 px-1 focus:outline-none ${active ? 'border-[#2563eb] bg-[#2563eb] text-white shadow-md' : 'border-border bg-background'}`}
                        data-testid={`mobile-open-${key}-section-button`}
                        aria-pressed={active}
                      >
                        <span className={`flex h-8 w-8 items-center justify-center ${active ? 'text-white' : 'text-[#2563eb] dark:text-[#60a5fa]'}`}>
                          <Icon className="w-6 h-6" />
                        </span>
                        <span className="text-[11px] font-semibold leading-tight">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {/* Map Provider Toggle - TOP CENTER
              Logic: 
              - OSM ❌ + Google ❌ = Show OSM only (fallback, no toggle needed)
              - OSM ✅ + Google ❌ = Show OSM only (no toggle needed)
              - OSM ✅ + Google ✅ = Show both buttons (toggle visible)
              - OSM ❌ + Google ✅ = Show Google only (no toggle needed)
          */}
          {hasOSMMaps && hasGoogleMaps && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000]">
              <div className="flex gap-2">
                {/* OSM Button */}
                <button
                  onClick={() => setMapType('osm')}
                  className={`flex items-center justify-center gap-0 lg:gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${
                    mapType === 'osm'
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'
                  }`}
                >
                  <MapIcon className="hidden lg:block w-4 h-4" />
                  <span>OSM</span>
                </button>
                {/* Google Button */}
                <button
                  onClick={() => setMapType('roadmap')}
                  className={`flex items-center justify-center gap-0 lg:gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${
                    mapType !== 'osm'
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'
                  }`}
                >
                  <Globe className="hidden lg:block w-4 h-4" />
                  <span>Google</span>
                </button>
              </div>
            </div>
          )}

          {/* Map Type Controls - TOP LEFT (icon-only) - ONLY FOR GOOGLE MAPS */}
          {hasGoogleMaps && mapType !== 'osm' && (
            <div className="absolute top-4 left-4 z-[1000] flex gap-1">
              {/* Street Map Button - ALWAYS VISIBLE */}
              <button
                onClick={() => setMapType('roadmap')}
                className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                  mapType === 'roadmap'
                    ? 'bg-[#2563eb] text-white shadow-lg'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                }`}
                title="Street Map"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                  <line x1="9" y1="3" x2="9" y2="18" />
                  <line x1="15" y1="6" x2="15" y2="21" />
                </svg>
              </button>

              {/* Satellite Button - ALWAYS VISIBLE */}
              {hasSatelliteView && (
                <button
                  onClick={() => setMapType('satellite')}
                  className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                    mapType === 'satellite'
                      ? 'bg-[#2563eb] text-white shadow-lg'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                  }`}
                  title="Satellite View"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16.201 7.79899C17.8024 9.40034 20.3987 9.40034 22 7.79899L16.201 2C14.5997 3.60135 14.5997 6.19764 16.201 7.79899Z" />
                    <path d="M16 8L14.5 9.5" />
                    <path d="M14.8322 13.001C15.4344 12.3988 15.7354 12.0977 15.7354 11.7236C15.7354 11.3494 15.4344 11.0484 14.8322 10.4462L13.5548 9.16879C12.9526 8.56662 12.6515 8.26553 12.2774 8.26553C11.9033 8.26553 11.6022 8.56662 11 9.16879L6.79367 13.3751C5.73544 14.4334 5.73544 16.1491 6.79367 17.2073C7.8519 18.2656 9.56763 18.2656 10.6259 17.2073L14.8322 13.001Z" />
                    <path d="M15.4688 16.8563L16.8563 15.4688C17.5104 14.8147 17.8374 14.4877 18.2438 14.4877C18.6502 14.4877 18.9773 14.8147 19.6314 15.4688L21.0189 16.8563C21.673 17.5104 22 17.8374 22 18.2438C22 18.6502 21.673 18.9773 21.0189 19.6314L19.6314 21.0189C18.9773 21.673 18.6502 22 18.2438 22C17.8374 22 17.5104 21.673 16.8563 21.0189L15.4688 19.6314C14.8147 18.9773 14.4877 18.6502 14.4877 18.2438C14.4877 17.8374 14.8147 17.5104 15.4688 16.8563Z" />
                    <path d="M2.98112 4.36864L4.36864 2.98112C5.02273 2.32704 5.34977 2 5.75616 2C6.16256 2 6.4896 2.32704 7.14368 2.98112L8.5312 4.36864C9.18528 5.02273 9.51233 5.34977 9.51233 5.75616C9.51233 6.16256 9.18528 6.4896 8.5312 7.14368L7.14368 8.5312C6.4896 9.18528 6.16256 9.51233 5.75616 9.51233C5.34977 9.51233 5.02273 9.18528 4.36864 8.5312L2.98112 7.14368C2.32704 6.4896 2 6.16256 2 5.75616C2 5.34977 2.32704 5.02273 2.98112 4.36864Z" />
                    <path d="M16 16L14 14M9.99999 10L8 8" />
                    <path d="m 3.6955907,16.837439 c 0.11884,1.5706 1.56973,3.0084 3.1517,3.1305 m -5.811874,-3.06153 c -0.15384005,3.0281 2.74794,5.9038 5.79835,5.7565" />
                  </svg>
                </button>
              )}

              {/* Terrain Button - Show when Street Map or Terrain is active */}
              {hasTerrainView && (mapType === 'roadmap' || mapType === 'terrain') && (
                <button
                  onClick={() => setMapType('terrain')}
                  className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                    mapType === 'terrain'
                      ? 'bg-[#2563eb] text-white shadow-lg'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                  }`}
                  title="Terrain View"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
                  </svg>
                </button>
              )}

              {/* Hybrid Button - Show when Satellite or Hybrid is active */}
              {(mapType === 'satellite' || mapType === 'hybrid') && (
                <button
                  onClick={() => setMapType('hybrid')}
                  className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${
                    mapType === 'hybrid'
                      ? 'bg-[#2563eb] text-white shadow-lg'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'
                  }`}
                  title="Hybrid View"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                    <path d="M18.8295 14.2542C19.1276 13.9312 19.2766 13.7698 19.435 13.6756C19.8171 13.4483 20.2876 13.4412 20.6761 13.6569C20.8372 13.7463 20.9908 13.9032 21.298 14.2171C21.6053 14.531 21.7589 14.6879 21.8464 14.8524C22.0575 15.2492 22.0506 15.7299 21.8281 16.1203C21.7359 16.2821 21.5779 16.4343 21.2617 16.7388L17.5003 20.3617C16.9012 20.9387 16.6016 21.2273 16.2272 21.3735C15.8528 21.5197 15.4413 21.509 14.6181 21.4874L14.5062 21.4845C14.2556 21.478 14.1303 21.4747 14.0574 21.392C13.9846 21.3094 13.9945 21.1817 14.0144 20.9265L14.0252 20.7879C14.0812 20.0694 14.1092 19.7102 14.2495 19.3873C14.3898 19.0644 14.6318 18.8022 15.1158 18.2778L18.8295 14.2542Z" />
                    <path d="M22 10.5V9.71749C22 7.77787 22 6.80807 21.4142 6.2055C20.8284 5.60294 19.8856 5.60294 18 5.60294H15.9214C15.004 5.60294 14.9964 5.60116 14.1715 5.18834L10.8399 3.52114C9.44884 2.82504 8.75332 2.47699 8.01238 2.50118C7.27143 2.52537 6.59877 2.91808 5.25345 3.70351L4.02558 4.42037C3.03739 4.99729 2.54329 5.28576 2.27164 5.76564C2 6.24553 2 6.82993 2 7.99873V16.2157C2 17.7514 2 18.5193 2.34226 18.9467C2.57001 19.231 2.88916 19.4222 3.242 19.4856C3.77226 19.5808 4.42148 19.2018 5.71987 18.4437C6.60156 17.929 7.45011 17.3944 8.50487 17.5394C9.38869 17.6608 10.21 18.2185 11 18.6138" />
                    <path d="M8 2.5L8 17.5" />
                    <path d="M15 5.5V13.5" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Custom Zoom Controls - RIGHT TOP */}
          <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
            <button
              onClick={handleZoomIn}
              className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer"
              title="Zoom in"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            <button
              onClick={handleZoomOut}
              className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer"
              title="Zoom out"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </button>
            {/* Fullscreen Toggle - desktop only */}
            <button
              onClick={handleToggleFullscreen}
              className="hidden lg:flex w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl hover:bg-slate-50 dark:hover:bg-slate-700 items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer"
              title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
              data-testid="map-fullscreen-toggle"
            >
              {isFullscreen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                  <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                  <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                  <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                  <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                  <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                  <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                </svg>
              )}
            </button>
            {/* Geofence Overlay Toggle */}
            {hasGeofenceMapOverlay && (
              <div className="relative" ref={geofenceOverlayPickerRef}>
                <button
                  type="button"
                  onClick={handleGeofenceOverlayButtonClick}
                  onContextMenu={(e) => { e.preventDefault(); setShowGeofenceOverlayPicker(prev => !prev); }}
                  className={`relative w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center border cursor-pointer ${
                    showGeofenceOverlay
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600'
                  }`}
                  title={showGeofenceOverlay ? `Geofencing Overlays: ${geofenceOverlayCounts.total} visible (right-click to filter)` : 'Show Geofencing Zones, Places and Routes'}
                  data-testid="toggle-geofence-overlay-btn"
                >
                  {geofenceOverlayLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Hexagon className="w-5 h-5" />}
                  {showGeofenceOverlay && geofenceOverlayCounts.total > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-white text-[9px] font-bold text-emerald-700 border border-emerald-200" data-testid="geofence-overlay-count-badge">
                      {geofenceOverlayCounts.total > 99 ? '99+' : geofenceOverlayCounts.total}
                    </span>
                  )}
                </button>

                {showGeofenceOverlayPicker && (
                  <div className="absolute right-11 top-0 z-[1100] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[200px] lg:min-w-[180px]" data-testid="geofence-overlay-picker">
                    <div className="px-4 lg:px-3 py-2 lg:py-1.5 text-xs lg:text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Map Overlays</div>
                    {showGeofenceOverlay && (
                      <button
                        type="button"
                        onClick={() => { setShowGeofenceOverlay(false); setShowGeofenceOverlayPicker(false); }}
                        className="lg:hidden w-full text-left px-4 py-3 text-base flex items-center gap-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                        data-testid="geofence-overlay-hide-button"
                      >
                        <span className="w-2 h-2 rounded-full border border-slate-400 shrink-0" />
                        Hide Overlays
                      </button>
                    )}
                    {geofenceOverlayTypeOptions.map(option => {
                      const enabled = geofenceOverlayTypes?.[option.key] ?? true;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => toggleGeofenceOverlayType(option.key)}
                          className={`w-full text-left px-4 lg:px-3 py-3 lg:py-2 text-base lg:text-xs flex items-center gap-2 ${enabled ? 'text-slate-800 dark:text-slate-100 font-medium' : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          data-testid={`geofence-overlay-type-${option.key}`}
                        >
                          <span className="w-2 h-2 lg:w-1.5 lg:h-1.5 rounded-full shrink-0" style={{ backgroundColor: enabled ? option.color : 'transparent', border: enabled ? 'none' : '1px solid currentColor' }} />
                          <span className="flex-1">{option.label}</span>
                          <span className="text-xs lg:text-[10px] tabular-nums text-muted-foreground">{option.count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {/* Device Name Labels Toggle */}
            <button
              onClick={handleToggleLabels}
              className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center border cursor-pointer ${
                showLabels
                  ? 'bg-[#2563eb] text-white border-[#2563eb]'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600'
              }`}
              title={showLabels ? "Hide Device Labels" : "Show Device Labels"}
              data-testid="toggle-labels-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
                <path d="M7 7h.01" />
              </svg>
            </button>
            {/* Marker Clustering Toggle */}
            <button
              onClick={handleToggleClustering}
              className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center border cursor-pointer ${
                clusteringEnabled
                  ? 'bg-[#2563eb] text-white border-[#2563eb]'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600'
              }`}
              title={clusteringEnabled ? "Disable Marker Clustering" : "Enable Marker Clustering"}
              data-testid="toggle-clustering-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="9" r="4" />
                <circle cx="16" cy="16" r="4" />
                <circle cx="16" cy="8" r="3" />
              </svg>
            </button>
            {/* More Features Button - always visible */}
            {hasAnyMapEnhancement && (
              <button
                onClick={() => setMoreFeaturesOpen(true)}
                className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center border cursor-pointer ${
                  mapFeatureSettings && Object.values(mapFeatureSettings).some(s => s?.enabled)
                    ? 'bg-violet-500 text-white border-violet-500'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600'
                }`}
                title="More Features"
                data-testid="more-features-btn"
              >
                <Sparkles className="w-[18px] h-[18px]" />
              </button>
            )}
          </div>

          {/* Middle Left Feature Toggles - ONLY FOR GOOGLE MAPS */}
          {hasGoogleMaps && mapType !== 'osm' && (
            <div className="absolute top-1/2 -translate-y-1/2 left-4 z-[1000] flex flex-col gap-2">
              {/* POI Labels Toggle - Hide when Satellite is active */}
              {mapType !== 'satellite' && (
                <button
                  onClick={handleTogglePOI}
                  className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                    showPOI
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                  title="Toggle POI Labels"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10V9.21749C22 7.27787 22 6.30807 21.4142 5.7055C20.8284 5.10294 19.8856 5.10294 18 5.10294H15.9214C15.004 5.10294 14.9964 5.10116 14.1715 4.68834L10.8399 3.02114C9.44884 2.32504 8.75332 1.97699 8.01238 2.00118C7.27143 2.02537 6.59877 2.41808 5.25345 3.20351L4.02558 3.92037C3.03739 4.49729 2.54329 4.78576 2.27164 5.26564C2 5.74553 2 6.32993 2 7.49873V15.7157C2 17.2514 2 18.0193 2.34226 18.4467C2.57001 18.731 2.88916 18.9222 3.242 18.9856C3.77226 19.0808 4.42148 18.7018 5.71987 17.9437C6.60156 17.429 7.45011 16.8944 8.50487 17.0394C9.38869 17.1608 10.21 17.7185 11 18.1138" />
                    <path d="M8 2L8 17" />
                    <path d="M15 5V9.5" />
                    <path d="M18.3083 21.6835C18.0915 21.8865 17.8017 22 17.5001 22C17.1985 22 16.9087 21.8865 16.6919 21.6835C14.7063 19.813 12.0455 17.7235 13.3431 14.6898C14.0447 13.0496 15.7289 12 17.5001 12C19.2713 12 20.9555 13.0496 21.6571 14.6898C22.9531 17.7196 20.2988 19.8194 18.3083 21.6835Z" />
                    <path d="M17.5 16.5H17.509" />
                  </svg>
                </button>
              )}

              {/* Traffic Toggle */}
              {hasTrafficLayer && (
                <button
                  onClick={() => setShowTraffic(!showTraffic)}
                  className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                    showTraffic
                      ? 'bg-amber-500 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                  title="Toggle Traffic"
                  data-testid="toggle-traffic-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="16" r="2" />
                    <rect x="6" y="4" width="12" height="16" rx="2" />
                  </svg>
                </button>
              )}

              {/* Heatmap Toggle */}
              {hasHeatmap && (
                <button
                  onClick={() => setShowHeatmap(!showHeatmap)}
                  className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                    showHeatmap
                      ? 'bg-orange-500 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                  title="Toggle Heat Map"
                  data-testid="toggle-heatmap-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M3.75586 14.501C3.75586 19.0494 7.45315 22.7483 12.0009 22.751C11.9992 22.751 11.9975 22.751 11.9958 22.751H12.0059C12.0042 22.751 12.0025 22.751 12.0009 22.751C16.5486 22.7483 20.2458 19.0494 20.2458 14.501V14.501C20.2458 13.441 20.2458 11.241 18.0859 8.53104C17.8959 8.29104 17.5659 8.19105 17.2659 8.29105C16.9759 8.38105 16.7659 8.66104 16.7559 8.97104C16.7459 9.37104 16.5758 10.301 16.1658 10.621C16.0658 10.701 15.9159 10.781 15.5959 10.711C14.9159 10.571 14.8059 10.061 14.7559 8.25104L14.7558 8.24708C14.6958 6.00742 14.6136 2.94008 10.8058 1.31104C10.5658 1.21104 10.3059 1.23103 10.0959 1.37103C9.88589 1.51103 9.75586 1.75104 9.75586 2.00104C9.75586 4.11104 8.54587 5.52103 7.14587 7.15103C5.55587 8.99103 3.75586 11.091 3.75586 14.501ZM8.28589 8.12103C9.51589 6.69103 10.7859 5.21103 11.1559 3.18103C13.1545 4.47014 13.2058 6.39838 13.2558 8.27716L13.2559 8.28104C13.2959 9.91104 13.3459 11.7611 15.2859 12.1711C16.1559 12.3511 16.7459 12.061 17.0959 11.791C17.4359 11.531 17.6759 11.181 17.8459 10.811C18.7659 12.451 18.7659 13.731 18.7659 14.491V14.491C18.7659 18.211 15.7359 21.241 12.0159 21.241C8.29587 21.241 5.26587 18.211 5.26587 14.491C5.26587 11.6476 6.72911 9.93894 8.27516 8.13357L8.28589 8.12103ZM7.75586 15.0005C7.75586 17.7505 9.54586 19.7505 12.0059 19.7505C14.3459 19.7505 16.2559 17.4005 16.2559 14.5005C16.2559 14.2505 16.1359 14.0205 15.9359 13.8805C15.7359 13.7405 15.4758 13.7005 15.2458 13.7905C14.3658 14.1205 13.6459 14.1204 13.0959 13.7704C12.2459 13.2404 11.8459 11.9504 11.7559 10.9304C11.7359 10.7004 11.6059 10.4804 11.4059 10.3604C11.2059 10.2404 10.9558 10.2204 10.7358 10.3004C8.94584 10.9804 7.75586 13.5805 7.75586 15.0005ZM9.25586 15.0005C9.25586 14.3005 9.77583 13.0605 10.4758 12.2905C10.7158 13.2205 11.2359 14.3904 12.2859 15.0504C12.9759 15.4804 13.7659 15.6305 14.6559 15.5005C14.3359 17.0805 13.2659 18.2505 12.0059 18.2505C10.3659 18.2505 9.25586 16.9405 9.25586 15.0005Z" />
                  </svg>
                </button>
              )}

              {/* Dark Mode Map Toggle */}
              {hasDarkMode && (
                <button
                  onClick={handleToggleDarkMode}
                  className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                    darkModeMap
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                  title={darkModeMap ? "Switch to Light Map" : "Switch to Dark Map"}
                  data-testid="toggle-dark-mode-map-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                </button>
              )}

              {/* Weather Overlay Toggle */}
              {hasWeather && weatherApiKey && (
                <div className="relative">
                  <button
                    onClick={handleWeatherButtonClick}
                    onContextMenu={(e) => { e.preventDefault(); setShowWeatherPicker(p => !p); }}
                    className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                      showWeather
                        ? 'bg-sky-500 text-white'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                    }`}
                    title={showWeather ? `Weather: ${weatherLayers.find(l => l.id === weatherLayer)?.label || weatherLayer} (right-click to change layer)` : "Toggle Weather Overlay (right-click for layer options)"}
                    data-testid="toggle-weather-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 15c-2.21 0-4-1.79-4-4 0-1.86 1.28-3.41 3-3.86V7a5 5 0 0 1 9.9-.84A3.5 3.5 0 0 1 20 9.5a3.5 3.5 0 0 1-3.5 3.5H16" />
                      <path d="M8 19v1" /><path d="M8 15v1" />
                      <path d="M12 19v1" /><path d="M12 15v1" />
                      <path d="M16 19v1" /><path d="M16 15v1" />
                    </svg>
                  </button>
                  {/* Layer Picker Dropdown */}
                  {showWeatherPicker && (
                    <div className="absolute left-11 top-0 z-[1100] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[190px] lg:min-w-[160px]" data-testid="weather-layer-picker">
                      <div className="px-4 lg:px-3 py-2 lg:py-1.5 text-xs lg:text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Weather Layer</div>
                      {showWeather && (
                        <button
                          type="button"
                          onClick={() => { setShowWeather(false); setShowWeatherPicker(false); }}
                          className="lg:hidden w-full text-left px-4 py-3 text-base flex items-center gap-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                          data-testid="weather-hide-button"
                        >
                          <span className="w-2 h-2 rounded-full border border-slate-400 shrink-0" />
                          Hide Weather
                        </button>
                      )}
                      {weatherLayers.map(l => (
                        <button
                          key={l.id}
                          onClick={() => { setWeatherLayer(l.id); setShowWeather(true); setShowWeatherPicker(false); }}
                          className={`w-full text-left px-4 lg:px-3 py-3 lg:py-2 text-base lg:text-xs flex items-center gap-2 ${weatherLayer === l.id ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 font-medium' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          data-testid={`weather-layer-${l.id}`}
                        >
                          {weatherLayer === l.id && <span className="w-2 h-2 lg:w-1.5 lg:h-1.5 rounded-full bg-sky-500 shrink-0" />}
                          {weatherLayer !== l.id && <span className="w-2 h-2 lg:w-1.5 lg:h-1.5 shrink-0" />}
                          {l.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Nearest Device Finder Toggle */}
              {hasNearestDevice && (
                <button
                  onClick={() => { 
                    const newState = !finderActive;
                    setFinderActive(newState);
                    if (!newState) { setFinderPin(null); setFinderResults([]); setFinderAddress(''); }
                  }}
                  className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${
                    finderActive
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                  title={finderActive ? "Exit Nearest Device Finder" : "Find Nearest Devices"}
                  data-testid="toggle-finder-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                    <line x1="12" y1="2" x2="12" y2="4" />
                    <line x1="12" y1="20" x2="12" y2="22" />
                    <line x1="2" y1="12" x2="4" y2="12" />
                    <line x1="20" y1="12" x2="22" y2="12" />
                  </svg>
                </button>
              )}

              {/* Fit All Devices - moved here from zoom controls */}
              <button
                onClick={handleFitAllDevices}
                className="w-9 h-9 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg transition-all flex items-center justify-center"
                title="Fit all devices"
                data-testid="fit-all-devices-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <polyline points="21 15 21 21 15 21" />
                  <polyline points="3 9 3 3 9 3" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                  <line x1="21" y1="21" x2="14" y2="14" />
                  <line x1="3" y1="3" x2="10" y2="10" />
                </svg>
              </button>
            </div>
          )}

          {/* Address Search Bar - Bottom Left */}
          <div className="hidden lg:block absolute bottom-8 left-4 z-[1000]">
            <AddressAutocomplete
              value={addressSearch}
              onChange={setAddressSearch}
              onSelect={(s) => {
                const lat = s.lat, lng = s.lon;
                setSearchMarker({ lat, lng });
                if (mapType === 'osm' && leafletMapRef.current) {
                  leafletMapRef.current.flyTo([lat, lng], 16);
                } else if (mapRef.current) {
                  mapRef.current.panTo({ lat, lng });
                  mapRef.current.setZoom(16);
                }
                toast.success('Location found');
              }}
              onClear={() => setSearchMarker(null)}
              placeholder="Search address..."
              inputClassName="h-9 w-56 text-sm bg-white dark:bg-[#0D1425] shadow-md"
              dropUp={true}
              data-testid="address-search"
            />
          </div>

          {/* Weather Legend - Bottom Right */}
          {showWeather && hasWeather && weatherApiKey && (
            <div className="absolute bottom-8 right-4 z-[1000] bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs" data-testid="weather-legend">
              <div className="font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                {weatherLayers.find(l => l.id === weatherLayer)?.label || 'Weather'}
              </div>
              {weatherLayer === 'precipitation_new' && (
                <div>
                  <div className="h-2.5 w-32 rounded-sm" style={{ background: 'linear-gradient(to right, rgba(225,200,100,0), rgba(200,150,150,0), rgba(150,150,170,0), rgba(120,120,190,0.1), rgba(110,110,205,0.3), rgba(80,80,225,0.7), rgba(20,20,255,0.9))' }} />
                  <div className="flex justify-between w-32 mt-0.5">
                    <span className="text-[9px] text-slate-500">0</span>
                    <span className="text-[9px] text-slate-500">mm</span>
                    <span className="text-[9px] text-slate-500">140</span>
                  </div>
                </div>
              )}
              {weatherLayer === 'clouds_new' && (
                <div>
                  <div className="h-2.5 w-32 rounded-sm" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(253,253,255,0.1), rgba(252,251,255,0.2), rgba(250,250,255,0.3), rgba(249,248,255,0.4), rgba(247,247,255,0.5), rgba(246,245,255,0.75), rgba(244,244,255,1), rgba(242,241,255,1), rgba(240,240,255,1))' }} />
                  <div className="flex justify-between w-32 mt-0.5">
                    <span className="text-[9px] text-slate-500">0%</span>
                    <span className="text-[9px] text-slate-500">Coverage</span>
                    <span className="text-[9px] text-slate-500">100%</span>
                  </div>
                </div>
              )}
              {weatherLayer === 'temp_new' && (
                <div>
                  <div className="h-2.5 w-32 rounded-sm" style={{ background: 'linear-gradient(to right, #821692, #8257db, #208cec, #20c4e8, #23dddd, #c2ff28, #fff028, #ffc228, #fc8014)' }} />
                  <div className="flex justify-between w-32 mt-0.5">
                    <span className="text-[9px] text-slate-500">-65</span>
                    <span className="text-[9px] text-slate-500">&deg;C</span>
                    <span className="text-[9px] text-slate-500">30</span>
                  </div>
                </div>
              )}
              {weatherLayer === 'wind_new' && (
                <div>
                  <div className="h-2.5 w-32 rounded-sm" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0), #aedbf9, #96c7dc, #78b4bd, #5da182, #5daa00, #a0be00, #d7c700, #ffaa00, #ff5500)' }} />
                  <div className="flex justify-between w-32 mt-0.5">
                    <span className="text-[9px] text-slate-500">0</span>
                    <span className="text-[9px] text-slate-500">m/s</span>
                    <span className="text-[9px] text-slate-500">100+</span>
                  </div>
                </div>
              )}
              {weatherLayer === 'pressure_new' && (
                <div>
                  <div className="h-2.5 w-32 rounded-sm" style={{ background: 'linear-gradient(to right, #0000cd, #0064ff, #00b4ff, #00e6c8, #64ff00, #c8ff00, #ffff00, #ff8c00, #ff0000, #cd0000)' }} />
                  <div className="flex justify-between w-32 mt-0.5">
                    <span className="text-[9px] text-slate-500">950</span>
                    <span className="text-[9px] text-slate-500">hPa</span>
                    <span className="text-[9px] text-slate-500">1050</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Nearest Device Finder Panel */}
          {finderActive && hasNearestDevice && (
            <div className="absolute bottom-8 right-4 z-[1001] bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 w-72" data-testid="finder-panel">
              <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Nearest Devices</span>
                <button onClick={() => { setFinderActive(false); setFinderPin(null); setFinderResults([]); setFinderAddress(''); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
                <AddressAutocomplete
                  value={finderAddress}
                  onChange={setFinderAddress}
                  onSelect={(s) => {
                    const lat = s.lat, lng = s.lon;
                    setFinderPin({ lat, lng });
                    computeFinderResults(lat, lng);
                    if (mapType === 'osm' && leafletMapRef.current) leafletMapRef.current.flyTo([lat, lng], 12);
                    else if (mapRef.current) { mapRef.current.panTo({ lat, lng }); mapRef.current.setZoom(12); }
                  }}
                  onClear={() => { setFinderPin(null); setFinderResults([]); }}
                  placeholder="Search address or click map..."
                  inputClassName="h-7 text-xs"
                  data-testid="finder-address"
                />
              </div>
              {finderPin && (
                <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-green-500 shrink-0" />
                  <span
                    className="text-[11px] text-blue-500 font-mono flex-1"
                    data-testid="finder-pin-coords"
                  >
                    {finderPin.lat.toFixed(6)}, {finderPin.lng.toFixed(6)}
                  </span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${finderPin.lat.toFixed(6)}, ${finderPin.lng.toFixed(6)}`); toast.success('Coordinates copied'); }}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-0.5"
                    title="Copy coordinates"
                    data-testid="finder-copy-coords"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
              {finderResults.length > 0 ? (
                <div className="max-h-60 overflow-y-auto">
                  {finderResults.map((r, i) => (
                    <button
                      key={r.id}
                      onClick={() => { setSelectedDevice(r.id); handleDeviceSelect(r.id); }}
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700/50 last:border-0"
                      data-testid={`finder-result-${i}`}
                    >
                      <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate text-slate-700 dark:text-slate-200">{r.name}</div>
                        {r.registration && <div className="text-[10px] text-slate-400 truncate">{r.registration}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-semibold text-emerald-600">{formatNearestDistance(r.distance)}</div>
                        <div className={`text-[9px] ${r.status === 'moving' ? 'text-blue-600' : r.status === 'online' ? 'text-green-600' : r.status === 'idle' ? 'text-yellow-600' : r.status === 'stopped' ? 'text-red-600' : 'text-gray-500'}`}>{r.status}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : finderPin ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400">No devices found</div>
              ) : (
                <div className="px-3 py-4 text-center text-xs text-slate-400">Click on map or search an address</div>
              )}
            </div>
          )}

          {/* Map Rendering - Conditional based on map type */}
          {mapType === 'osm' ? (
            /* Leaflet Map for OSM */
            <MapContainer
              center={[lastMapCenter.lat, lastMapCenter.lng]}
              zoom={lastMapZoom}
              className="h-full w-full"
              scrollWheelZoom={true}
              zoomControl={false}
              ref={leafletMapRef}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
              {/* Weather Tile Layer for OSM */}
              {showWeather && hasWeather && weatherApiKey && (
                <TileLayer
                  url={`https://tile.openweathermap.org/map/${weatherLayer}/{z}/{x}/{y}.png?appid=${weatherApiKey}`}
                  opacity={1.0}
                  attribution='&copy; OpenWeatherMap'
                />
              )}

              {/* Geofencing Zones / Places / Routes Overlay for OSM */}
              {renderLeafletGeofenceOverlays()}

              <LeafletMapController selectedDevice={selectedDevice} devices={devices} onBoundsChange={handleBoundsChange} onMapClick={handleFinderMapClick} getDisplayPosition={getDeviceDisplayPosition} skipPan={eventPanActive} />

              {/* Search Result Marker for OSM */}
              {searchMarker && (
                <LeafletMarker
                  position={[searchMarker.lat, searchMarker.lng]}
                  icon={L.icon({
                    iconUrl: '/leaflet/marker-icon-red.png',
                    shadowUrl: '/leaflet/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                  })}
                >
                  <Popup>
                    <div className="p-1">
                      <p className="font-semibold text-sm">Search Result</p>
                      <p className="text-xs text-gray-600">{addressSearch}</p>
                    </div>
                  </Popup>
                </LeafletMarker>
              )}

              {/* Finder Pin Marker for OSM */}
              {finderPin && finderActive && (
                <LeafletMarker
                  position={[finderPin.lat, finderPin.lng]}
                  icon={L.divIcon({ className: '', html: '<div style="width:12px;height:12px;border-radius:50%;background:#10b981;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);margin:-6px 0 0 -6px;"></div>', iconSize: [12, 12], iconAnchor: [6, 6] })}
                />
              )}

              {/* Clustered device markers for OSM */}
              {clusteredData.map((item) => {
                if (item.type === 'cluster') {
                  return (
                    <LeafletMarker
                      key={`cluster-${item.clusterId}`}
                      position={[item.lat, item.lng]}
                      icon={createClusterIcon(item.count)}
                      eventHandlers={{
                        click: () => handleClusterClick(item.clusterId, item.lat, item.lng),
                      }}
                    />
                  );
                }
                const device = item.device;
                if (!device) return null;
                const dp = getDeviceDisplayPosition(device);
                return (
                  <LeafletMarker
                    key={device.id}
                    position={[dp.lat, dp.lng]}
                    icon={isAssetBeaconDevice(device)
                      ? createLeafletBeaconIcon(device, showLabels ? getDeviceLabel(device) : null)
                      : device.map_marker_mode === 'icon'
                        ? createLeafletDeviceIconMarker(device, showLabels ? getDeviceLabel(device) : null, getStopTimerInfo(device), customIcons)
                        : createLeafletIcon(device.status, dp.heading, showLabels ? getDeviceLabel(device) : null, getStopTimerInfo(device))}
                    eventHandlers={{
                      click: () => handleDeviceSelect(device.id),
                    }}
                  />
                );
              })}

              {/* Speed Limit Badges — Leaflet/OSM (all devices with ignition ON) */}
              {(mapFeatureSettings?.speedAlert?.showSpeedLimit ?? true) &&
                visibleDevices
                  .filter(d => !isAssetBeaconDevice(d) && d.status !== 'offline' && roadSpeedLimits[d.id]?.limit && isIgnitionOn(d))
                  .map(device => {
                    const dp = getDeviceDisplayPosition(device);
                    if (!dp) return null;
                    return (
                      <LeafletMarker
                        key={`speed-limit-badge-leaflet-${device.id}`}
                        position={[dp.lat, dp.lng]}
                        icon={L.divIcon({
                          className: '',
                          html: createSpeedLimitLeafletHtml(roadSpeedLimits[device.id].limit),
                          iconSize: [0, 0],
                          iconAnchor: [0, 0],
                        })}
                        interactive={false}
                        zIndexOffset={9999}
                      />
                    );
                  })
              }

              {/* Trip Direction Trail polylines (OSM) */}
              {/* Event Marker — Leaflet/OSM */}
              {eventMarker && (() => {
                const sev = eventMarker.severity || 'warning';
                const shieldColor = sev === 'critical' ? '#dc2626' : sev === 'info' ? '#2563eb' : '#f59e0b';
                const ringColor   = sev === 'critical' ? '#fca5a5' : sev === 'info' ? '#93c5fd' : '#fde68a';
                return (
                  <LeafletMarker
                    key={`event-marker-leaflet-${eventMarker.eventId}`}
                    position={[eventMarker.lat, eventMarker.lng]}
                    icon={L.divIcon({
                      className: '',
                      html: `<div style="width:0;height:0;overflow:visible;cursor:pointer" data-testid="event-map-marker-leaflet">
                        <div style="position:absolute;width:52px;height:52px;border-radius:50%;background:${ringColor};opacity:0.5;transform:translate(-50%,-60%);animation:eventMarkerPulse 2s ease-out infinite"></div>
                        <div style="position:absolute;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 6px rgba(0,0,0,0.4));z-index:1">
                          <svg width="36" height="36" viewBox="0 0 514 512" xmlns="http://www.w3.org/2000/svg">
                            <path fill="${shieldColor}" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/>
                            <path fill="#FFFFFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/>
                          </svg>
                        </div>
                        <style>@keyframes eventMarkerPulse{0%{transform:translate(-50%,-60%) scale(.8);opacity:.5}70%{transform:translate(-50%,-60%) scale(2);opacity:0}100%{transform:translate(-50%,-60%) scale(2);opacity:0}}</style>
                      </div>`,
                      iconSize: [0, 0],
                      iconAnchor: [0, 0],
                    })}
                    zIndexOffset={10000}
                    eventHandlers={{ click: () => setForensicModalOpen(true) }}
                  />
                );
              })()}
              {mapFeatureSettings?.trail?.enabled && mapEnhancementPrivileges.trip_direction_trail &&
                visibleDevices
                  .filter(d => {
                    if (!getRawDevicePosition(d)) return false;
                    const history = positionHistoryRef.current[d.id];
                    if (!history || history.length < 2) return false;
                    if (!mapFeatureSettings.trail.showAllDevices && !trailEnabledDevices.has(d.id)) return false;
                    return true;
                  })
                  .map(d => {
                    const ts = mapFeatureSettings.trail;
                    const useRoadSnap = (ts.useRoadSnap ?? true) !== false;
                    const trailColor = ts.colorMode === 'single' ? (ts.singleColor || '#2563eb') : (STATUS_COLORS[d.status] || STATUS_COLORS.offline);
                    const rawPoints = positionHistoryRef.current[d.id];
                    const snapPending = trailSnapPendingRef.current[d.id];
                    const snappedPath = useRoadSnap ? snappedTrailsRef.current[d.id] : null;
                    const snappedPointColor = ts.snappedPointColor || DEFAULT_SNAPPED_POINT_COLOR;
                    const snappedPointRadius = (ts.snappedPointSize ?? DEFAULT_SNAPPED_POINT_SIZE_PX) / 2;
                    const renderedPath = buildRenderableTrailPath(d.id, rawPoints, snappedPath);
                    const polylinePoints = renderedPath.map(p => [p.lat, p.lng]);
                    if (polylinePoints.length < 2 && snapPending) return null;
                    return (
                      <React.Fragment key={`trail-group-${d.id}`}>
                        <LeafletPolyline
                          key={`trail-${d.id}`}
                          positions={polylinePoints}
                          pathOptions={{
                            color: trailColor,
                            weight: ts.thickness ?? 3,
                            opacity: ts.opacity ?? 0.6,
                          }}
                        />
                        {useRoadSnap && ts.showSnappedPoints && snappedPath && snappedPath.length >= 2 && snappedPath
                          .map((p, i) => ({ p, i, heading: i < snappedPath.length - 1 ? calcBearing(p, snappedPath[i + 1]) : (i > 0 ? calcBearing(snappedPath[i - 1], p) : 0) }))
                          .filter((_, idx) => idx % 5 === 0)
                          .map(({ p, i, heading }) => (
                          <LeafletMarker
                            key={`snapped-chev-${d.id}-${i}`}
                            position={[p.lat, p.lng]}
                            icon={createChevronIcon(heading, snappedPointColor, (ts.snappedPointSize ?? DEFAULT_SNAPPED_POINT_SIZE_PX) + 4)}
                            interactive={false}
                          />
                        ))}
                        {ts.showDataPoints && rawPoints.map((p, i) => (
                          <LeafletCircleMarker
                            key={`tp-${d.id}-${i}`}
                            center={[p.lat, p.lng]}
                            radius={ts.dataPointSize ?? 5}
                            pathOptions={{
                              color: ts.dataPointColor || trailColor,
                              fillColor: ts.dataPointColor || trailColor,
                              fillOpacity: 0.9,
                              weight: 1,
                              opacity: ts.opacity ?? 0.6,
                            }}
                          />
                        ))}
                      </React.Fragment>
                    );
                  })
              }
            </MapContainer>
          ) : (
            /* Google Map — keyed div forces full recreation on POI/dark mode toggle */
            <div key={`gmap-${gmapEpoch}-${showPOI ? 'poi' : 'nopoi'}-${darkModeMap ? 'dark' : 'light'}`} style={{ width: '100%', height: '100%' }}>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <GoogleMap
                defaultCenter={lastMapCenter}
                defaultZoom={lastMapZoom}
                mapId={showPOI ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
                colorScheme={darkModeMap ? 'DARK' : 'LIGHT'}
                style={FULL_SIZE_STYLE}
                disableDefaultUI={true}
                zoomControl={false}
                mapTypeControl={false}
                scaleControl={false}
                streetViewControl={false}
                rotateControl={false}
                fullscreenControl={false}
                mapTypeId={mapType}
              >
                {/* Controller — exposes map ref and handles fly-to */}
                <LiveTrackingMapController
                  selectedDevice={selectedDevice}
                  devices={devices}
                  onMapReady={handleGoogleMapReady}
                  onBoundsChange={handleBoundsChange}
                  onMapClick={handleFinderMapClick}
                  getDisplayPosition={getDeviceDisplayPosition}
                  skipPan={eventPanActive}
                />

                {/* Traffic Layer */}
                <GoogleTrafficLayer visible={showTraffic && hasTrafficLayer} />

                {/* deck.gl Overlay — heatmap + weather tiles (separate WebGL canvas).
                    Only mounted when there are actually layers to draw (heatmap or weather
                    enabled). When empty (the common/default case) we skip it entirely so NO
                    deck.gl Deck instance, WebGL context, luma.gl AnimationLoop (a
                    self-rescheduling requestAnimationFrame loop), or _getSizeAndAspect forced
                    reflows are created on every Live Tracking mount — this cuts the page-entry
                    cost (a chunk of the "[Violation] 'message' handler took ~Xms" spike on
                    navigating into Live Tracking) and removes the render loop entirely when
                    the feature is unused. It mounts on-demand when the user enables
                    heatmap/weather and tears down (finalize) when they disable it. */}
                {allDeckLayers.length > 0 && <DeckGLOverlay layers={allDeckLayers} />}

                {/* Geofencing Zones / Places / Routes Overlay for Google Maps */}
                {renderGoogleGeofenceOverlays()}

                {/* Trip Direction Trail polylines */}
                {mapFeatureSettings?.trail?.enabled && mapEnhancementPrivileges.trip_direction_trail &&
                  visibleDevices
                    .filter(d => {
                      if (!getRawDevicePosition(d)) return false;
                      const history = positionHistoryRef.current[d.id];
                      if (!history || history.length < 2) return false;
                      if (!mapFeatureSettings.trail.showAllDevices && !trailEnabledDevices.has(d.id)) return false;
                      return true;
                    })
                    .map(d => {
                      const history = positionHistoryRef.current[d.id];
                      const snapPending = trailSnapPendingRef.current[d.id];
                      const ts = mapFeatureSettings.trail;
                      const useRoadSnap = (ts.useRoadSnap ?? true) !== false;
                      const snappedPath = useRoadSnap ? snappedTrailsRef.current[d.id] : null;
                      const path = buildRenderableTrailPath(d.id, history, snappedPath);
                      if (path.length < 2 && snapPending) return null;
                      const trailColor = ts.colorMode === 'single' ? (ts.singleColor || '#2563eb') : (STATUS_COLORS[d.status] || STATUS_COLORS.offline);
                      const snappedPointColor = ts.snappedPointColor || DEFAULT_SNAPPED_POINT_COLOR;
                      // Convert pixel size to meters based on zoom level for GoogleCircle
                      const metersPerPixel = 156543.03392 * Math.cos((path[0]?.lat || 0) * Math.PI / 180) / Math.pow(2, mapZoom);
                      const dotRadiusMeters = (ts.dataPointSize ?? 5) * metersPerPixel;
                      const snappedPointRadiusMeters = Math.max(((ts.snappedPointSize ?? DEFAULT_SNAPPED_POINT_SIZE_PX) / 2) * metersPerPixel, 0.5);
                      const chevronIcons = (useRoadSnap && ts.showSnappedPoints && snappedPath && snappedPath.length >= 2 && window.google?.maps?.SymbolPath)
                        ? [{
                            icon: {
                              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                              scale: (ts.snappedPointSize ?? DEFAULT_SNAPPED_POINT_SIZE_PX) * 0.4,
                              fillColor: snappedPointColor,
                              fillOpacity: 0.95,
                              strokeColor: '#ffffff',
                              strokeWeight: 0.8,
                            },
                            repeat: '40px',
                          }]
                        : undefined;
                      return (
                        <React.Fragment key={`trail-group-${d.id}`}>
                          <GooglePolyline
                            key={`trail-${d.id}`}
                            path={path}
                            options={{
                              strokeColor: trailColor,
                              strokeOpacity: ts.opacity ?? 0.6,
                              strokeWeight: ts.thickness ?? 3,
                              ...(chevronIcons ? { icons: chevronIcons } : {}),
                            }}
                          />
                          {ts.showDataPoints && history.map((p, i) => (
                            <GoogleCircle
                              key={`tp-${d.id}-${i}`}
                              center={{ lat: p.lat, lng: p.lng }}
                              radius={dotRadiusMeters}
                              options={{
                                strokeColor: ts.dataPointColor || trailColor,
                                fillColor: ts.dataPointColor || trailColor,
                                fillOpacity: 0.9,
                                strokeWeight: 1,
                                strokeOpacity: ts.opacity ?? 0.6,
                              }}
                            />
                          ))}
                        </React.Fragment>
                      );
                    })
                }

                {/* Event Marker — Google Maps */}
                {eventMarker && (
                  <AdvancedMarker
                    position={{ lat: eventMarker.lat, lng: eventMarker.lng }}
                    onClick={() => setForensicModalOpen(true)}
                    zIndex={10000}
                  >
                    <EventMarkerIcon severity={eventMarker.severity} />
                  </AdvancedMarker>
                )}

                {/* Search Result Marker */}
                {searchMarker && (
                  <AdvancedMarker
                    position={{ lat: searchMarker.lat, lng: searchMarker.lng }}
                    onClick={() => setInfoWindowOpen('search-marker')}
                  >
                    <img
                      src="/leaflet/marker-icon-red.png"
                      alt="Search result"
                      style={{ width: '25px', height: '41px' }}
                    />
                  </AdvancedMarker>
                )}
                {infoWindowOpen === 'search-marker' && searchMarker && (
                  <GInfoWindow
                    position={{ lat: searchMarker.lat, lng: searchMarker.lng }}
                    onClose={() => setInfoWindowOpen(null)}
                  >
                    <div className="p-1">
                      <p className="font-semibold text-sm">Search Result</p>
                      <p className="text-xs text-gray-600">{addressSearch}</p>
                    </div>
                  </GInfoWindow>
                )}

                {/* Finder Pin Marker */}
                {finderPin && finderActive && (
                  <AdvancedMarker position={{ lat: finderPin.lat, lng: finderPin.lng }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981', border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
                  </AdvancedMarker>
                )}

                {/* Clustered Device Markers */}
                {clusteredData.map((item) => {
                  if (item.type === 'cluster') {
                    return (
                      <AdvancedMarker
                        key={`cluster-${item.clusterId}`}
                        position={{ lat: item.lat, lng: item.lng }}
                        onClick={() => handleClusterClick(item.clusterId, item.lat, item.lng)}
                      >
                        <ClusterMarkerContent count={item.count} />
                      </AdvancedMarker>
                    );
                  }
                  const device = item.device;
                  if (!device) return null;
                  const dp = getDeviceDisplayPosition(device);
                  return (
                    <AdvancedMarker
                      key={device.id}
                      position={{ lat: dp.lat, lng: dp.lng }}
                      onClick={() => {
                        handleDeviceSelect(device.id);
                      }}
                    >
                      {isAssetBeaconDevice(device)
                        ? <BeaconMarkerContent device={device} label={showLabels ? getDeviceLabel(device) : null} />
                        : device.map_marker_mode === 'icon'
                          ? <DeviceIconMarkerContent device={device} label={showLabels ? getDeviceLabel(device) : null} stopTimer={getStopTimerInfo(device)} customIcons={customIcons} />
                          : <DeviceMarkerContent status={device.status} heading={dp.heading} label={showLabels ? getDeviceLabel(device) : null} stopTimer={getStopTimerInfo(device)} />
                      }
                    </AdvancedMarker>
                  );
                })}

                {/* Speed Limit Badges — Google Maps (all devices with ignition ON) */}
                {(mapFeatureSettings?.speedAlert?.showSpeedLimit ?? true) &&
                  visibleDevices
                    .filter(d => !isAssetBeaconDevice(d) && d.status !== 'offline' && roadSpeedLimits[d.id]?.limit && isIgnitionOn(d))
                    .map(device => {
                      const dp = getDeviceDisplayPosition(device);
                      if (!dp) return null;
                      return (
                        <AdvancedMarker
                          key={`speed-limit-badge-${device.id}`}
                          position={{ lat: dp.lat, lng: dp.lng }}
                          zIndex={9999}
                        >
                          <SpeedLimitBadge limit={roadSpeedLimits[device.id].limit} />
                        </AdvancedMarker>
                      );
                    })
                }

              </GoogleMap>
            </APIProvider>
            </div>
          )}
        </div>

        {/* Right Panel - Device Telemetry */}
        {selectedDeviceData && (
          <div className="hidden lg:flex" data-testid="desktop-telemetry-wrapper">
            <TelemetryPanel
              device={selectedDeviceData}
              onClose={() => setSelectedDevice(null)}
              api={api}
              userTimezone={userTimezone}
              roadSpeedLimit={roadSpeedLimits[selectedDeviceData?.id]?.limit}
              roadSpeedSource={roadSpeedLimits[selectedDeviceData?.id]?.source}
              roadName={roadSpeedLimits[selectedDeviceData?.id]?.road_name}
              refreshKey={telemetryRefreshKey}
              activeEvents={activeEventsByDevice[selectedDeviceData?.id] || []}
              onViewEventOnMap={(evt) => {
                if (evt.latitude != null && evt.longitude != null) {
                  setEventPanActive(true);
                  setEventMarker({
                    eventId: evt.id,
                    lat: evt.latitude,
                    lng: evt.longitude,
                    severity: evt.severity || 'warning',
                    occurrence: evt,
                  });
                  setMobileTelemetrySection(null);
                  panMapToPosition({ lat: evt.latitude, lng: evt.longitude }, 17);
                  setTimeout(() => setEventPanActive(false), 1500);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Mobile Device List Drawer */}
      {mobileDeviceListOpen && (
        <div className="fixed inset-0 z-[1300] lg:hidden flex items-stretch justify-center p-0" data-testid="mobile-device-list-drawer">
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
            onClick={() => setMobileDeviceListOpen(false)}
            data-testid="mobile-device-list-backdrop"
            aria-label="Close device list"
          />
          <div className="relative mt-16 h-[calc(100dvh-4rem)] w-full overflow-hidden border-0 bg-card shadow-2xl flex flex-col" data-testid="mobile-device-list-drawer-panel">
            <div className="h-16 shrink-0 flex items-center justify-between px-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563eb]/10 text-[#2563eb]">
                  <Navigation className="w-5 h-5" />
                </span>
                <div>
                  <span className="block text-base font-semibold">Devices</span>
                  <span className="block text-xs text-muted-foreground">Select a vehicle to view tools</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileDeviceListOpen(false)}
                className="h-10 w-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                data-testid="mobile-device-list-close-button"
                aria-label="Close device list"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              {renderDeviceListPanel(true)}
            </div>
          </div>
        </div>
      )}


      {/* Edit Device Dialog */}
      <EditDeviceDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        device={editDevice}
        api={api}
        onSaved={fetchDevices}
      />

      {/* Manage Device Groups Modal */}
      <ManageGroupsModal
        open={manageGroupsOpen}
        onOpenChange={setManageGroupsOpen}
        api={api}
        devices={devices}
        onSaved={fetchDevices}
      />

      {/* Share Position Modal */}
      <SharePositionModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        device={shareDevice}
        api={api}
      />

      {/* Create Tracking Link Modal */}
      <CreateTrackingLinkModal
        open={trackingLinkModalOpen}
        onOpenChange={setTrackingLinkModalOpen}
        device={trackingLinkDevice}
        api={api}
        onCreated={() => fetchDevicesRef.current?.()}
        dateFormat={userDateFormat}
        timeFormat={userTimeFormat}
        userTimezone={userTimezone}
      />

      {/* Revoke Confirmation Modal */}
      <Dialog open={revokeModalOpen && !revokeResult} onOpenChange={(v) => { if (!v) { setRevokeModalOpen(false); setRevokeDevice(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-red-500" />
              Revoke Tracking Links
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to revoke all active tracking links for <strong>{deviceDisplayName(revokeDevice)}</strong>?
            Anyone with these links will no longer be able to track this device.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => { setRevokeModalOpen(false); setRevokeDevice(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={executeRevoke} disabled={revoking}>
              {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revoke All
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Result Modal */}
      <Dialog open={!!revokeResult} onOpenChange={(v) => { if (!v) { setRevokeResult(null); setRevokeModalOpen(false); setRevokeDevice(null); } }}>
        <DialogContent className="max-w-xs">
          <div className="text-center py-2">
            {revokeResult?.success ? (
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            ) : (
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            )}
            <p className="text-sm font-medium">{revokeResult?.message}</p>
          </div>
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => { setRevokeResult(null); setRevokeModalOpen(false); setRevokeDevice(null); }}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* More Features Modal */}
      <MoreFeaturesModal
        open={moreFeaturesOpen}
        onOpenChange={setMoreFeaturesOpen}
        api={api}
        privileges={mapEnhancementPrivileges}
        onSettingsChange={setMapFeatureSettings}
        soundEnabled={soundEnabled}
      />

      {/* Forensic Telemetry Modal — shown when clicking the Event Marker on the map */}
      <ForensicTelemetryModal
        open={forensicModalOpen}
        onOpenChange={(open) => {
          setForensicModalOpen(open);
          if (!open) {
            // Clear the event marker when the modal is closed
            setEventMarker(null);
          }
        }}
        occurrenceId={eventMarker?.eventId}
        occurrence={eventMarker?.occurrence}
      />

      <LiveTrackingVideoModal
        open={!!liveVideoDevice}
        device={liveVideoDevice}
        onOpenChange={(open) => { if (!open) setLiveVideoDevice(null); }}
      />

    </div>
  );
};
